/**
 * PageContentRenderer — renders a Page's element list (structure) for
 * the public-facing /p/:slug route.
 *
 * 0.36.13 — Created as part of the Pages/Forms unification. A "page"
 * after this ship may have content elements (markdown, title, hint,
 * button, separator, faq) and/or input elements (text, textarea,
 * number, email, dropdown, multiselect, radio, checkbox, date, phone).
 *
 * SCOPE for this ship — CONTENT ONLY:
 *   - Renders the seven content element types fully.
 *   - Renders input elements with disabled stand-in markup + a tag
 *     identifying them. Functional form submission lives in a future
 *     ship (will reuse AdminFormPreview's rich runtime). For now, a
 *     citizen visiting a form-page sees the layout but can't submit.
 *
 * Why not reuse AdminFormPreview directly:
 *   It's a complex admin-flow component (load form by id, manage
 *   state, submit dialog, etc.). For a public page we just need a
 *   READ-ONLY structure renderer. Easier to write a lean version.
 */
// 0.47.73 - Number fields' Min / Max.
// 0.47.74 - Filter operators (equals, does not equal, number comparisons).
import { filterRows } from '../lib/rowFilter';
import { searchRows, searchWords, filterByControls } from '../lib/rdSearch';
import { withBase } from '../lib/basePath';
import { goTo } from './SpaLinks';
import { cascadeMatches, cascadeOp } from '../lib/cascadeMatch';
import { flagRangeErrors, releaseFlags, flagOneRangeError, minusBlocked, numberLimits, fillRangePlaceholders, hasPattern, patternError, numberFieldIndex } from '../lib/numberRange';
// 0.47.76 - Stay in view while scrolling.
import { floatAttrs, watchFloating, layoutFloating } from '../lib/floating';
import React, { useState, useMemo, useEffect, useLayoutEffect, useRef, useCallback, cloneElement, isValidElement } from 'react';
import buttonStyles from './Button.module.css';
import fieldStyles from './Input.module.css';
import MainAlignAnchor from './MainAlignAnchor';
import { lineSpacingOf, lineSpacingStyle } from '../lib/lineSpacing.js';
import { titleWeightStyle } from '../lib/titleWeight.js';
import Chip from './Chip';
import { infoCardOrder } from '../lib/infoCardParts';
import { readLibraryRows, writeLibraryRows } from '../lib/libraryRowsCache';
import BandVideo, { bandMediaSpacer, bandMediaFocus } from './BandVideo';
import MarkdownContent from './MarkdownContent';
// 0.38.67 - Payment card element.
import PaymentCard from './PaymentCard';
// 0.39.16 - Canonical FileUpload for the file_upload element on the
// public form.
import FileUpload from './FileUpload';
// 0.38.69 - Information card element.
import InformationCard from './InformationCard';
import GuideCard from './GuideCard';
// 0.44.55 - Google map element.
import GoogleMap, { elementMapProps } from './GoogleMap';
// 0.36.266 - Henaket components used by the public form. Until now the
// public renderer used raw HTML controls for phone/date/dropdown/
// multiselect/radio/checkbox-group, which made it diverge significantly
// from AdminFormPreview (admin's "Preview"). The date case in particular
// silently ignored the admin's working-day / disabled-range / min/max
// config because the native browser picker doesn't respect any of it.
// This brings the citizen-facing render in line with admin preview.
import HenaketDropdown from './HenaketDropdown';
import CascadeField from './CascadeField';
import HenaketMultiDropdown from './HenaketMultiDropdown';
// 0.36.657 — Server-source eligibility check for library-backed
// dropdowns. See util file for the full rule list.
import { isServerSourceEligible as _isServerSourceEligible } from '../utils/serverSourceEligibility';
// 0.37.54 — Shared option resolver — was inline `_resolveElementOptions`
// in this file. Extracted so the rules engine's labels_of() calls
// the same code path and returns the EXACT options the dropdown
// shows (was drifting; see 0.37.53 changelog for the bug that
// forced the extraction).
import { resolveElementOptions as _resolveElementOptions } from '../lib/resolveElementOptions';
import HenaketDatePicker from './HenaketDatePicker';
import HenaketPhoneInput from './HenaketPhoneInput';
import FormRadioGroup from './FormRadioGroup';
import FormCheckboxGroup from './FormCheckboxGroup';
import { resolveDateLimit, dateElementForPicker } from '../utils/dateLimits';
// 0.36.381 — countriesApi.list() replaces cafApi.formOptions().
// The old endpoint returned a dict with countries as one slice;
// the new one returns the array directly. Public endpoint (no auth)
// so anonymous citizens hitting the form can still populate their
// phone widget's country picker.
import { countriesApi, formLibraryRowsApi } from '../api/admin';
// 0.36.284 — Current UI language drives library row localisation
// so dropdowns show translated labels per the citizen's chosen lang.
import { useLanguage } from '../context/LanguageContext';
import { useOptionalUser, useAuthLoading } from '../context/AuthContext';
import PageModal from './PageModal';
// 0.37.25 — Subscribe to library-cache changes so lib() misses in
// rule expressions trigger a re-render + re-eval after the fetch
// resolves. See the libTick useEffect in _PageContentRendererImpl.
// 0.37.27 — fetchLibrary + extractLibKeysFromExpression drive the
// prefetch of libraries referenced from button action_values and
// rule set_value effects, so first-click always hits cache.
import {
  subscribeLibraryCache,
  lookupLibraryValue,
  fetchLibrary,
  // 0.37.93 — Point-lookup fetch for lib() expressions; and its
  // companion extractor that finds fully-literal lib() calls in
  // expression strings so the prewarm useEffect can fire targeted
  // point-lookups instead of full-library fetches.
  fetchLibraryRow,
  // 0.37.97 — Batch equivalent for prewarm: fires one HTTP call per
  // (libKey, lang) group instead of one per literal-call reference.
  // Individual on-demand callers still use fetchLibraryRow.
  fetchLibraryRowsBatch,
  extractLibKeysFromExpression,
  extractLiteralLibCallsFromExpression,
  // 0.37.95 — Used by prewarm to skip point-lookups whose value
  // is already sitting in the full-library cache (from
  // localStorage hydrate or a prior full fetch).
  getCachedLibrary,
  // 0.37.57 — Prewarm libraries referenced by labels_of(<name>).
  // See prefetch useEffect below.
  extractLabelsOfFieldsFromExpression,
} from '../lib/libraryCache';
// 0.37.26 — Same '=' expression syntax used by rule set_values is
// also accepted in a button's "Value to write" (action_value). We
// evaluate on click, not at render time, so field references pick
// up the values at the moment the citizen clicks.
import { isExpression, extractExpression, evaluateExpression } from '../lib/rules/expression';
import FaqAccordion from './FaqAccordion';
import HenaketPanel from './HenaketPanel';
import BuiltinIcon, { iconColorValue, BUILTIN_ICON_OPTIONS } from './BuiltinIcon';
import Button from './Button';
// 0.41.37 - Reading rows and cells out of an API response.
import { readPath, splitApiTarget, apiRows } from '../lib/apiResponsePath';
import { loadVisitApi, keepForVisit, isKeptForVisit, visitOwner } from '../lib/visitApi';
import { firstPageInputsBySlug, apiPagingOf } from '../lib/apiPaging';

/* 0.49.41 - A platform icon named by a value ("users", "Calendar",
 * "open in new"), as the Information card's icon from a row. Returns
 * the icon's name, or '' when the value names no icon (an added one,
 * custom.<key>, is taken as it is). */
const _ICON_NAMES = new Set(BUILTIN_ICON_OPTIONS.map(o => o.value));
function valueIconName(v) {
  const raw = String(v == null ? '' : v).trim();
  if (!raw) return '';
  if (raw.startsWith('custom.')) return raw;
  const n = raw.toLowerCase().replace(/[\s-]+/g, '_');
  return _ICON_NAMES.has(n) ? n : '';
}

/* 0.46.00 - Keys in order, so two input objects compare by content. */
function sortKeys(o) {
  const out = {};
  for (const k of Object.keys(o).sort()) out[k] = o[k];
  return out;
}
import ApiPageDriver from './ApiPageDriver';
import PageTable, { lastColRestOf, edgeLinesOf } from './PageTable';
import LibraryFaq from './LibraryFaq';
import SearchHighlightZone from './SearchHighlightZone';
import { filterFaqItems } from './FaqAccordion';
import LibraryBackedTable from './LibraryBackedTable';
// 0.36.246 — Toggle switch component for the 'toggle' element type.
import Toggle from './Toggle';
import { PaginationProvider, PaginationElement } from './PaginationContext';
import PagePagination from './PagePagination';
import SearchField from './SearchField';
import { getElementId } from '../utils/format';
// 0.36.106 — Phase 1 runtime evaluator on the citizen side. Same
// pure function used by AdminFormPreview; now drives hide / lock /
// required / set-value behavior in the live form at /p/{slug} and
// the landing page. Submission is still a no-op (no endpoint yet)
// but the form is visually correct: rules fire, fields hide/show,
// locked inputs disable, required overrides drive the asterisk.
import { evaluateRules, effectiveRequired, isFieldLocked, buildExpressionFunctions, EXPLICIT_STRING_MARKER } from '../lib/rules/evaluate';
// 0.37.36 — Detect rules triggered by button clicks so their
// reactive effects (set_label today) can be applied as sticky
// state that persists past the one-render just_clicked window.
import { ruleHasClickTrigger } from '../lib/rules/helpers';
import { useSignupGuard } from '../lib/signupGuard';
import { loadPasswordPolicy, subscribePasswordPolicy, policyMessage, POLICY_MESSAGE, MATCH_MESSAGE } from '../lib/passwordPolicy';
import { clampInt } from '../utils/format';
// 0.36.714 — {element_id} placeholder interpolation for display
// elements (title, hint, markdown).
import { interpolateValues } from '../utils/interpolate';
// 0.38.65 - Shared scroll-to-target helper used by button
// `scroll` action. Also used by AdminFormPreview's button
// onClick so preview matches the public runtime.
import { scrollToTarget } from '../utils/scrollToTarget';
// 0.38.16 - Platform-wide number-format defaults for the expression
// cascade applied in resolveAdminText below.
// 0.38.17 - Also import the subscribe hook so components can
// re-render when the platform defaults change after mount (typical
// case: /api/public/settings fetch resolves post first render).
import { getNumberFormatDefaults, subscribeToNumberFormatDefaults, numberFormatKnown } from '../utils/numberFormat';


/* Lightweight per-element renderers. Each takes (element) and returns
 * JSX. No state, no controlled inputs. */


/* ============================================================
 * 0.37.85 — Sticky table-overlay helpers.
 *
 * cloneTableOverlays deep-clones the sticky.tableOverlays Map
 * for a fresh snapshot before applyStickyEffects mutates it.
 * Without this, sticky writes would mutate the Map in place
 * (React state == referential equality; UI wouldn't re-render).
 *
 * ensureStickyTableOverlay lazily seeds a per-element_id entry
 * on the sticky map. Same shape as the evaluator's overlay:
 *   { cellOverrides, rowsAdded, rowsRemoved }
 * Kept as a module-level function so applyStickyEffects and
 * (future) other callsites share one definition.
 * ============================================================ */
function cloneTableOverlays(prevMap) {
  const next = new Map();
  if (!prevMap || prevMap.size === 0) return next;
  for (const [key, overlay] of prevMap) {
    next.set(key, {
      cellOverrides: new Map(overlay.cellOverrides || new Map()),
      rowsAdded:     Array.isArray(overlay.rowsAdded)
        ? overlay.rowsAdded.map(r => Array.isArray(r) ? r.slice() : r)
        : [],
      rowsRemoved:   new Set(overlay.rowsRemoved || new Set()),
    });
  }
  return next;
}

function ensureStickyTableOverlay(target, tableName) {
  if (!target.tableOverlays.has(tableName)) {
    target.tableOverlays.set(tableName, {
      cellOverrides: new Map(),
      rowsAdded:     [],
      rowsRemoved:   new Set(),
    });
  }
  return target.tableOverlays.get(tableName);
}


/**
 * 0.37.89 — Shared processor for any admin-authored text that
 * should support `{field_name}` interpolation and `=expression`
 * evaluation. Consolidates what renderTitle, renderTable's cell
 * eval, and (as of this ship) every other text-bearing renderer
 * do so they share one contract:
 *
 *   Text starts with `=`  → evaluate as an expression; result
 *                           coerced to string.
 *   Otherwise             → interpolate `{field_name}`
 *                           placeholders from ctx.values.
 *
 * The two mechanisms are mutually exclusive at the whole-string
 * level (matches the guide's promise). Undefined / null returns
 * as empty string so admin doesn't see the literal "undefined"
 * in the DOM if a referenced field is unset.
 *
 * Used for labels, hints, placeholders, error messages, button
 * text as of ship 0.37.89 — enabling library-backed values via
 * `=lib(...)` at the configurator level (Ship 1 of the
 * library-backed labels feature).
 */
// 0.38.23 - Shared cascade helper: converts a raw form-state value
// to its display string, applying the number-format cascade when
// appropriate. Used by both the `=expression` branch of
// resolveAdminText (for calculation results) and the `{field}`
// interpolation branch (for embedded field values in mixed text
// like "Total: {number_total_amount} AMD"). Behavior mirrors the
// auto-detect + opt-out logic developed in 0.38.16 through 0.38.21.
//
//   - value contains EXPLICIT_STRING_MARKER (from year()/str()):
//       strip marker, return raw string (never format)
//   - value is a finite Number:
//       apply cascade if pattern set, else String(value)
//   - value is a numeric-looking string
//       (matches /^-?\d+(\.\d+)?$/ and NOT leading-zero-plus-digit):
//       coerce to Number, apply cascade if pattern set, else raw
//   - anything else:
//       String(value) fallback (never applies cascade)
//
// Returns null when the value is null/undefined so callers can
// decide the empty-string mapping (interpolate uses '', resolve-
// AdminText's expression branch handles it separately).
function _formatValueViaCascade(value, ctx) {
  if (value == null) return null;

  // Opt-out marker
  if (typeof value === 'string' && value.indexOf(EXPLICIT_STRING_MARKER) !== -1) {
    return value.split(EXPLICIT_STRING_MARKER).join('');
  }

  // Number detection
  let numberCandidate = null;
  if (typeof value === 'number' && Number.isFinite(value)) {
    numberCandidate = value;
  } else if (typeof value === 'string') {
    const trimmed = value.trim();
    if (/^-?\d+(\.\d+)?$/.test(trimmed)) {
      const looksLikeIdOrCode = /^-?0\d/.test(trimmed);
      if (!looksLikeIdOrCode) {
        const n = Number(trimmed);
        if (Number.isFinite(n)) numberCandidate = n;
      }
    }
  }

  if (numberCandidate !== null
      && ctx && ctx.expressionFunctions
      && typeof ctx.expressionFunctions.format === 'function') {
    const perForm = (ctx.formNumberDefaults) || {};
    const perPlatform = getNumberFormatDefaults();
    const pattern = perForm.default_format || perPlatform.defaultFormat || '';
    const locale = perForm.default_locale || perPlatform.defaultLocale || '';
    /* 0.42.21 - Group only from this many digits. Per-form overrides
       the platform default, like the pattern and the locale above. */
    const groupFrom = perForm.group_from_digits
      || perPlatform.groupFromDigits || 0;
    // 0.38.18 - Debug logging gated on ?libdebug=1. Kept here in
    // the shared helper so both the `=expression` and `{field}`
    // paths emit consistent diagnostics.
    if (typeof window !== 'undefined') {
      try {
        const q = new URLSearchParams(window.location.search || '');
        if (q.get('libdebug') === '1' || window.__DEBUG_LIBS === true) {
          const t = typeof performance !== 'undefined' && performance.now
            ? Math.round(performance.now()) : Date.now();
          // eslint-disable-next-line no-console
          console.log(
            `[numFmt t=${t}ms] value=${numberCandidate}  `
            + `raw_type=${typeof value}  `
            + `form.fmt=${JSON.stringify(perForm.default_format || '')}  `
            + `platform.fmt=${JSON.stringify(perPlatform.defaultFormat || '')}  `
            + `chosen.fmt=${JSON.stringify(pattern)}  `
            + `chosen.locale=${JSON.stringify(locale)}  `
            + `will_apply=${!!pattern}`
          );
        }
      } catch (_e) { /* silent */ }
    }
    if (pattern) {
      return ctx.expressionFunctions.format(numberCandidate, pattern, locale, groupFrom);
    }
  }
  return String(value);
}

// 0.42.50 - exported for the admin Preview, so it behaves as the page does.
export function resolveAdminText(raw, ctx) {
  if (raw == null) return '';
  if (typeof raw !== 'string') return String(raw);
  if (isExpression(raw)) {
    const functions = (ctx && ctx.expressionFunctions) || {
      lib: (libKey, keyColumn, keyValue, valueColumn) =>
        lookupLibraryValue(libKey, keyColumn, keyValue, valueColumn,
                           ctx && ctx.language),
    };
    const evaluated = evaluateExpression(
      extractExpression(raw),
      (ctx && ctx.values) || {},
      functions,
    );
    // 0.37.100 — In debug mode, log what an expression-bearing
    // field actually renders. Helps chase down "shows 0" reports
    // where an expression coerces to 0 while a lib() is still
    // loading.
    if (typeof window !== 'undefined') {
      try {
        const q = new URLSearchParams(window.location.search || '');
        if (q.get('libdebug') === '1' || window.__DEBUG_LIBS === true) {
          const t = typeof performance !== 'undefined' && performance.now
            ? Math.round(performance.now()) : Date.now();
          // eslint-disable-next-line no-console
          console.log(`[resolveAdminText t=${t}ms] expr=${JSON.stringify(raw).slice(0, 100)} → ${JSON.stringify(evaluated)}`);
        }
      } catch (_e) { /* silent */ }
    }
    if (evaluated === undefined || evaluated === null) return '';

    // 0.38.23 - Route through the same helper the interpolation
    // branch uses, so both `=field_name` and `{field_name}` see
    // identical cascade behavior including opt-out marker
    // stripping and auto-detect for numeric strings. The debug
    // log for the cascade decision now lives inside the helper's
    // caller sites; instrumentation kept lean here.
    return _formatValueViaCascade(evaluated, ctx);
  }
  return ctx && ctx.values
    // 0.38.23 - Pass the cascade formatter so `{field_name}`
    // interpolations get the same number formatting that bare
    // `=field_name` expressions already do. Without this, admin
    // writing "Total: {number_total_amount} AMD" saw the raw
    // "120000 AMD" instead of "120,000 AMD".
    ? interpolateValues(raw, ctx.values, (v) => _formatValueViaCascade(v, ctx))
    : raw;
}


/**
 * 0.40.02 - Henaket heading spacing scale.
 *
 * Taken from the documented token mapping in
 * MarkdownContent.module.css, which applies this same scale to
 * markdown headings. Until now it lived ONLY there (descendant-
 * scoped under .markdownContent), so Title elements - which render
 * a bare <h1>..<h6> - picked up the global `h1..h6 { margin: 0 }`
 * and had no spacing of their own. Every Title, whatever its level,
 * got the col wrapper's flat 16px bottom and nothing above, which
 * is why an H1 and an H3 looked identically spaced.
 *
 *   level   top            bottom
 *   h1      space-8 32px   space-4 16px
 *   h2      space-8 32px   space-4 16px
 *   h3      space-6 24px   space-3 12px
 *   h4      space-5 20px   space-2  8px
 *   h5      space-4 16px   space-2  8px
 *   h6      space-4 16px   space-2  8px
 *
 * The asymmetry is deliberate: a larger top margin separates the
 * heading from preceding content, a smaller bottom keeps it
 * attached to the content it introduces.
 *
 * IMPORTANT - only the TOP is applied to the heading itself. Bottom
 * spacing stays with the col wrapper (see colMarginBottomFor), which
 * 0.36.65 made the single controller of vertical rhythm after a bug
 * where the col AND the inner wrapper both added margin. Putting a
 * bottom margin here too would recreate exactly that stacking.
 */
// 0.36.65 / 0.40.03 - Valid Henaket space-N steps for the
// margin_top / margin_bottom fields. Declared here (was further
// down) because the title spacing helpers below consume it; a const
// used before its definition only works by call-time luck.
const VALID_SPACE_STEPS = new Set([0, 1, 2, 3, 4, 5, 6, 8, 10, 12, 16, 20]);

const HEADING_SPACE = {
  1: { top: 8, bottom: 4 },
  2: { top: 8, bottom: 4 },
  3: { top: 6, bottom: 3 },
  4: { top: 5, bottom: 2 },
  5: { top: 4, bottom: 2 },
  6: { top: 4, bottom: 2 },
};

export function headingSpaceFor(level) {
  const l = Math.min(Math.max(Number(level) || 2, 1), 6);
  return HEADING_SPACE[l];
}

/**
 * 0.40.03 - Per-Title spacing mode.
 *
 *   'henaket'   (default) the scale above: top roughly double the
 *               bottom, so the heading separates from what precedes
 *               it and stays attached to what follows.
 *   'symmetric' equal space above and below, using the scale's TOP
 *               value for both (the section-break magnitude), for
 *               admins who want a heading to sit evenly between two
 *               blocks.
 *   'custom'    admin sets margin_top / margin_bottom directly as
 *               Henaket space-N steps; each falls back to the
 *               scale's value for that side when unset.
 *
 * Back-compat: a Title with no spacing_mode is treated as 'henaket',
 * and an explicit margin_bottom still wins there, so titles saved
 * before this ship keep the bottom spacing their admin chose.
 *
 * Returns space STEPS (not px), which both the heading's inline
 * margin-top and the col wrapper's margin-bottom turn into
 * var(--space-N).
 */
export function titleSpacingFor(el) {
  const scale = headingSpaceFor(el && el.level);
  const mode = (el && el.spacing_mode) || 'henaket';
  const step = (v, fallback) =>
    VALID_SPACE_STEPS.has(v) ? v : fallback;

  if (mode === 'symmetric') {
    return { top: scale.top, bottom: scale.top };
  }
  if (mode === 'custom') {
    return {
      top: step(el && el.margin_top, scale.top),
      bottom: step(el && el.margin_bottom, scale.bottom),
    };
  }
  // henaket: explicit margin_bottom still honored (pre-0.40.03 forms).
  return { top: scale.top, bottom: step(el && el.margin_bottom, scale.bottom) };
}

function renderTitle(e, ctx) {
  const level = e.level || 2;
  // 0.36.714 — Interpolate {element_id} placeholders in the title
  // text against current form values. Empty for missing keys.
  // 0.37.76 — Rule-driven set_text override: when a rule set text
  // for this element_id, prefer that over e.text. Interpolation
  // still runs afterward so `{other_field}` in the override text
  // works the same way as in the static text.
  // 0.37.77 — Also evaluate `=expressions` in the text (whether
  // static or override). Two mechanisms coexist and don't compete:
  //
  //   {field_name}   → placeholder interpolation. Substitutes the
  //                    field's current value inline. Useful for
  //                    mixed prose ("Application from {name}").
  //   =expression    → whole-string expression evaluation. The
  //                    entire text is treated as one expression;
  //                    the result becomes the title text. Useful
  //                    for computed values ("=amount * 1.2" or
  //                    "=join([amount * 1.2, 'AMD'], ' ')").
  //
  // Detection: if the trimmed text starts with `=`, evaluate.
  // Otherwise interpolate. Admin picks one style per title.
  const eid = e && (e.element_id || e.name);
  const override = eid && ctx && ctx.ruleState && ctx.ruleState.texts
    && ctx.ruleState.texts.has(eid)
    ? ctx.ruleState.texts.get(eid)
    : null;
  const rawText = override != null ? override : (e.text || '');
  // 0.37.89 — Route through the shared resolveAdminText helper
  // (dedupes the inline body that shipped 0.37.77 + 0.37.79).
  const text = resolveAdminText(rawText, ctx);
  // 0.36.61 — Optional color override (Henaket palette token).
  // 0.40.02 — Henaket heading top spacing (see HEADING_SPACE). The
  // bottom deliberately stays with the col wrapper. Using margin
  // rather than padding so it collapses with the previous element's
  // bottom margin exactly as markdown headings do: adjacent 16px
  // bottom + 32px top reads as 32px, not 48px.
  const hs = titleSpacingFor(e);
  const style = {
    marginTop: `var(--space-${hs.top})`,
    // 0.45.04 - A palette token or a custom hex (the Henaket picker).
    ...(e.color && colorCss(e.color) ? { color: colorCss(e.color) } : null),
  };
  // 0.38.15 - Extended to H4-H6 alongside the original H1-H3 set.
  // Level clamped to the valid 1-6 range so any legacy or malformed
  // value degrades to a sensible heading rather than crashing React
  // with an invalid tag name.
  const safeLevel = Math.min(Math.max(level, 1), 6);
  // 0.45.08 - Optional line spacing offset (Henaket by default).
  const Tag = `h${safeLevel}`;
  // 0.45.50 - Optional weight (Bold / Semibold; Henaket by default).
  return <Tag key={e.id} style={{ ...style, ...lineSpacingStyle(e), ...titleWeightStyle(e) }}>{text}</Tag>;
}

function renderParagraph(e) {
  // 0.36.61 — Legacy renderer kept for backward compatibility with
  // any structure that still has type='paragraph' (the seed migration
  // converts them to markdown next setup.bat, and the editor
  // auto-converts on load; this is the defensive render path for
  // anything that slips through).
  const style = e.color ? { color: `var(${e.color})` } : undefined;
  return <p key={e.id} style={style}>{e.text || ''}</p>;
}

function renderMarkdown(e, ctx) {
  // The element body is Markdown source; reuse the platform renderer.
  // 0.36.61 — Optional color: passed through to MarkdownContent which
  // applies it as the --md-text-color CSS variable. All headings, p,
  // li, etc. inside the markdown block pick it up.
  // 0.36.62 — Optional text_size: passed through as --md-scale CSS
  // variable; proportionally scales every text element.
  // 0.36.65 — margin_bottom is no longer passed here; it's applied
  // at the COL WRAPPER level by renderElementInRow (the layout
  // container is the architecturally right place for it).
  // 0.36.714 — Placeholder interpolation runs BEFORE markdown
  // parsing so admin can put `**{key}**` and get bold live values.
  // 0.36.714 — Placeholder interpolation runs BEFORE markdown
  // parsing so admin can put `**{key}**` and get bold live values.
  // 0.39.84 - Route through resolveAdminText (like the hint element)
  // so a whole-string `=lib(...)` / `=expression` markdown body
  // RESOLVES to its value instead of rendering the raw expression.
  // resolveAdminText also handles `{field}` placeholder interpolation
  // with the number-format cascade, so this covers both cases.
  // 0.39.85 - Also honor a rule-driven set_text override (was
  // ignored - markdown read e.text directly, so `set_text on
  // markdown_N` had no effect). Mirrors renderTitle / renderHint:
  // prefer ctx.ruleState.texts[element_id] over the static text,
  // then resolve expressions / interpolate placeholders.
  const eid = e && (e.element_id || e.name);
  const override = eid && ctx && ctx.ruleState && ctx.ruleState.texts
    && ctx.ruleState.texts.has(eid)
    ? ctx.ruleState.texts.get(eid)
    : null;
  const rawText = override != null ? override : (e.text || '');
  const text = resolveAdminText(rawText, ctx);
  // 0.45.02 - A palette token or a custom hex (the Henaket picker);
  // empty = the default (dark on light layers, white on dark ones).
  const color = e.color ? (colorCss(e.color) || undefined) : undefined;
  return <MarkdownContent key={e.id} source={text}
                          color={color} size={e.text_size}
                          leading={lineSpacingOf(e)} />;
}

/**
 * 0.40.00 - Alert element. Canonical Henaket alert block.
 *
 * Three things are rule-controllable, mapped onto existing effects
 * so no new concepts are introduced for two of them:
 *   set_label   -> the bold first line (the message)
 *   set_text    -> the description under it
 *   set_variant -> info | success | warning | error  (new effect)
 *
 * Both text fields go through resolveAdminText, so =lib(...) and
 * {field} placeholders work exactly as they do in hint / markdown.
 */
const ALERT_VARIANTS = ['info', 'success', 'warning', 'error'];

function renderAlert(e, ctx) {
  const eid = e && (e.element_id || e.name);
  const rs = ctx && ctx.ruleState;

  // Bold message line: set_label override, else the static title.
  const titleOverride = eid && rs && rs.labels && rs.labels.has(eid)
    ? rs.labels.get(eid) : null;
  const rawTitle = titleOverride != null ? titleOverride : (e.title || '');

  // Description: set_text override, else the static text.
  const textOverride = eid && rs && rs.texts && rs.texts.has(eid)
    ? rs.texts.get(eid) : null;
  const rawText = textOverride != null ? textOverride : (e.text || '');

  // Variant: set_variant override, else the configured one. Anything
  // unrecognised falls back to 'info' rather than emitting a bogus
  // class that would render an unstyled box.
  const variantOverride = eid && rs && rs.variants && rs.variants.has(eid)
    ? rs.variants.get(eid) : null;
  const wanted = variantOverride != null ? variantOverride : (e.variant || 'info');
  const variant = ALERT_VARIANTS.includes(wanted) ? wanted : 'info';

  let title = resolveAdminText(rawTitle, ctx);
  let text = resolveAdminText(rawText, ctx);
  // 0.46.82 - The login page's message box shows the sign-in message
  // (a node: it may hold a link), in the admin's colors and variant.
  if (e.builtin && ctx && ctx.system && ctx.system.messages && ctx.system.messages[e.builtin] != null) {
    title = '';
    text = ctx.system.messages[e.builtin];
  }

  // warning / error interrupt a screen reader; info / success are
  // announced politely. Mirrors the Alert component's own rule.
  const role = (variant === 'error' || variant === 'warning') ? 'alert' : 'status';

  return (
    <div key={e.id} className={`henaket-alert henaket-alert-${variant}`} role={role}>
      <div className="henaket-alert-bar" aria-hidden="true" />
      <div className="henaket-alert-content">
        {title && <div className="henaket-alert-heading">{title}</div>}
        {text && <div className="henaket-alert-body">{text}</div>}
      </div>
    </div>
  );
}

/* 0.43.94 - Panel element (Henaket confirmation / status panel). Rule
 * overrides as the Alert: set_label -> the title, set_text -> the value
 * (e.g. a reference number from an API answer), set_variant -> the
 * colour. Every text goes through resolveAdminText (=lib, =api...). */
function renderPanel(e, ctx) {
  const eid = e && (e.element_id || e.name);
  const rs = ctx && ctx.ruleState;
  const pick = (map, fallback) => (eid && rs && rs[map] && rs[map].has(eid) ? rs[map].get(eid) : fallback);
  return (
    <HenaketPanel
      key={e.id}
      variant={pick('variants', e.variant || 'info')}
      level={e.level || 'h2'}
      title={resolveAdminText(pick('labels', e.title || ''), ctx)}
      label={resolveAdminText(e.panel_label || '', ctx)}
      value={resolveAdminText(pick('texts', e.text || ''), ctx)}
    />
  );
}

function renderHint(e, ctx) {
  // 0.36.714 → 0.37.89 — Was: plain interpolation only.
  // Now: resolveAdminText handles both `{field_name}` placeholders
  // AND `=expression` evaluation (whole-string). Enables
  // `=lib("ui_strings", "key", "help_country", "value")` for
  // library-backed hint text.
  // 0.38.42 - Now also parses markdown so admin can bold important
  // phrases, add inline links, or use lists. resolveAdminText runs
  // FIRST (matches the Markdown element's ordering) so `{field}`
  // placeholders resolve before markdown parsing sees them - `**{key}**`
  // renders as bold with the live value inside.
  const text = resolveAdminText(e.text, ctx);
  return (
    <div key={e.id} className="hint">
      <MarkdownContent source={text} />
    </div>
  );
}

/**
 * 0.38.42 - renderInputHint
 *
 * Shared helper for the `e.help` (hint under label / above input)
 * field of every input element. Applies the same treatment the
 * standalone Hint element gets: interpolates `{field}` placeholders
 * via resolveAdminText, then renders as markdown.
 *
 * Callers pass the pair of classes (`.help` + `.helpAbove` or
 * `.helpBelow`) so the outer span keeps the platform's hint
 * typography. MarkdownContent produces its own `<div>` wrapper for
 * block content (headings, paragraphs, lists); for single-line
 * hints the visual reads as a paragraph inside the styled span -
 * fine because the outer `<span>` inherits font metrics from `.help`
 * and MarkdownContent's own paragraphs use `inherit` for size when
 * no scale is set.
 *
 * Returns null when text is empty so callers can skip the wrapping
 * span entirely (same shape as their existing `e.help && (...)`
 * guards).
 */
/* 0.46.64 - Where a field's main part (its input) starts and ends, for
 * the alignment of a line (lib/mainAlign.js): two zero-height blocks
 * around it, so the label and hint above and the summary below do not
 * count. They take no space in the field's column. */
const MAIN_MARK = { display: 'block', height: 0, margin: 0, padding: 0 };
const mainStart = () => <i data-main-start="" aria-hidden="true" style={MAIN_MARK} />;
const mainEnd = () => <i data-main-end="" aria-hidden="true" style={MAIN_MARK} />;

function renderInputHint(text, ctx, className) {
  if (!text) return null;
  const resolved = resolveAdminText(text, ctx);
  if (!resolved) return null;
  return (
    <span className={className}>
      {/* MarkdownContent defaults to text-800; the .help class uses
          text-700 for the hint tone. Pass the token explicitly so
          the markdown-rendered text stays the same shade the
          plain-text version was. */}
      <MarkdownContent source={resolved} color="var(--color-text-700)" />
    </span>
  );
}

function renderSeparator(e) {
  return renderSeparatorImpl(e);
}

/**
 * 0.38.67 - Payment card renderer (public + citizen form).
 *
 * Every string goes through resolveAdminText so =lib(...) and {field}
 * interpolation work, matching every other text field. Rule and
 * command overrides are read from ctx.ruleState.texts keyed by dotted
 * paths: `<element_id>.cash_heading`, `.cash_description`,
 * `.cash_amount`, `.card_heading`, `.card_description`, `.card_amount`,
 * and `.sides` (value 'both'|'cash'|'card'). A set_text rule or a
 * button set_value command targeting one of those keys overrides the
 * authored value at render time. Missing element_id -> no override
 * possible, static values used.
 */
function renderPaymentCard(e, ctx) {
  const eid = e.element_id || e.name || '';
  const texts = (ctx && ctx.ruleState && ctx.ruleState.texts) || null;
  const values = (ctx && ctx.values) || null;

  // Resolve a field: rule/command override (via texts map at
  // `<eid>.<field>`) wins over the authored value; both then go
  // through resolveAdminText for lib/interpolation. Also accept a
  // runtime value written into ctx.values under the dotted key, so
  // button set_value commands (which write to ctx.values) can drive
  // the card too.
  const resolve = (field, authored) => {
    let raw = authored;
    const dotted = eid ? `${eid}.${field}` : null;
    if (dotted && values && values[dotted] != null && values[dotted] !== '') {
      raw = values[dotted];
    }
    if (dotted && texts && texts.has(dotted)) {
      raw = texts.get(dotted);
    }
    return resolveAdminText(raw || '', ctx);
  };

  // sides override: same dotted-key lookup, but it's an enum not a
  // display string, so no resolveAdminText.
  let sides = e.sides || 'both';
  const sidesDotted = eid ? `${eid}.sides` : null;
  if (sidesDotted && values && typeof values[sidesDotted] === 'string'
      && ['both', 'cash', 'card'].includes(values[sidesDotted])) {
    sides = values[sidesDotted];
  }
  if (sidesDotted && texts && texts.has(sidesDotted)) {
    const t = texts.get(sidesDotted);
    if (['both', 'cash', 'card'].includes(t)) sides = t;
  }

  return (
    <PaymentCard
      // 0.47.20 - The card's own fill and stroke.
      cardColor={e.card_color || ''}
      cardStroke={e.card_stroke || ''}
      key={e.id}
      sides={sides}
      cashHeading={resolve('cash_heading', e.cash_heading)}
      cashDescription={resolve('cash_description', e.cash_description)}
      cashAmount={resolve('cash_amount', e.cash_amount)}
      cardHeading={resolve('card_heading', e.card_heading)}
      cardDescription={resolve('card_description', e.card_description)}
      cardAmount={resolve('card_amount', e.card_amount)}
    />
  );
}

function renderSeparatorImpl(e) {
  return _separatorMarkup(e);
}

/**
 * 0.38.69 - Information card renderer (public + citizen form).
 *
 * Same override model as the payment card: every string is a dotted
 * rule/command target read from ctx.ruleState.texts and ctx.values
 * keyed by `<element_id>.<field>`, falling back to the authored value,
 * then run through resolveAdminText for lib/interpolation.
 *
 * Boolean toggles (show_edit, show_action) also accept overrides via
 * the dotted keys `<eid>.show_edit` / `<eid>.show_action` with string
 * values 'true'/'false' (rules and commands write strings), so a rule
 * can reveal the action button when some condition is met.
 *
 * Sections come from flat fields section_1_label / section_1_value ...
 * section_8_label / section_8_value. Each is individually resolvable
 * and overridable; empty pairs are dropped by the component.
 *
 * The Edit and action buttons navigate to edit_url / action_url when
 * set. Both also fire signalClick(<eid>) so rules can react to the
 * click. A button with no URL and no rule does nothing (by design).
 */
/* 0.46.40 - Guide card. Its texts are dotted rule / command targets
 * (`<eid>.title`, `<eid>.badge`, `<eid>.text`), as on the Information
 * card. A click anywhere on the card fires `<eid>.action` for rules
 * (inside a repeating display that also records which card was chosen)
 * and follows the link when there is one. */
function renderGuideCard(e, ctx) {
  const eid = e.element_id || e.name || '';
  const texts = (ctx && ctx.ruleState && ctx.ruleState.texts) || null;
  const values = (ctx && ctx.values) || null;
  const rawResolve = (field, authored) => {
    let raw = authored;
    const dotted = eid ? `${eid}.${field}` : null;
    if (dotted && values && values[dotted] != null && values[dotted] !== '') raw = values[dotted];
    if (dotted && texts && texts.has(dotted)) raw = texts.get(dotted);
    return raw;
  };
  const resolve = (field, authored) => resolveAdminText(rawResolve(field, authored) || '', ctx);
  const url = resolve('link_url', e.link_url);
  return (
    <GuideCard
      // 0.47.20 - The card's own fill and stroke.
      cardColor={e.card_color || ''}
      cardStroke={e.card_stroke || ''}
      // 0.47.26 - Picture fit.
      imageFit={e.image_fit === 'cover' ? 'cover' : 'contain'}
      key={e.id}
      badge={resolve('badge', e.badge)}
      badgeColor={e.badge_color || ''}
      badgeStroke={e.badge_stroke || ''}
      badgeTextColor={e.badge_text_color || ''}
      title={resolve('title', e.title)}
      text={resolve('text', e.text)}
      imageSrc={asImageSrc(resolve('image_src', e.image_src))}
      imageAlt={resolve('image_alt', e.image_alt)}
      href={url}
      newTab={!!e.link_new_tab}
      onActivate={() => {
        if (eid && ctx && typeof ctx.signalClick === 'function') ctx.signalClick(`${eid}.action`);
      }}
    />
  );
}

function renderInformationCard(e, ctx) {
  const eid = e.element_id || e.name || '';
  const texts = (ctx && ctx.ruleState && ctx.ruleState.texts) || null;
  const values = (ctx && ctx.values) || null;

  const rawResolve = (field, authored) => {
    let raw = authored;
    const dotted = eid ? `${eid}.${field}` : null;
    if (dotted && values && values[dotted] != null && values[dotted] !== '') {
      raw = values[dotted];
    }
    if (dotted && texts && texts.has(dotted)) {
      raw = texts.get(dotted);
    }
    return raw;
  };
  // 0.47.38 - A color from a rule may be a formula (=if(...)), as
  // its text can: evaluated here, a palette name or hex comes out.
  const colorResolve = (field, authored) => {
    const v = rawResolve(field, authored);
    return typeof v === 'string' && /^\s*=/.test(v) ? resolveAdminText(v, ctx) : v;
  };
  const resolve = (field, authored) => resolveAdminText(rawResolve(field, authored) || '', ctx);

  // Boolean toggle with override. Accepts real booleans (authored) or
  // string 'true'/'false' (rule/command overrides).
  const resolveBool = (field, authored) => {
    const v = rawResolve(field, authored);
    if (typeof v === 'boolean') return v;
    if (typeof v === 'string') return v.trim().toLowerCase() === 'true';
    return !!v;
  };

  // 0.38.95 - Render only `section_count` sections (1..15). Missing
  // section_count (cards saved before this ship) defaults to 8, the
  // old fixed count, so existing cards keep all their sections.
  const _sectionCount = (() => {
    const c = parseInt(e.section_count, 10);
    return Number.isFinite(c) ? Math.min(Math.max(c, 1), 15) : 8;
  })();
  const sections = [];
  for (let n = 1; n <= _sectionCount; n++) {
    /* 0.49.44 - A line with a value to show (a column, a field, a
       formula) whose value comes out empty is left out, label and all,
       unless the admin keeps it (section_N_keep_empty). A line written
       with a label only (no value at all, "4 academic hours") stays. */
    const vKey = `section_${n}_value`;
    const vVal = resolve(vKey, e[vKey]);
    const hadValue = String(e[vKey] == null ? '' : e[vKey]).trim() !== '' || !!(e.__bound && e.__bound[vKey]);
    if (hadValue && String(vVal == null ? '' : vVal).trim() === '' && !e[`section_${n}_keep_empty`]) continue;
    sections.push({
      // Its own number, so the part order still finds it when a line
      // before it is left out.
      n,
      label: resolve(`section_${n}_label`, e[`section_${n}_label`]),
      value: vVal,
      // 0.47.21 - A color for this one section, from a rule.
      labelColor: colorResolve(`section_${n}_label_color`, e[`section_${n}_label_color`]),
      valueColor: colorResolve(`section_${n}_value_color`, e[`section_${n}_value_color`]),
    });
  }

  const showEdit = resolveBool('show_edit', e.show_edit);
  const showAction = resolveBool('show_action', e.show_action);
  const editUrl = rawResolve('edit_url', e.edit_url);
  const actionUrl = rawResolve('action_url', e.action_url);

  const fireClick = (suffix) => {
    if (eid && ctx && typeof ctx.signalClick === 'function') {
      ctx.signalClick(`${eid}.${suffix}`);
    }
  };

  return (
    <InformationCard
      // 0.47.47 - The parts in the admin's order.
      partOrder={infoCardOrder(e)}
      // 0.47.62 - The icon's side and line.
      iconSide={e.icon_side === 'right' ? 'right' : 'left'}
      iconShare={e.icon_line === 'above' || e.icon_line === 'below' ? e.icon_line : 'own'}
      iconLineText={e.icon_line_text === 'right' ? 'right' : 'left'}
      // 0.48.14 - On phones.
      phoneLayout={e.phone_layout === 'fill' || e.phone_layout === 'fill_icon' ? e.phone_layout : 'keep'}
      // 0.48.15 - Same height as the cards beside it (on unless unticked).
      equalHeight={e.equal_height !== false}
      // 0.47.20 - The card's own fill and stroke.
      cardColor={e.card_color || ''}
      cardStroke={e.card_stroke || ''}
      // 0.47.26 - Picture fit.
      imageFit={e.image_fit === 'cover' ? 'cover' : 'contain'}
      key={e.id}
      badgeColor={e.badge_color || ''}
      badgeStroke={e.badge_stroke || ''}
      // 0.48.05 - The badge's text color (a rule may set it).
      badgeTextColor={colorResolve('badge_text_color', e.badge_text_color) || ''}
      // 0.42.42 - Big, Medium or Small.
      size={e.card_size || 'medium'}
      /* 0.41.95 - An image divider. The value is a plain URL or a
         {column:...} token already filled by the repeating display, so
         nothing special happens here. */
      // 0.42.28 - The whole card as the action button.
      asButton={!!e.card_is_button}
      /* 0.44.59 - Resolved like the Image element's source, so a library
         or collection value (=lib(...)), {field} and base64 all work. A
         {column:...} token is already filled by the repeating display. */
      imageSrc={asImageSrc(resolve('image_src', e.image_src))}
      /* 0.45.82 - A picture was asked for: if there is none (an empty
         column, a value that resolves to nothing) or it fails to load,
         the card shows the "no picture" box in its place. */
      imageExpected={!!(e._image_missing || (typeof e.image_src === 'string' && e.image_src.trim()))}
      imageAlt={resolve('image_alt', e.image_alt)}
      imagePadding={Number(e.image_padding) || 0}
      // 0.47.18 - The icon and the divider line.
      /* 0.49.41 - The icon from a value (a row's column in a repeating
         display, a field, a library value): a platform icon's name, or
         a picture. Empty or unknown keeps the icon chosen in settings. */
      iconName={valueIconName(resolve('icon_name_value', e.icon_name_value)) || e.icon_name || ''}
      iconSrc={asImageSrc(resolve('icon_src_value', e.icon_src_value)) || asImageSrc(resolve('icon_src', e.icon_src))}
      iconColor={e.icon_color || ''}
      dividerColor={e.divider_color || ''}
      dividerHidden={!!e.divider_hidden}
      /* 0.49.35 - The divider opens and closes what is below it. */
      details={!!e.details}
      detailsLabel={resolve('details_label', e.details_label)}
      detailsHideLabel={resolve('details_hide_label', e.details_hide_label)}
      detailsStart={e.details_start || 'closed'}
      detailsKeepAction={!e.details_hide_action}
      detailsDividerClosed={!e.details_hide_divider}
      // 0.49.39 - Off unless ticked.
      detailsCloseOthers={!!e.details_close_others}
      /* 0.47.21 - The icon's text, and every text's color. A rule's
         "set text" on <id>.title_color (and so on) changes a color. */
      iconText={resolve('icon_text', e.icon_text)}
      iconTextColor={colorResolve('icon_text_color', e.icon_text_color)}
      titleColor={colorResolve('title_color', e.title_color)}
      titleLevel={e.title_level || ''}
      idTextColor={colorResolve('id_text_color', e.id_text_color)}
      sectionLabelColor={colorResolve('section_label_color', e.section_label_color)}
      sectionValueColor={colorResolve('section_value_color', e.section_value_color)}
      textColor={colorResolve('text_color', e.text_color)}
      badge={resolve('badge', e.badge)}
      title={resolve('title', e.title)}
      idText={resolve('id_text', e.id_text)}
      sections={sections}
      showEdit={showEdit}
      editLabel={resolve('edit_label', e.edit_label)}
      onEdit={() => {
        fireClick('edit');
        if (editUrl) goTo(editUrl);
      }}
      showAction={showAction}
      actionLabel={resolve('action_label', e.action_label)}
      onAction={() => {
        fireClick('action');
        if (actionUrl) goTo(actionUrl);
      }}
    />
  );
}

function _separatorMarkup(e) {
  // 0.36.42 — full_bleed (default true) controls whether the hr
  // escapes its column container to span the full viewport, or
  // stays inside the column system. Full-bleed uses the same
  // 100vw + escape margins pattern as the live preview's
  // .liveSeparator.
  const baseStyle = {
    height: `${e.thickness ?? 1}px`,
    // 0.49.00 - The separator's own color, Text 500 by default.
    background: colorCss(e.color) || 'var(--color-text-500)',
    border: 'none',
    margin: '8px 0',
  };
  if (e.full_bleed === false) {
    return <hr key={e.id} style={baseStyle} />;
  }
  return <FullBleedRule key={e.id} style={baseStyle} />;
}

/* 0.42.86 - A rule across the whole window, measured rather than assumed.
 *
 * The old way was `width: 100vw; left: 50%; margin-left: -50vw`, which
 * centres a viewport-wide box on ITS CONTAINER's midpoint. That only
 * reaches both edges when the container is itself centred in the
 * window. Nested in a column group, or with any padding on one side of
 * the chain, it shifted: over the left edge, where the body's overflow
 * rule clipped it, and short of the right by the same amount.
 *
 * Measuring its own position needs no such assumption: whatever it sits
 * inside, it starts where the window starts and ends where the window
 * ends.
 *
 * Width comes from documentElement.clientWidth, not 100vw, because
 * 100vw INCLUDES the scrollbar on Windows - the old rule overhung the
 * right by that much and relied on the body clipping it. */
// 0.42.97 - Exported: the flow's title rule wants the same measured
// full width, and one implementation is better than two.
export function FullBleedRule({ style }) {
  const ref = React.useRef(null);
  const [box, setBox] = React.useState(null);

  React.useEffect(() => {
    const measure = () => {
      const el = ref.current;
      if (!el) return;
      // Its own offset from the window's left edge, with the escape
      // margin removed so the reading is of where it WOULD sit.
      const prev = el.style.marginLeft;
      el.style.marginLeft = '0px';
      const left = el.getBoundingClientRect().left;
      el.style.marginLeft = prev;
      setBox({ marginLeft: -left, width: document.documentElement.clientWidth });
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  return (
    <hr
      ref={ref}
      style={{
        ...style,
        ...(box ? { marginLeft: box.marginLeft, marginRight: 0, width: box.width }
                : { width: '100%' }),
        maxWidth: 'none',
      }}
    />
  );
}

// 0.36.42 — Spacer: empty vertical space, no border, no content.
/* 0.47.76 - A Scroll zone marker: nothing to see, only a place on the
   page (its Element ID) a floating element's shaft can start or stop at. */
function renderScrollMarker(e) {
  return <div key={e.id} style={{ height: 0 }} aria-hidden="true" />;
}

function renderSpacer(e) {
  const h = Number.isFinite(e.height) && e.height > 0 ? e.height : 32;
  return <div key={e.id} style={{ height: h }} aria-hidden="true" />;
}

// 0.36.41 — Image element. Renders <figure> with the <img> inside,
// plus optional caption. Inherits its grid placement (col_span /
// col_md_span / offset) from the standard element wrapping; the
// admin uses those to sit images next to text fields in the same
// row. Empty src returns null — empty images shouldn't reserve space
// on the citizen-facing render.
/* 0.42.12 - What a `src` may be, and what it becomes.
 *
 * An admin's upload already stores base64 - readAsDataURL produces
 * `data:image/png;base64,...` and that goes straight into src. A data
 * URL IS a URL, so base64 has always worked by that route.
 *
 * What did not work is a BARE base64 string with no `data:` prefix,
 * which is what an API returns: `{column:image_base64}` from a
 * trademark search. This adds the header, sniffing the format from the
 * first bytes rather than assuming PNG - a JPEG served as image/png
 * renders in most browsers but is a lie in the markup.
 */
// 0.42.50 - exported for the admin Preview, so it behaves as the page does.
export function asImageSrc(raw) {
  const v = (raw || '').trim();
  if (!v) return '';
  /* 0.42.12 - A leading slash means a site path - EXCEPT for base64
     JPEG, which begins `/9j/`. Testing for the path first swallowed
     every JPEG an API returned, which is the most common format a
     camera or a scanner produces. */
  const isJpegB64 = /^\/9j\//.test(v);
  if (!isJpegB64 && /^(data:|https?:|blob:|\/)/i.test(v)) return v;

  // Base64 is A-Z a-z 0-9 + / =, possibly wrapped. Anything else is a
  // path or a mistake, and is returned untouched so the browser can
  // report it rather than us guessing.
  const compact = v.replace(/\s+/g, '');
  if (!/^[A-Za-z0-9+/]+={0,2}$/.test(compact) || compact.length < 32) return v;

  // Magic numbers, as base64 renders them at offset 0.
  const head = compact.slice(0, 12);
  const type = head.startsWith('/9j/') ? 'image/jpeg'
    : head.startsWith('iVBORw0') ? 'image/png'
      : head.startsWith('R0lGOD') ? 'image/gif'
        : head.startsWith('UklGR') ? 'image/webp'
          : head.startsWith('PHN2Zy') || head.startsWith('PD94bWw') ? 'image/svg+xml'
            : '';
  // An unrecognised format is left alone. Declaring the wrong type is
  // worse than not declaring one: the browser trusts the header.
  return type ? `data:${type};base64,${compact}` : v;
}

/* 0.42.40 - The Henaket dots loader.
 *
 * 'api' (the default): shown ONLY while the chosen connection's call is
 * running, so it appears as a rule starts the call and disappears when
 * the answer arrives - no rules needed. With no connection chosen it
 * never shows, rather than spinning forever over nothing.
 *
 * 'always': shown unless a rule hides it, for waits that are not an API
 * call. Rule-driven hiding already happens before a renderer is called.
 *
 * The label goes through the ordinary resolver, so a library value
 * arrives in the viewer's language. With the label turned off it still
 * names the loader for screen readers, as the spec requires.
 */
/* 0.42.66 - The Henaket Step navigator.
 *
 * In a flow it shows the flow's own steps and moves between them. On a
 * page opened by itself there is no flow, so it shows the steps the
 * admin typed - which is also what makes it previewable while a form is
 * being built. */
function renderStepper(e, ctx) {
  return <StepperElement key={e.id} el={e} ctx={ctx} />;
}

function StepperElement({ el, ctx }) {
  const flow = useFlow();
  const manual = (Array.isArray(el.steps) ? el.steps : [])
    .filter(st => st && (st.title || '').trim())
    .map((st, i) => ({
      key: `m${i}`,
      title: resolveAdminText(st.title || '', ctx),
      description: resolveAdminText(st.description || '', ctx),
      complete: i < (Number(el.current_index) || 0),
      locked: false,
    }));
  const steps = flow && flow.steps.length ? flow.steps : manual;
  const current = flow ? flow.currentIndex : (Number(el.current_index) || 0);
  return (
    <HenaketStepper
      steps={steps}
      currentIndex={current}
      onGoTo={flow ? flow.goTo : null}
      label={el.label == null ? 'Step {n} of {m}' : resolveAdminText(el.label, ctx)}
    />
  );
}

function renderLoader(e, ctx) {
  if ((e.show_while || 'api') === 'api') {
    const conn = (e.api_connection || '').trim();
    const st = conn && ctx && ctx.apiState ? ctx.apiState[conn] : null;
    if (!st || st.status !== 'pending') return null;
  }
  const text = resolveAdminText(e.label || '', ctx);
  /* 0.43.31 - Where the dots sit across the page.
   *
   * The loader is as wide as its dots, so on a full-width page it sat
   * wherever the text flow left it - which reads as left, whatever the
   * design wanted. A wrapper places it, and the loader itself is
   * unchanged.
   *
   * Centre is the default: a loader marks a pause in the whole page,
   * not a note beside one field. Anything already saved has no setting
   * and takes it. */
  const align = ['left', 'center', 'right'].includes(e.align) ? e.align : 'center';
  return (
    <div key={e.id}
         style={{ display: 'flex',
                  justifyContent: align === 'left' ? 'flex-start'
                    : align === 'right' ? 'flex-end' : 'center' }}>
      <HenaketLoader
        size={e.size}
        label={e.show_label === false ? '' : text}
        ariaLabel={text}
      />
    </div>
  );
}

// 0.44.55 - Google map. Address, coordinates and the texts resolve like
// any other text, so {field} and {column:...} work (a map per row of a
// Repeating display, say).
function renderGoogleMap(e, ctx) {
  return (
    <GoogleMap key={e.id}
      {...elementMapProps(e, { resolve: (v) => resolveAdminText(v, ctx), pageLanguage: ctx && ctx.language })} />
  );
}

function renderImage(e, ctx) {
  // 0.42.12 - Resolved like every other text property, so {field},
  // {column:...} and =expressions work in a src. Without this the
  // token was passed to the browser verbatim.
  const src = asImageSrc(resolveAdminText(e.src || '', ctx));
  if (!src) return null;
  // 0.36.60 — Optional click-to-link. When `href` is set, the image
  // becomes a clickable link. `link_target` controls window target
  // ('_self' default, '_blank' to open in new tab). External links
  // (anything that looks like a URL with a scheme) automatically get
  // rel="noopener noreferrer" for security; internal links don't.
  const href = (e.href || '').trim();
  const target = e.link_target === '_blank' ? '_blank' : undefined;
  const isExternal = /^[a-z][a-z0-9+.-]*:/i.test(href);
  const rel = target === '_blank' || isExternal
    ? 'noopener noreferrer'
    : undefined;
  const img = (
    <img
      onLoad={roundImageOnLoad(e)}
      key={`img-${imageCornersOf(e)}`}
      src={src}
      alt={e.alt || ''}
      style={{
        /* 0.43.35 / 0.46.14 - A fixed area with both numbers, or a
           largest width or height with one (imageSizeStyle). */
        ...imageSizeStyle(e),
        // 0.42.39 - The frame, when set. Shared with the other renderer.
        ...imageFrameStyle(e),
        // 0.47.12 - Square, rounded or round corners.
        ...imageCornerStyle(e),
        /* The platform's static-card shadow, so an image meant to sit
           off the page does it at the depth a card does. */
        ...(e.shadow
          ? { boxShadow: 'var(--shadow-card-static, 0 2px 4px 0 rgba(0, 0, 0, 0.15))' }
          : null),
        display: 'block',
        // 0.46.08 - Left (default), center or right.
        ...imageAlignStyle(e),
      }}
    />
  );
  return (
    <figure key={e.id} style={{ margin: 0, textAlign: imageAlignOf(e), ...imageSpaceStyle(e) }}>
      {href ? (
        <a href={href} target={target} rel={rel}
           style={{ display: 'block', textDecoration: 'none' }}>
          {img}
        </a>
      ) : img}
      {e.caption && (
        <figcaption style={{
          marginTop: 8,
          color: 'var(--color-text-600)',
          fontSize: 14,
          fontStyle: 'italic',
          lineHeight: 1.4,
        }}>
          {e.caption}
        </figcaption>
      )}
    </figure>
  );
}

function renderFaq(e, ctx) {
  // 0.45.29 - Search: the text typed in the linked Search element hides
  // the questions that do not contain it (question or answer). null =
  // no search set up.
  const search = e.search_enabled && e.search_source_element_id
    ? String((ctx && ctx.values && ctx.values[e.search_source_element_id]) ?? '')
    : null;
  const emptyNone = e.search_empty_source_behavior === 'show_none';
  // 0.45.30 - Matches open their question and are coloured (Warning
  // light unless the element sets a Highlight color).
  const hlColor = (e.search_highlight_color && colorCss(e.search_highlight_color)) || null;
  const colorMatches = !e.search_highlight_off;   // 0.45.31 - "Highlight matches", on by default
  // 0.45.10 - Questions from a library or collection: one row, one
  // question, from the two columns the admin picked.
  if (e.source === 'library') {
    return <LibraryFaq key={e.id} library={e.library}
                       questionColumn={e.question_column}
                       answerColumn={e.answer_column}
                       fill={e.width_mode === 'fill'}
                       search={search} searchEmptyShowsNone={emptyNone}
                       highlightColor={hlColor} colorMatches={colorMatches} />;
  }
  // The form-constructor FAQ element stores items as [{question, answer}]
  // which is exactly what FaqAccordion wants.
  const all = Array.isArray(e.items) ? e.items : [];
  if (all.length === 0) return null;
  const items = search == null ? all : filterFaqItems(all, search, emptyNone);
  // 0.45.32 - With a search, the accordion stays mounted when nothing
  // matches (it shows nothing), so it still knows what was open before.
  if (items.length === 0 && search == null) return null;
  return <FaqAccordion key={e.id} items={items} fill={e.width_mode === 'fill'}
                       highlight={search || ''} highlightColor={hlColor} colorMatches={colorMatches} />;
}

// 0.36.74 — Helpers to compute a luminance-aware text color and to
// build the inline-style object for the `custom` button variant.
// Duplicated from AdminFormEditor (the editor and public renderer
// live in separate bundles via dynamic import isolation; we can't
// share a util from /pages without pulling the editor into the
// public bundle). Stays small.
function buttonIsLightColor(hex) {
  if (!hex) return true;
  let h = hex.replace('#', '').trim();
  if (h.length === 3) h = h.split('').map(c => c + c).join('');
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return true;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  // Relative luminance (perceived brightness).
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum > 0.6;
}

function customButtonStyleVars(e) {
  const out = {};
  if (e.custom_bg) out['--btn-custom-bg'] = e.custom_bg;
  if (e.custom_border) out['--btn-custom-border'] = e.custom_border;
  if (e.custom_text) out['--btn-custom-text'] = e.custom_text;
  else if (e.custom_bg) out['--btn-custom-text'] = buttonIsLightColor(e.custom_bg) ? 'var(--color-text-800)' : 'var(--color-white)';
  if (e.custom_hover_bg) out['--btn-custom-hover-bg'] = e.custom_hover_bg;
  if (e.custom_hover_border) out['--btn-custom-hover-border'] = e.custom_hover_border;
  if (e.custom_hover_text) out['--btn-custom-hover-text'] = e.custom_hover_text;
  else if (e.custom_hover_bg) out['--btn-custom-hover-text'] = buttonIsLightColor(e.custom_hover_bg) ? 'var(--color-text-800)' : 'var(--color-white)';
  if (e.custom_active_bg) out['--btn-custom-active-bg'] = e.custom_active_bg;
  if (e.custom_active_border) out['--btn-custom-active-border'] = e.custom_active_border;
  if (e.custom_active_text) out['--btn-custom-active-text'] = e.custom_active_text;
  else if (e.custom_active_bg) out['--btn-custom-active-text'] = buttonIsLightColor(e.custom_active_bg) ? 'var(--color-text-800)' : 'var(--color-white)';
  // 0.36.75 — Bold toggle.
  if (e.custom_bold) out['--btn-custom-font-weight'] = 'var(--font-weight-bold)';
  // 0.36.76 — Border stroke width (px).
  // 0.44.74 - Any width but the 2px default (1px used to be dropped,
  // so a "1px" button drew 2px).
  if (Number.isFinite(e.custom_border_width) && e.custom_border_width > 0 && e.custom_border_width !== 2) {
    out['--btn-custom-border-width'] = `${e.custom_border_width}px`;
  }
  // 0.36.80 — Min-height (px). 40 = canonical fallback, no override needed.
  if (Number.isFinite(e.custom_min_height) && e.custom_min_height !== 40) {
    out['--btn-custom-min-height'] = `${e.custom_min_height}px`;
  }
  // 0.47.25 - Corners: rounded (a radius) or round (a pill).
  if (e.custom_corners === 'round') {
    out['--btn-custom-radius'] = '999px';
  } else if (e.custom_corners === 'rounded') {
    const r = Number(e.custom_radius);
    out['--btn-custom-radius'] = `${Number.isFinite(r) && r >= 0 ? Math.min(Math.round(r), 200) : 8}px`;
  }
  // 0.36.82 — "New service" badge toggle.
  if (e.custom_badge) {
    out['--btn-custom-show-badge'] = 'block';
  }
  return out;
}

// 0.36.76 — Module-scoped templates array set by the PageContentRenderer
// at the start of each render pass. Used by renderButton to resolve
// `variant: 'template:N'` references to the actual template config.
// Because React renders synchronously top-down in a single tree, a
// module-scoped variable is safe here — no concurrent renders.
let templatesForRender = [];

// 0.36.76 — Resolve a button element by looking up its template if it
// references one. Returns a "merged" element where template config
// values populate the custom_* fields and the variant is normalized
// to 'custom' so downstream rendering (CSS class, styleVars) treats
// it identically to an inline custom button. If the template is
// missing (e.g., deleted but still referenced), falls back to
// 'primary' so the button still renders something.
function resolveButtonElement(e) {
  if (typeof e.variant === 'string' && e.variant.startsWith('template:')) {
    const id = parseInt(e.variant.slice('template:'.length), 10);
    const tpl = (templatesForRender || []).find(t => t.id === id);
    if (tpl && tpl.config) {
      // 0.36.707 — Only merge STYLING fields (custom_*) from the
      // template. Label / url / action / icon fields stay
      // element-owned so admin's edits to those propagate. Was:
      // `{ ...e, ...tpl.config, variant: 'custom' }`, which let
      // template.label override e.label whenever the template
      // happened to have one saved.
      const merged = { ...e, variant: 'custom' };
      for (const key of Object.keys(tpl.config)) {
        if (key.startsWith('custom_')) merged[key] = tpl.config[key];
      }
      return merged;
    }
    // Template not found — fall back to primary.
    return { ...e, variant: 'primary' };
  }
  return e;
}

/* 0.46.91 - A button of its own size (Size and position, Custom button
 * size): width and height in px, each optional, kept within 16 to 1200.
 * The label stays centered; a narrower width wraps it. */
export function buttonSizeStyle(e) {
  // 0.47.09 - Button height, for every variant but Link (a line of
  // text): its smallest height. An exact Custom button size wins.
  const bh = Number(e && e.btn_height);
  const heightStyle = e && e.variant !== 'link' && Number.isFinite(bh) && bh >= 16 && bh <= 200
    ? { minHeight: `${Math.round(bh)}px` } : null;
  // 0.46.92 - As wide as its column (Size and position).
  if (e && e.fill_column && !(e.custom_size && Number(e.size_width) >= 16)) {
    const h = e.custom_size ? Number(e.size_height) : NaN;
    return { width: '100%', ...(heightStyle || {}), ...(Number.isFinite(h) && h >= 16 && h <= 1200 ? { height: `${Math.round(h)}px`, minHeight: `${Math.round(h)}px` } : {}) };
  }
  if (!e || !e.custom_size) return heightStyle;
  const px = (v) => {
    const n = Number(v);
    return Number.isFinite(n) && n >= 16 && n <= 1200 ? `${Math.round(n)}px` : null;
  };
  const out = { ...(heightStyle || {}) };
  const w = px(e.size_width);
  const h = px(e.size_height);
  if (w) { out.width = w; out.maxWidth = '100%'; }
  if (h) { out.height = h; out.minHeight = h; }
  return Object.keys(out).length ? out : null;
}

function renderButton(e, ctx) {
  // 0.36.76 — Resolve template reference (if any) BEFORE everything
  // else, so the rest of the render treats the element as if its
  // custom_* fields were inline.
  const resolved = resolveButtonElement(e);
  const variant = resolved.variant || 'primary';
  const cls = `${buttonStyles.button} ${buttonStyles[variant] || ''}`;
  // 0.43.78 - An uploaded icon wins over a built-in one when both are set.
  const iconImg = resolved.icon_url ? (
      <img src={resolved.icon_url} alt=""
           style={{ height: 16, width: 'auto', flexShrink: 0 }} />
    ) : resolved.builtin_icon
    ? <BuiltinIcon name={resolved.builtin_icon} color={iconColorValue(resolved.icon_color)} />
    : null;
  const pos = resolved.icon_position || 'before';
  // 0.36.74 — Custom variant: inline CSS variables drive default /
  // hover / active state colors. Fallback chain in Button.module.css
  // handles partially-set configurations (e.g. just default bg → hover
  // and active reuse it).
  const customVars = (variant === 'custom') ? customButtonStyleVars(resolved) : null;
  // 0.36.700 — 'write_value' action: on click, write
  // `${value}:${Date.now()}` to ctx.values[target] and swallow the
  // navigation. Timestamp lets consumers detect a fresh command
  // even if the same command was fired earlier. If the target or
  // value is missing, the button falls back to no-op link behavior.
  // 0.36.700 / 0.36.710 / 0.36.711 - Action normalization + click
  // + rule overrides.
  //
  // Rule effects `set_label` and `set_action_value` (ship 0.36.711)
  // are keyed by the button's element_id. When active, they
  // override the base label / action_value at render time; when
  // the rule's condition stops matching, the base value comes back
  // (state.labels / state.actionValues no longer contain the key).
  const rawAction = resolved.action || 'navigate';
  const canonicalAction =
    (rawAction === 'link') ? 'navigate' :
    (rawAction === 'write_value') ? 'set_value' :
    rawAction;
  const buttonEid = resolved.element_id || resolved.name || '';
  const labelOverride = buttonEid && ctx && ctx.ruleState
    && ctx.ruleState.labels && ctx.ruleState.labels.has(buttonEid)
    ? ctx.ruleState.labels.get(buttonEid)
    : null;
  const actionValueOverride = buttonEid && ctx && ctx.ruleState
    && ctx.ruleState.actionValues && ctx.ruleState.actionValues.has(buttonEid)
    ? ctx.ruleState.actionValues.get(buttonEid)
    : null;
  const effectiveLabel = resolveAdminText(
    // 0.37.89 — Route through resolveAdminText so `=lib(...)` or
    // `{field_name}` in the button label (static or overridden by
    // set_label) is processed at render time, matching the treatment
    // labels/hints get for inputs.
    labelOverride != null
      ? labelOverride
      : (resolved.label ?? 'Button'),
    ctx,
  );
  const effectiveActionValue = actionValueOverride != null
    ? actionValueOverride
    : (resolved.action_value || '');
  const isSetValue = canonicalAction === 'set_value'
    && resolved.action_target
    && ctx && typeof ctx.updateValue === 'function';
  // 0.46.82 - A system page's built-in button: its look is the admin's,
  // its click is the system's (Log in submits, the ID card button goes
  // to the card address). 0.46.84 - Each as wide as its text.
  // 0.46.86 - In the normal size, so Settings > Design system
  // decides it, as for every other button (was the large size).
  if (resolved.builtin && ctx && ctx.system && typeof ctx.system.onButton === 'function') {
    const sys = ctx.system;
    const busy = !!(sys.busy && sys.busy[resolved.builtin]);
    const href = (sys.hrefs && sys.hrefs[resolved.builtin]) || resolved.url || '#';
    return (
      <a key={resolved.id} href={href}
         className={cls}
         aria-disabled={busy || undefined}
         style={{ alignSelf: 'flex-start',
                  // 0.46.88 - A built-in text link is only its line of
                  // text (no button padding), so the gaps above and
                  // below it are the ones set.
                  ...(variant === 'link' ? { padding: 0, minHeight: 0 } : {}),
                  ...(customVars || {}),
                  ...(buttonSizeStyle(resolved) || {}) }}
         onClick={(ev) => { if (sys.onButton(resolved.builtin, ev) === false) ev.preventDefault(); }}>
        {busy && <span className={buttonStyles.spinner} aria-hidden="true" />}
        {iconImg && pos === 'before' ? iconImg : null}
        {effectiveLabel}
        {iconImg && pos === 'after' ? iconImg : null}
      </a>
    );
  }
  /* 0.49.08 - "lock for editing" on a button disables it: drawn at half
     strength, not focusable, and a click does nothing (no navigation, no
     action, no "was just clicked" for rules). */
  const buttonLocked = !!(buttonEid && ctx && ctx.ruleState && ctx.ruleState.locked
    && ctx.ruleState.locked.has(buttonEid));
  if (buttonLocked) {
    return (
      <a key={resolved.id}
         role="button"
         aria-disabled="true"
         tabIndex={-1}
         className={cls}
         style={{ alignSelf: 'flex-start', ...(customVars || {}), ...(buttonSizeStyle(resolved) || {}) }}
         onClick={(ev) => ev.preventDefault()}>
        {iconImg && pos === 'before' ? iconImg : null}
        {effectiveLabel}
        {iconImg && pos === 'after' ? iconImg : null}
      </a>
    );
  }
  return (
    <a key={resolved.id}
       href={isSetValue ? '#' : (resolved.url || '#')}
       target={!isSetValue && resolved.open_in_new_tab ? '_blank' : undefined}
       rel={!isSetValue && resolved.open_in_new_tab ? 'noopener noreferrer' : undefined}
       className={cls}
       style={{ alignSelf: 'flex-start', ...(customVars || {}), ...(buttonSizeStyle(resolved) || {}) }}
       onClick={(ev) => {
         // 0.37.32 — Always tell the rule engine this button was
         // clicked, regardless of the button's action_type — a
         // button that just navigates might still be interesting
         // to a `just_clicked` rule. Uses the resolved element's
         // element_id; missing id (older data) → no-op.
         const buttonId = resolved.element_id || resolved.name;
         if (buttonId && ctx && typeof ctx.signalClick === 'function') {
           ctx.signalClick(buttonId);
         }
         if (isSetValue) {
           ev.preventDefault();
           const raw = effectiveActionValue;
           // 0.37.26 — Excel-style '=' expression support in the
           // button's action_value. Same syntax as rule set_values:
           // numbers, field refs, + - * /, parens, string literals,
           // lib() lookups. Evaluated at click time so field refs
           // pick up the CURRENT values, not the values at render
           // time. If parsing fails, evaluateExpression returns
           // undefined and we fall back to writing the raw string
           // (so admin who wrote a literal starting with '=' by
           // mistake sees a visible clue in the target field
           // rather than silence).
           let computed = raw;
           if (isExpression(raw)) {
             // 0.37.38 — Was: inline `{ lib: ... }`. Now uses the
             // shared registry from ctx (built by the main
             // component's expressionFunctions useMemo) so
             // buttons get join / counta / labels_of too.
             const functions = (ctx && ctx.expressionFunctions) || {
               lib: (libKey, keyColumn, keyValue, valueColumn) =>
                 lookupLibraryValue(libKey, keyColumn, keyValue, valueColumn,
                                    ctx && ctx.language),
             };
             const evaluated = evaluateExpression(
               extractExpression(raw),
               (ctx && ctx.values) || {},
               functions,
             );
             if (evaluated !== undefined) computed = evaluated;
           }
           const isCmd = String(resolved.action_target).endsWith('__cmd');
           const val = isCmd ? `${computed}:${Date.now()}` : computed;
           ctx.updateValue(resolved.action_target, val);
           return;
         }
         if (!resolved.url) ev.preventDefault();
       }}>
      {iconImg && pos === 'before' ? iconImg : null}
      {effectiveLabel}
      {iconImg && pos === 'after' ? iconImg : null}
    </a>
  );
}

/* 0.36.69 — Real interactive input rendering for the public page.
 *
 * 0.36.106 — Now CONTROLLED. Values flow from `ctx.values[name]`;
 * onChange routes through `ctx.updateValue(name, value)`. Rule
 * effects applied:
 *   - hide   → handled upstream in renderElementInRow / renderLineGroupChild
 *              (the element doesn't reach this function at all)
 *   - lock   → input gets `disabled={true}` so the citizen can't edit
 *   - set_required → drives BOTH the label asterisk AND the `required`
 *                     attribute on the input via effectiveRequired()
 *   - set_value / clear_value → applied edge-triggered upstream;
 *                                this function just reads ctx.values
 *
 * Submission still NOT WIRED — there's no public endpoint to POST to
 * yet. Button elements in the structure render as <a> links via
 * renderButton; they don't trigger any form submission. That's the
 * next ship (0.36.107). For now, citizens can fill in fields, watch
 * rules fire (e.g. picking "Other" reveals a free-text field), but
 * pressing the action button doesn't persist anything.
 *
 * For richer interactivity (validation, masked inputs, country
 * dropdowns for phone, etc.), use the dedicated /caf/register flow
 * or AdminFormPreview as a reference for what's possible. This
 * lean public renderer trades some polish for simplicity. */
// 0.37.54 — _resolveElementOptions moved to ../lib/resolveElementOptions.
// See the import alias near the top of this file. The rules engine
// (evaluate.js labels_of) now calls the same code path.


function renderRealInput(e, ctx) {  const id = `f-${e.id}`;
  const name = getElementId(e) || '';
  const safeCtx = ctx || { values: {}, updateValue: () => {}, ruleState: null };
  // 0.37.35 — Rule override wins over the element's own label,
  // same shape as renderButton has done since ship 0.36.711.
  // Without this, `set_label` on any input (dropdown, text,
  // radio, ...) fired the effect but no renderer read it, so
  // admin saw no change on screen. Now every renderer that reads
  // a label goes through this pattern.
  const labelOverride = name && safeCtx.ruleState
    && safeCtx.ruleState.labels && safeCtx.ruleState.labels.has(name)
    ? safeCtx.ruleState.labels.get(name)
    : null;
  // 0.37.89 — Route the label (whether static from e.label or
  // rule-overridden from state.labels) through resolveAdminText
  // so it processes `{field_name}` placeholders AND
  // `=expressions`. Enables `=lib("ui_strings", "key",
  // "field_country", "value")` in the label field of any input.
  const labelText = resolveAdminText(
    labelOverride != null ? labelOverride : (e.label || ''),
    safeCtx,
  );

  const currentValue = name ? (safeCtx.values[name] ?? '') : '';
  // 0.48.59 - Read-only set on the element locks it as the rule
  // effect "lock for editing" does.
  const locked = !!e.read_only || (name && safeCtx.ruleState
    ? isFieldLocked(name, safeCtx.ruleState)
    : false);
  const reqEffective = safeCtx.ruleState
    ? effectiveRequired(name, !!e.required, safeCtx.ruleState)
    : !!e.required;

  const labelNode = labelText ? (
    <label htmlFor={id} className={fieldStyles.label}>
      {/* 0.38.54 - Henaket design system puts the required indicator
          BEFORE the label text, not after. Was: {labelText}{*}; now
          {*}{labelText}. Applies platform-wide - every input, dropdown,
          radio group, checkbox group, date picker etc. gets consistent
          leading-asterisk treatment. .required class already has
          symmetric horizontal margin (see Input.module.css) so no
          text-hugging in either position. */}
      {reqEffective && <span className={fieldStyles.required} aria-hidden="true">*</span>}
      {labelText}
    </label>
  ) : null;

  /* 0.47.98 - A system page's built-in field the page draws itself (the
     six code boxes of the two-factor and password reset pages): the
     element's label, then the page's own control. */
  if (e.builtin && safeCtx.system && safeCtx.system.custom && safeCtx.system.custom[e.builtin]) {
    return (
      <div key={e.id} className={fieldStyles.field} style={{ marginBottom: 0 }}>
        {labelText && <span className={fieldStyles.label}>{labelText}</span>}
        {safeCtx.system.custom[e.builtin]}
      </div>
    );
  }

  // Common props for the platform <input>-style elements (text, email,
  // number, phone, date, textarea, dropdown). value + onChange make
  // the input controlled; disabled honors the `lock` effect.
  //
  // 0.37.89 — Placeholder now goes through resolveAdminText so
  // library-backed placeholder strings work the same way as
  // labels (`=lib(...)` or `{field_name}`).
  const resolvedPlaceholder = resolveAdminText(e.placeholder || '', safeCtx);
  // 0.38.51 - Search placeholder for the search input inside
  // dropdown / multiselect. Same resolution path so library
  // values and interpolation work identically. Empty string
  // means "use HenaketDropdown/HenaketMultiDropdown default".
  const resolvedSearchPlaceholder = resolveAdminText(e.search_placeholder || '', safeCtx);
  const commonInputProps = {
    id,
    name: name || undefined,
    required: reqEffective,
    placeholder: resolvedPlaceholder,
    value: typeof currentValue === 'string' || typeof currentValue === 'number'
      ? currentValue
      : '',
    onChange: (ev) => safeCtx.updateValue(name, ev.target.value),
    disabled: locked,
    className: fieldStyles.input,
    // 0.41.00 - Default every input to autoComplete="off".
    //
    // The page wraps its elements in a <form>. Chrome's password
    // manager looks for a form containing a password input and then
    // treats the nearest preceding TEXT input as the username - so
    // adding a Password element caused a saved account to be filled
    // into whatever text field happened to sit above it.
    //
    // `new-password` on the password input alone does not prevent
    // that: it stops saved credentials being offered FOR THAT FIELD,
    // but not the form being classified as a login or the chosen
    // username field being filled.
    //
    // A citizen's saved password auto-filled into a government form -
    // and submitted with it - is a privacy problem, not a cosmetic
    // one. Off by default; a field that genuinely wants autofill (a
    // name, an address) can opt in later with a proper token.
    autoComplete: 'off',
  };
  /* 0.48.00 - A Text field with allowed characters is checked when it
     is left (as a Number's Min / Max). */
  if (hasPattern(e) && typeof safeCtx.flagNumberRange === 'function') {
    commonInputProps.onBlur = (ev) => safeCtx.flagNumberRange(e, ev.target.value);
  }
  // 0.46.82 - The login page's own fields are what a password manager
  // should fill.
  if (e.builtin === 'login_email') commonInputProps.autoComplete = 'email';
  if (e.builtin === 'login_password') commonInputProps.autoComplete = 'current-password';

  let inputEl = null;
  // 0.44.74 - Computed before the switch so the single checkbox and
  // the toggle, which return early, get the error bar and text too.
  const _eidForError = name;
  // 0.37.89 — Resolve the error message text through the same
  // helper labels use, so admin can bind an error string to a
  // library value via `set_error_message on X = "=lib(...)"` or
  // put a literal `=lib(...)` in the effect's message field. The
  // ruleState.errors map holds the raw admin string; runtime
  // evaluates.
  const _ruleErrorRaw = (safeCtx.ruleState && safeCtx.ruleState.errors
                            && _eidForError && safeCtx.ruleState.errors.get(_eidForError)) || '';
  /* 0.41.74 - Fall back to the element's own error message.
   *
   * `show_error_message` only flips the visibility flag, so before this
   * a rule had to carry the wording itself - which put citizen-facing
   * text inside a rule, where it is NOT translatable: rules are copied
   * verbatim across language versions.
   *
   * The element's `error_message` IS translatable, like its label and
   * hint. A rule that writes its own message still wins, for the case
   * where the text must vary with the failure. */
  const _ruleErrorFallback = (_ruleErrorRaw === '' && e && e.error_message)
    ? resolveAdminText(e.error_message, safeCtx)
    : '';
  // 0.47.82 - A Number field's Min / Max message from a library keeps
  // its {min} / {max} until resolved here: filled in now.
  /* 0.48.30 - A Password field's own checks carry a stand-in until
     here, where the page's language is known. */
  const _pwT = typeof safeCtx.t === 'function' ? safeCtx.t : null;
  const _ruleErrorText = (_ruleErrorRaw === POLICY_MESSAGE && _pwT) ? policyMessage(_pwT)
    : (_ruleErrorRaw === MATCH_MESSAGE && _pwT) ? _pwT('password.mismatch')
    : fillRangePlaceholders(e,
      resolveAdminText(_ruleErrorRaw, safeCtx) || _ruleErrorFallback,
      safeCtx.values || {}, safeCtx.expressionFunctions);
  const _ruleErrorShown = !!(safeCtx.ruleState && safeCtx.ruleState.showError
                              && _eidForError && safeCtx.ruleState.showError.has(_eidForError));
  const _showRuleError = _ruleErrorShown && _ruleErrorText !== '';
  const _wrapClass = `${fieldStyles.field}${_showRuleError ? ' field-error-bar' : ''}`;

  switch (e.type) {
    case 'text':
      inputEl = <input type="text" {...commonInputProps}
                       minLength={e.min_length || undefined}
                       maxLength={e.max_length || undefined} />;
      break;
    // 0.40.97 - Password. Same settings as Text; the only difference
    // is type="password" so the value is masked on screen, plus
    // autoComplete="new-password" to stop browsers offering a saved
    // credential for what is usually an API key or a fresh secret.
    case 'password':
      inputEl = <PasswordInput inputProps={commonInputProps}
                               minLength={e.min_length}
                               maxLength={e.max_length} />;
      break;
    case 'email':
      inputEl = <input type="email" {...commonInputProps} />;
      break;
    case 'number': {
      // 0.38.19 - Excel-style formatted display for number inputs.
      // 0.38.20 - Trigger widened from "field has set_value
      // expression OR is locked" to "cascade has a pattern".
      // Rationale: admin who sets a platform-wide (or per-form)
      // default like "#,##0" is expecting EVERY number field to
      // format, not only computed / read-only ones. Citizens who
      // type into a plain editable number field also want to see
      // "77,777" instead of "77777" once they blur. Under the hood
      // the FormattedNumberInput handles both cases: raw digits
      // during focus, formatted on blur, live-parse-on-change so
      // downstream rules react to typing.
      //
      // When the cascade has NO pattern set (fresh install with
      // neither platform nor form default), we fall back to the
      // plain <input type="number"> so we keep the browser's
      // native number validation, spinner controls, and mobile
      // numeric keypad without adding formatting nobody asked for.
      const perForm = (safeCtx.formNumberDefaults) || {};
      const perPlatform = getNumberFormatDefaults();
      const pattern = perForm.default_format || perPlatform.defaultFormat || '';
      const locale = perForm.default_locale || perPlatform.defaultLocale || '';
      /* 0.47.73 - Min / Max checked. Leaving the field with a value
         outside them shows the Min / Max error (it clears as soon as
         the value fits); with Min 0 or more a minus sign is refused
         as it is typed, and a pasted negative number shows the Min
         error at once. */
      const _flag = (v) => {
        if (typeof safeCtx.flagNumberRange === 'function') safeCtx.flagNumberRange(e, v);
      };
      const _noMinus = minusBlocked(e, safeCtx.values, safeCtx.expressionFunctions);
      // 0.47.74 - Min / Max as numbers (a formula's current result).
      const _lim = numberLimits(e, safeCtx.values, safeCtx.expressionFunctions);
      const _onKeyDown = _noMinus ? (ev) => {
        if (ev.key === '-' || ev.key === 'Subtract') ev.preventDefault();
      } : undefined;
      const _onBeforeInput = _noMinus ? (ev) => {
        const ne = ev.nativeEvent || {};
        if (ne.inputType === 'insertFromPaste' || ne.inputType === 'insertFromDrop') return;
        if (typeof ev.data === 'string' && ev.data.includes('-')) ev.preventDefault();
      } : undefined;
      const _onPaste = (ev) => {
        const el = ev.currentTarget;
        setTimeout(() => {
          const raw = el && typeof el.value === 'string' ? el.value.trim() : '';
          const parsed = raw === '' ? '' : (_parseFormattedNumber(raw) ?? raw);
          _flag(parsed);
        }, 0);
      };
      if (pattern) {
        const formatFn = safeCtx.expressionFunctions
          && safeCtx.expressionFunctions.format;
        inputEl = (
          <FormattedNumberInput
            id={id}
            name={name}
            value={currentValue}
            onChange={(v) => safeCtx.updateValue(name, v)}
            disabled={locked}
            required={reqEffective}
            placeholder={resolvedPlaceholder}
            className={fieldStyles.input}
            min={_lim.min ?? undefined}
            max={_lim.max ?? undefined}
            step={e.step || undefined}
            formatFn={formatFn}
            formatPattern={pattern}
            formatLocale={locale}
            onLeave={_flag}
            onKeyDown={_onKeyDown}
            onBeforeInput={_onBeforeInput}
            onPaste={_onPaste}
          />
        );
      } else {
        inputEl = <input type="number" {...commonInputProps}
                         min={_lim.min ?? undefined}
                         max={_lim.max ?? undefined}
                         step={e.step || undefined}
                         onBlur={(ev) => _flag(ev.target.value)}
                         onKeyDown={_onKeyDown}
                         onBeforeInput={_onBeforeInput}
                         onPaste={_onPaste} />;
      }
      break;
    }
    case 'phone':
      // 0.36.266 — Switched from raw <input type="tel"> to the
      // canonical HenaketPhoneInput so the citizen-facing form
      // matches AdminFormPreview exactly. Country list lazy-loaded
      // above; if the fetch failed we pass an empty array and the
      // widget degrades to a plain digits input.
      // 0.36.358 — Admin can restrict the country picker to a subset
      // via element.allowed_countries (array of ISO alpha-2 codes).
      // Conventions:
      //   - unset / null / undefined → all countries (default)
      //   - empty array              → no countries (picker hidden by
      //                                 the widget; widget degrades
      //                                 to a digits-only input)
      //   - non-empty array          → filter the country list to
      //                                 those codes (case-insensitive
      //                                 match against country.code)
      {
        const baseCountries = safeCtx.countries || [];
        const allowedCountries = e.allowed_countries;
        let filteredCountries = baseCountries;
        if (Array.isArray(allowedCountries)) {
          const allow = new Set(
            allowedCountries.map(c => (c || '').toLowerCase())
          );
          filteredCountries = baseCountries.filter(c =>
            allow.has((c.code || '').toLowerCase())
          );
        }
        inputEl = (
          <HenaketPhoneInput
            id={id}
            value={currentValue ?? ''}
            onChange={(v) => safeCtx.updateValue(name, v || '')}
            countries={filteredCountries}
            defaultCountry={e.default_country || 'am'}
            placeholder={resolvedPlaceholder || ''}
            name={name || undefined}
            required={reqEffective}
            disabled={locked}
          />
        );
      }
      break;
    case 'date':
      // 0.36.266 — Switched from native <input type="date"> to the
      // canonical HenaketDatePicker. The native picker silently
      // ignored every date-element config (working/non-working day
      // overrides, disabled ranges, dual-mode min/max). Now the
      // public form honours the admin's setup exactly.
      {
        const dn = dateElementForPicker(e);
        inputEl = (
          <HenaketDatePicker
            id={id}
            value={currentValue ?? ''}
            onChange={(v) => safeCtx.updateValue(name, v || '')}
            placeholder={resolvedPlaceholder || 'Click to pick a date'}
            required={reqEffective}
            disabled={locked}
            name={name || undefined}
            minDate={resolveDateLimit(dn.min_date)}
            maxDate={resolveDateLimit(dn.max_date)}
            nonWorkingDays={dn.non_working_days}
            workingOverrides={dn.working_overrides}
            nonWorkingOverrides={dn.non_working_overrides}
            nonWorkingColor={dn.non_working_color ? `var(${dn.non_working_color})` : undefined}
            disabledRanges={dn.disabled_ranges}
            flush
          />
        );
      }
      break;
    case 'textarea':
      // 0.44.74 - With .textarea, as Input's multiline: resizable
      // vertically, 1.5 line height, no 40px minimum.
      inputEl = <textarea {...commonInputProps}
                          className={`${fieldStyles.input} ${fieldStyles.textarea}`}
                          rows={e.rows || 4}
                          minLength={e.min_length || undefined}
                          maxLength={e.max_length || undefined} />;
      break;
    case 'dropdown': {
      // 0.36.266 — Switched from raw <select> to the canonical
      // HenaketDropdown so the public form matches AdminFormPreview.
      // Native <select> rendering varies by OS / locale; the Henaket
      // dropdown is consistent everywhere and supports keyboard
      // navigation + searchable options on the same styling.
      // 0.36.278 — Library-backed source: when `e.source === 'library'`,
      // materialise options from ctx.librariesData[e.library] at
      // render time. Manual source unchanged.
      // 0.36.281 — Per-element threshold overrides pass through to
      // the component when set; otherwise the component's defaults
      // (route-aware → platform setting on citizen routes) apply.
      // 0.36.657 — Library-backed dropdowns with no advanced features
      // switch to server-source mode. HenaketDropdown fetches its own
      // options via /meta + /rows + /hydrate; the browser skips the
      // full library download.
      const thresholdProps = {};
      if (e.inline_threshold !== undefined && e.inline_threshold !== null) {
        thresholdProps.inlineThreshold = e.inline_threshold;
      }
      if (e.search_threshold !== undefined && e.search_threshold !== null) {
        thresholdProps.searchThreshold = e.search_threshold;
      }
      // 0.45.33 - "Highlight matches" (off by default for dropdowns).
      if (e.search_highlight) {
        thresholdProps.highlightMatches = true;
        thresholdProps.highlightColor = (e.search_highlight_color && colorCss(e.search_highlight_color)) || null;
      }
      const useServerSource = _isServerSourceEligible(e, safeCtx.ruleState);
      if (useServerSource) {
        // 0.36.658 — Cascade filter: forward parent's current
        // value to the backend as filter_column + filter_values.
        // Multiselect parents contribute multiple values (OR match).
        let cascadeFields = {};
        if (e.filter_by_element && e.filter_by_column) {
          const parentVal = safeCtx.values ? safeCtx.values[e.filter_by_element] : '';
          const filterValues = Array.isArray(parentVal)
            ? parentVal.map(v => String(v))
            : (parentVal === '' || parentVal == null ? [] : [String(parentVal)]);
          cascadeFields = {
            filterColumn: e.filter_by_column,
            filterValues,
          };
        }
        // 0.36.659 — Multi-column label passthrough.
        let labelFields = {};
        if (Array.isArray(e.label_columns) && e.label_columns.length > 0) {
          labelFields = {
            labelColumns: e.label_columns,
            labelSeparator: typeof e.label_separator === 'string' ? e.label_separator : ' ',
          };
        } else if (e.label_column) {
          labelFields = { labelColumn: e.label_column };
        }
        // 0.36.665 — Admin-configured value_column takes precedence
        // over the internal-id fallback in the dropdown component.
        // Without this pass-through, the stored form value was always
        // the internal library-entry id even when admin explicitly
        // picked another column (matches the legacy path at
        // _resolveElementOptions line 423).
        const valueColField = e.value_column
          ? { valueColumn: e.value_column } : {};
        inputEl = (
          <HenaketDropdown
            id={id}
            value={currentValue ?? ''}
            onChange={(v) => safeCtx.updateValue(name, v)}
            serverSource={{
              libraryKey: e.library,
              lang: safeCtx.language || null,
              ...labelFields,
              ...valueColField,
              ...cascadeFields,
            }}
            searchPlaceholder={resolvedSearchPlaceholder || undefined}
            placeholder={resolvedPlaceholder || "- select -"}
            required={reqEffective}
            disabled={locked}
            name={name || undefined}
            {...thresholdProps}
          />
        );
      } else {
        const options = _resolveElementOptions(e, safeCtx);
        inputEl = (
          <HenaketDropdown
            id={id}
            value={currentValue ?? ''}
            onChange={(v) => safeCtx.updateValue(name, v)}
            options={options.map(opt => ({ value: opt.value, label: opt.label }))}
            searchPlaceholder={resolvedSearchPlaceholder || undefined}
            placeholder={resolvedPlaceholder || "- select -"}
            required={reqEffective}
            disabled={locked}
            name={name || undefined}
            {...thresholdProps}
          />
        );
      }
      break;
    }
    case 'multiselect': {
      // 0.36.266 — Switched from a group of <input type="checkbox">
      // to the canonical HenaketMultiDropdown. Previously citizens
      // got a completely different widget than what admin saw in
      // Preview (chips dropdown). Now both render identically and
      // the value model (array of selected option values) is the
      // same in both.
      // 0.36.278 — Same library-backed materialisation as dropdown.
      // 0.36.281 — Same per-element threshold override pattern.
      // 0.36.657 — Same server-source escape hatch as dropdown.
      const thresholdProps = {};
      if (e.inline_threshold !== undefined && e.inline_threshold !== null) {
        thresholdProps.inlineThreshold = e.inline_threshold;
      }
      if (e.search_threshold !== undefined && e.search_threshold !== null) {
        thresholdProps.searchThreshold = e.search_threshold;
      }
      // 0.45.33 - "Highlight matches" (off by default for dropdowns).
      if (e.search_highlight) {
        thresholdProps.highlightMatches = true;
        thresholdProps.highlightColor = (e.search_highlight_color && colorCss(e.search_highlight_color)) || null;
      }
      const arrValue = Array.isArray(currentValue) ? currentValue : [];
      const useServerSource = _isServerSourceEligible(e, safeCtx.ruleState);
      if (useServerSource) {
        // 0.36.658 — Cascade filter same as single-select branch.
        let cascadeFields = {};
        if (e.filter_by_element && e.filter_by_column) {
          const parentVal = safeCtx.values ? safeCtx.values[e.filter_by_element] : '';
          const filterValues = Array.isArray(parentVal)
            ? parentVal.map(v => String(v))
            : (parentVal === '' || parentVal == null ? [] : [String(parentVal)]);
          cascadeFields = {
            filterColumn: e.filter_by_column,
            filterValues,
          };
        }
        // 0.36.659 — Multi-column label passthrough.
        let labelFields = {};
        if (Array.isArray(e.label_columns) && e.label_columns.length > 0) {
          labelFields = {
            labelColumns: e.label_columns,
            labelSeparator: typeof e.label_separator === 'string' ? e.label_separator : ' ',
          };
        } else if (e.label_column) {
          labelFields = { labelColumn: e.label_column };
        }
        // 0.36.665 — value_column passthrough. See dropdown case.
        const valueColField = e.value_column
          ? { valueColumn: e.value_column } : {};
        inputEl = (
          <HenaketMultiDropdown
            id={id}
            value={arrValue}
            onChange={(arr) => safeCtx.updateValue(name, arr)}
            serverSource={{
              libraryKey: e.library,
              lang: safeCtx.language || null,
              ...labelFields,
              ...valueColField,
              ...cascadeFields,
            }}
            searchPlaceholder={resolvedSearchPlaceholder || undefined}
            placeholder={resolvedPlaceholder || "- select multiple -"}
            required={reqEffective}
            disabled={locked}
            name={name || undefined}
            {...thresholdProps}
          />
        );
      } else {
        const options = _resolveElementOptions(e, safeCtx);
        inputEl = (
          <HenaketMultiDropdown
            id={id}
            value={arrValue}
            onChange={(arr) => safeCtx.updateValue(name, arr)}
            options={options.map(opt => ({ value: opt.value, label: opt.label }))}
            searchPlaceholder={resolvedSearchPlaceholder || undefined}
            placeholder={resolvedPlaceholder || "- select multiple -"}
            required={reqEffective}
            disabled={locked}
            name={name || undefined}
            {...thresholdProps}
          />
        );
      }
      break;
    }
    case 'radio': {
      // 0.36.266 — Switched from raw <input type="radio"> per option
      // to the canonical FormRadioGroup.
      // 0.36.364 — Size variant support: 'standard' (40 px, default)
      // or 'small' (24 px with padding, row still 40 px tall).
      const options = Array.isArray(e.options) ? e.options : [];
      inputEl = (
        <FormRadioGroup
          value={currentValue ?? ''}
          onChange={(v) => safeCtx.updateValue(name, v)}
          options={options.map(opt => ({ value: opt.value, label: opt.label }))}
          name={name || `radio-${e.id}`}
          disabled={locked}
          size={e.size || 'standard'}
        />
      );
      break;
    }
    case 'checkbox': {
      // The schema's `checkbox` is a GROUP of checkboxes (multi-value),
      // not a single boolean. If el.options is provided, render the
      // group. If not, render a single checkbox with the label.
      // 0.36.363 — Both branches now go through FormCheckboxGroup so
      // the size variant (standard / small) applies uniformly. The
      // single-boolean branch synthesizes a one-option group with
      // the field label as the option label; the outer field label
      // is still suppressed to avoid duplication.
      const options = Array.isArray(e.options) ? e.options : [];
      const singleBoolean = options.length === 0;
      const size = e.size || 'standard';
      const arrValue = Array.isArray(currentValue) ? currentValue : [];
      const effectiveOptions = singleBoolean
        ? [{ value: 'on', label: (
          <>
            {/* 0.44.74 - The required asterisk, as on every other label. */}
            {reqEffective && <span className={fieldStyles.required} aria-hidden="true">*</span>}
            {labelText}
          </>
        ) }]
        : options.map(opt => ({ value: opt.value, label: opt.label }));
      inputEl = (
        <FormCheckboxGroup
          value={arrValue}
          onChange={(arr) => safeCtx.updateValue(name, arr)}
          options={effectiveOptions}
          name={name || `check-${e.id}`}
          disabled={locked}
          size={size}
        />
      );
      if (singleBoolean) {
        // Single checkbox already shows its label inline; suppress the
        // top label to avoid duplication.
        // 0.36.262 — Even with the label suppressed, hint above and
        // summary below still render so admin's spec lines work here.
        return (
          <div key={e.id} className={_wrapClass}>
            {_showRuleError && <div className="field-error-text">{_ruleErrorText}</div>}
            {renderInputHint(e.help, ctx, `${fieldStyles.help} ${fieldStyles.helpAbove}`)}
            {mainStart()}{inputEl}{mainEnd()}
            {(e.summary_heading || e.summary_value) && (
              <div className={fieldStyles.summary}>
                {e.summary_heading && <span className={fieldStyles.summaryHeading}>{resolveAdminText(e.summary_heading, ctx)}</span>}
                {e.summary_value && <span className={fieldStyles.summaryValue}>{resolveAdminText(e.summary_value, ctx)}</span>}
              </div>
            )}
          </div>
        );
      }
      break;
    }
    /* 0.36.246 — Toggle switch. Single boolean field; value is `true`
       (on) or `false` (off). Mirrors the single-boolean checkbox case
       above structurally, but uses the Henaket Toggle component for
       presentation and stores a real boolean rather than the
       array-form-as-bool the checkbox case uses for compatibility. */
    case 'toggle': {
      const checked = currentValue === true || currentValue === 'true' || currentValue === 'on';
      inputEl = (
        // 0.44.74 - 16px to the label, as the checkbox and radio rows.
        <label htmlFor={id} style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <Toggle
            id={id}
            name={name || undefined}
            checked={checked}
            disabled={locked}
            onChange={(v) => safeCtx.updateValue(name, v)}
          />
          <span style={{ lineHeight: 'var(--line-height-base)' }}>
            {reqEffective && <span className={fieldStyles.required} aria-hidden="true">*</span>}
            {labelText}
          </span>
        </label>
      );
      // 0.36.262 — hint above, summary below — same pattern as the
      // single-checkbox case. Label stays suppressed (toggle has its
      // own inline label).
      return (
        <div key={e.id} className={_wrapClass}>
          {_showRuleError && <div className="field-error-text">{_ruleErrorText}</div>}
          {renderInputHint(e.help, ctx, `${fieldStyles.help} ${fieldStyles.helpAbove}`)}
          {mainStart()}{inputEl}{mainEnd()}
          {(e.summary_heading || e.summary_value) && (
            <div className={fieldStyles.summary}>
              {e.summary_heading && <span className={fieldStyles.summaryHeading}>{resolveAdminText(e.summary_heading, ctx)}</span>}
              {e.summary_value && <span className={fieldStyles.summaryValue}>{resolveAdminText(e.summary_value, ctx)}</span>}
            </div>
          )}
        </div>
      );
    }
    // 0.48.71 - Cascade: library dropdowns and text fields filled as
    // one value (an address), shown closed as one summary line.
    case 'cascade':
      inputEl = (
        <CascadeField
          id={id}
          el={e}
          value={currentValue}
          onChange={(v) => safeCtx.updateValue(name, v)}
          lang={safeCtx.language || null}
          resolveText={(s) => resolveAdminText(s || '', safeCtx)}
          disabled={locked}
          required={reqEffective}
          invalid={_ruleErrorShown}
          emptyText={e.empty_text || ''}
          selectText={e.placeholder || ''}
        />
      );
      break;
    default:
      // Unknown input type — fall back to a generic text input
      // rather than nothing, so the field at least appears.
      inputEl = <input type="text" {...commonInputProps} />;
  }

  // 0.37.47 — Rule-set error display. Populated by the
  // set_error_message / show_error_message effects (see
  // evaluate.js). Uses the same .field-error-bar wrapper class
  // and .field-error-text child that Input.jsx uses so the
  // Henaket error styling (red left bar, red text below label)
  // renders identically here. Only shown when both:
  //   1. ruleState.showError has this eid (either effect adds it)
  //   2. ruleState.errors has a non-empty message for this eid
  //      (so a bare show_error_message with no set message
  //      doesn't render an empty red bar)
  // 0.44.74 - Rule error computed above the switch (see there).

  return (
    /* 0.46.88 - A system page's built-in field has no space of its own
       under it: its element's Bottom margin alone sets the gap, so the
       login page can place its parts to the pixel. */
    <div key={e.id} className={_wrapClass} style={e.builtin ? { marginBottom: 0 } : undefined}>
      {labelNode}
      {_showRuleError && (
        <div className="field-error-text">{_ruleErrorText}</div>
      )}
      {/* 0.36.261 — Henaket hint line (between label and input).
          Renders only when e.help is set. Uses the same .help +
          .helpAbove classes Input.jsx uses, so typography stays
          consistent. */}
      {renderInputHint(e.help, ctx, `${fieldStyles.help} ${fieldStyles.helpAbove}`)}
      {mainStart()}{inputEl}{mainEnd()}
      {/* 0.36.261 — Henaket Summary block (below input). Renders
          only when at least one of summary_heading / summary_value
          is set; each inner line is independently optional. */}
      {(e.summary_heading || e.summary_value) && (
        <div className={fieldStyles.summary}>
          {e.summary_heading && (
            <span className={fieldStyles.summaryHeading}>{resolveAdminText(e.summary_heading, ctx)}</span>
          )}
          {e.summary_value && (
            <span className={fieldStyles.summaryValue}>{resolveAdminText(e.summary_value, ctx)}</span>
          )}
        </div>
      )}
    </div>
  );
}


/* Element type → renderer dispatch. */
// 0.36.43 — Layers no longer have an "inline element" path. The
// full_bleed setting now toggles the BAND wrapper's width
// (full-viewport vs container-constrained) rather than swapping
// the layer between band-marker and inline-card modes. This means
// the layer always groups subsequent content; only the colored
// area's edge behavior changes.

// 0.36.169 - Search field element. Configurable variant (icon/text),
// dimensions, placeholder, and button label. Element is interactive on
// public pages: clicking the X clears the input, the button submits.
// 0.36.262 — wrapped with optional Henaket hint above + summary below
// so the spec lines work on this element too.
// 0.36.699 — Now publishes its text value to form values under
// element_id so tables (and any other consumer) can read it. Was
// entirely uncontrolled before, which meant ship 0.36.698's
// "link a table's search to a Search element" had no value to read.
/* 0.42.47 - Summary lines are RESOLVED, here and in renderRealInput.
 * They were drawn raw, so a library value showed as the formula itself,
 * and they were never translatable. Now they are resolved like the hint
 * beside them, and registered for translation. */
function renderSearch(e, ctx) {
  const hasExtras = !!(e.help || e.summary_heading || e.summary_value);
  const eid = (typeof e.element_id === 'string' && e.element_id)
    ? e.element_id
    : (typeof e.name === 'string' ? e.name : '');
  const value = (eid && ctx && ctx.values && ctx.values[eid] != null)
    ? String(ctx.values[eid])
    : '';
  const onChange = (v) => {
    if (eid && ctx && typeof ctx.updateValue === 'function') {
      ctx.updateValue(eid, v);
    }
  };
  const searchEl = (
    <SearchField
      key={e.id}
      defaultVariant={e.default_variant === 'text' ? 'text' : 'icon'}
      buttonLabel={resolveAdminText(e.button_label ?? 'Search', ctx)}
      placeholder={resolveAdminText(e.placeholder || '', ctx)}
      heightPx={Number(e.height_px) || 40}
      widthPx={Number(e.width_px) || 350}
      value={value}
      onChange={onChange}
    />
  );
  if (!hasExtras) return searchEl;
  return (
    <div key={e.id} className={fieldStyles.field}>
      {renderInputHint(e.help, ctx, `${fieldStyles.help} ${fieldStyles.helpAbove}`)}
      {mainStart()}{searchEl}{mainEnd()}
      {(e.summary_heading || e.summary_value) && (
        <div className={fieldStyles.summary}>
          {e.summary_heading && <span className={fieldStyles.summaryHeading}>{resolveAdminText(e.summary_heading, ctx)}</span>}
          {e.summary_value && <span className={fieldStyles.summaryValue}>{resolveAdminText(e.summary_value, ctx)}</span>}
        </div>
      )}
    </div>
  );
}


// 0.36.164 - Pagination element. Configurable total/current; visual demo.
// In a future ship this can hook up to a Table's pagination or to URL params.
function renderPagination(e) {
  // 0.36.173 - use PaginationElement to handle both standalone and
  // table-linked modes (auto-hide when linked totalPages <= 1).
  return <PaginationElement key={e.id} el={e} />;
}


// 0.36.157 - Table element. Headers, rows, optional client-side sort.
// 0.36.667 — Optional `checkable` first-column checkboxes.
// 0.36.669 — Optional built-in pagination.
// 0.36.671 — Optional library-backed data source. When
// `source === 'library'`, delegate to LibraryBackedTable which
// fetches the data + materializes headers/rows before rendering
// a PageTable. Everything downstream (sort, pagination, checkable)
// works identically because the materialized shape matches the
// manual shape.
// 0.36.679 — For library tables, resolves filter values from
// current form values via `ctx` and passes them to LibraryBackedTable
// as `filterValues`. Manual tables ignore filters.
/* 0.41.77 - Filters and search for manual and API tables, in memory.
 *
 * A library table asks the server: rows are fetched with filter
 * parameters and the database does the work. These two have no server
 * to ask - their rows are authored cells or a stored response - so the
 * same job is done here, matching the server's rules exactly:
 *
 *   filter  String(cell) === value, case-SENSITIVE, a comma-separated
 *           value matching any of its parts, filters ANDed together
 *   search  cell.toLowerCase().includes(q.toLowerCase()) across the
 *           chosen columns, or all columns when none are chosen
 *
 * Divergence here would be worse than no feature: the same
 * configuration would behave differently on a library table and an API
 * table, and an admin would have no way to know which rule applied.
 */
function applyInMemoryFilters(el, rows, headers, ctx) {
  const values = (ctx && ctx.values) || {};
  let out = rows;

  if (el.filters_enabled !== false && Array.isArray(el.filters)) {
    for (const f of el.filters) {
      if (!f || !f.source_element_id || f.library_column === undefined
          || f.library_column === '') continue;

      // The column is an INDEX for a manual table and a PATH for an
      // API one - the configurator stores whichever identifies a
      // column in that source. Both resolve to a position here.
      // 0.41.79 - `_api_source`, not `source`: by the time this runs
      // the element has been rewritten to 'manual'. Keying on source
      // made every API filter resolve to NaN and be skipped, so a table
      // showed all rows - including when the filter was empty and the
      // setting said show none, because the skip happened first.
      const colIdx = el._api_source
        ? (el.api_columns || []).findIndex(c => c.path === f.library_column)
        : Number(f.library_column);
      if (!Number.isInteger(colIdx) || colIdx < 0) continue;

      const raw = values[f.source_element_id];
      const parts = (Array.isArray(raw) ? raw : [raw])
        .flatMap(v => String(v ?? '').split(','))
        .map(v => v.trim())
        .filter(Boolean);

      if (parts.length === 0) {
        // Empty source: the table's own setting decides, as it does
        // for a library table.
        if (el.empty_source_behavior === 'show_none') return [];
        continue;
      }
      const wanted = new Set(parts);
      out = out.filter(row => wanted.has(String(row[colIdx] ?? '')));
    }
  }

  if (el.search_enabled && el.search_source_element_id) {
    const q = String(values[el.search_source_element_id] ?? '').trim().toLowerCase();
    if (q === '') {
      if (el.search_empty_source_behavior === 'show_none') return [];
    } else {
      const picked = Array.isArray(el.search_columns) ? el.search_columns : [];
      const idxs = picked.length === 0
        ? headers.map((_, i) => i)
        : picked.map(c => (el._api_source
          ? (el.api_columns || []).findIndex(x => x.path === c)
          : Number(c))).filter(i => Number.isInteger(i) && i >= 0);
      out = out.filter(row =>
        idxs.some(i => String(row[i] ?? '').toLowerCase().includes(q)));
    }
  }

  return out;
}

/* 0.48.64 - The Edit link on each row of a table.
 *
 * A row's KEY stays the same through sorting, filters and pages: the
 * row's place in the authored rows (manual), in the API response (API),
 * or the row's id (library). Values written by the rule effect "set
 * clicked row" are kept per key in ctx.rowEdits and laid over the row
 * when it is drawn. A row's COLUMNS are named c1, c2 (manual), by path
 * (API) or by the library column. */
const ROW_YES = new Set(['true', '1', 'yes', 'on', 'y']);
function rowEditCellValue(rowObj, column) {
  if (!rowObj || !column) return undefined;
  if (Object.prototype.hasOwnProperty.call(rowObj, column)) return rowObj[column];
  return readPath(rowObj, column);
}
export function rowEditAllowedByRule(when, rowObj) {
  if (!when || !when.column) return true;
  const raw = rowEditCellValue(rowObj, when.column);
  const v = raw == null ? '' : (typeof raw === 'object' ? JSON.stringify(raw) : String(raw)).trim();
  const want = String(when.value ?? '').trim();
  switch (when.op || 'yes') {
    case 'yes':        return raw === true || ROW_YES.has(v.toLowerCase());
    case 'no':         return !(raw === true || ROW_YES.has(v.toLowerCase()));
    case 'empty':      return v === '';
    case 'filled':     return v !== '';
    case 'equals':     return v.toLowerCase() === want.toLowerCase();
    case 'not_equals': return v.toLowerCase() !== want.toLowerCase();
    default:           return true;
  }
}
function setPathCopy(obj, path, value) {
  const parts = String(path).split('.').filter(Boolean);
  if (parts.length <= 1) return { ...(obj || {}), [path]: value };
  const root = { ...(obj || {}) };
  let cur = root;
  for (let i = 0; i < parts.length - 1; i++) {
    const k = parts[i];
    const nxt = cur[k];
    cur[k] = nxt && typeof nxt === 'object' && !Array.isArray(nxt) ? { ...nxt } : {};
    cur = cur[k];
  }
  cur[parts[parts.length - 1]] = value;
  return root;
}
export function applyRowEdits(rowObj, edits) {
  if (!edits) return rowObj;
  let out = rowObj;
  for (const [col, val] of Object.entries(edits)) out = setPathCopy(out, col, val);
  return out;
}
function rowEditSetup(e, ctx) {
  if (!e || !e.row_edit) return null;
  const eid = getElementId(e);
  if (!eid) return null;
  const isIcon = (e.row_edit_style || 'icon') === 'icon';
  const label = resolveAdminText(e.row_edit_label || '', ctx) || 'Edit';
  const hint = resolveAdminText(e.row_edit_disabled_hint || '', ctx);
  const header = resolveAdminText(e.row_edit_header || '', ctx);
  const edits = (ctx && ctx.rowEdits && ctx.rowEdits[eid]) || {};
  const off = Array.isArray(e.row_edit_off) ? e.row_edit_off : [];
  const cell = (rowObj, key, srcIndex) => {
    const allowed = !(typeof srcIndex === 'number' && off[srcIndex])
      && rowEditAllowedByRule(e.row_edit_when, rowObj);
    const btn = (
      <Button variant={isIcon ? 'tertiary' : 'link'} icon={isIcon} disabled={!allowed}
              /* 0.48.66 - A text link sits on the line of the row's
                 text, not centred in a 40px box. */
              style={isIcon ? undefined : { minHeight: 0, padding: 0, border: 0, lineHeight: 'inherit', verticalAlign: 'top' }}
              aria-label={label} title={allowed && isIcon ? label : undefined}
              data-row-edit={key}
              onClick={() => { if (allowed && ctx && ctx.clickRowEdit) ctx.clickRowEdit(eid, key, rowObj); }}>
        {isIcon ? <BuiltinIcon name="pencil" size={20} /> : label}
      </Button>
    );
    // 0.48.68 - Or nothing at all, when the admin hides it.
    if (!allowed && e.row_edit_hide_disabled) return '';
    // A disabled button shows no tooltip in every browser: the hint
    // sits on a wrapper instead.
    return !allowed && hint ? <span title={hint} style={{ display: 'inline-flex' }}>{btn}</span> : btn;
  };
  return { eid, isIcon, header, edits, cell };
}
function withRowEditColumn(re, headers, colWidths, sortableColumns) {
  const n = headers.length;
  const h = [...headers, re.header];
  const w = Array.isArray(colWidths) ? [...colWidths, re.isIcon ? 56 : null] : colWidths;
  const sc = Array.isArray(sortableColumns) ? [...sortableColumns] : [];
  sc[n] = false;
  return { headers: h, colWidths: w, sortableColumns: sc };
}

function renderTable(e, ctx) {
  let filterValues = [];
  if (e.source === 'library' && Array.isArray(e.filters) && ctx && ctx.values) {
    filterValues = e.filters
      .filter(f => f && f.source_element_id && f.library_column)
      .map(f => {
        const raw = ctx.values[f.source_element_id];
        // 0.36.692 / 0.36.695 — Two source shapes:
        //   1. Dropdown / multiselect: raw is a scalar or an array
        //      of scalars.
        //   2. Table: raw is an array of full row objects. The
        //      filter extracts `source_column` from each row.
        //
        // 0.36.695 — Discrimination is SHAPE-BASED, not field-based.
        // Previously we branched on f.source_column being truthy;
        // that meant a filter with a stale source_column pointing
        // at a dropdown-shaped value would fall down the table path
        // and produce zero valid values (typeof 'a' === 'object' is
        // false → mapped to null → filtered out). Result: filter
        // silently treated as empty-source and with default
        // show_all, ALL rows shown — filter appeared broken.
        // Now the runtime inspects the actual value shape: array
        // of plain objects → table path; otherwise scalar/array
        // path. Stale source_column is harmless.
        const isTableShape = Array.isArray(raw)
          && raw.length > 0
          && raw.every(v => v && typeof v === 'object' && !Array.isArray(v));
        let values = [];
        if (isTableShape && f.source_column) {
          values = raw
            .map(row => row[f.source_column])
            .filter(v => v != null && v !== '')
            .map(v => String(v));
        } else {
          values = Array.isArray(raw)
            ? raw.map(v => String(v)).filter(v => v !== '')
            : (raw === '' || raw == null ? [] : [String(raw)]);
        }
        return {
          library_ref: f.library_ref || 'primary',
          library_column: f.library_column,
          values,
          empty_source_behavior: f.empty_source_behavior || 'show_all',
        };
      });
  }
  // 0.36.682 — Selection mode source. Reads the linked form
  // element's current value. LibraryBackedTable normalises to
  // 'page' or 'all' internally.
  let selectionModeValue = null;
  if (e.source === 'library' && e.select_all_mode_source_element_id && ctx && ctx.values) {
    const raw = ctx.values[e.select_all_mode_source_element_id];
    if (raw != null && raw !== '') selectionModeValue = String(raw);
  }
  // 0.36.692 — Publisher callback for when this table is a filter
  // source. LibraryBackedTable calls it with (element_id, rowsArray)
  // whenever its selection state changes. We forward to
  // ctx.updateValue which is the standard form-values setter used
  // across the runtime. No-op if ctx or updateValue isn't
  // available (e.g. reading paths, tests).
  const onSelectionPublish = (ctx && typeof ctx.updateValue === 'function')
    ? (eid, rows) => ctx.updateValue(eid, rows)
    : null;
  // 0.36.698 — Linked search source. Read the current text value
  // from the picked Search element (if any). null → not linked;
  // LibraryBackedTable uses its internal input state (legacy path).
  let searchSourceValue = null;
  if (e.source === 'library' && e.search_source_element_id && ctx && ctx.values) {
    const raw = ctx.values[e.search_source_element_id];
    searchSourceValue = raw == null ? '' : String(raw);
  }
  // 0.36.700 — Command channel. Convention: `${element_id}__cmd`
  // holds a `command:timestamp` string written by a Write-value
  // button. Parse it into { command, ts } for PageTable's
  // externalCommand prop.
  let externalCommand = null;
  if (e.source === 'library' && e.element_id && ctx && ctx.values) {
    const cmdRaw = ctx.values[`${e.element_id}__cmd`];
    if (typeof cmdRaw === 'string' && cmdRaw.length > 0) {
      const colonIdx = cmdRaw.indexOf(':');
      if (colonIdx > 0) {
        const command = cmdRaw.slice(0, colonIdx);
        const ts = Number(cmdRaw.slice(colonIdx + 1));
        if (Number.isFinite(ts)) externalCommand = { command, ts };
      } else {
        externalCommand = { command: cmdRaw, ts: 0 };
      }
    }
  }
  /* 0.41.37 - An API-backed table.
   *
   * Rather than a third renderer, the response is turned into the
   * `headers` and `rows` the MANUAL table already takes, and the
   * existing branch renders it. Sorting, column widths, the checkbox
   * column, pagination and the rule overlays all keep working because
   * nothing downstream knows the difference.
   *
   * `api_rows_path` picks the array of rows (results[]); each column's
   * path is then read RELATIVE to one row. That is what keeps a row's
   * values together - `results[].mark_id` and `results[].image_url` as
   * separate paths are parallel arrays with nothing tying row 1 of one
   * to row 1 of the other.
   *
   * A row missing a column's key gets an empty cell rather than a
   * shifted one: matches in the trademark response carry different
   * `signals` keys, and a shifted row would silently misreport scores.
   */
  /* 0.42.49 - Nothing at all while this table's call runs.
   *
   * The Henaket loader specification: never show an empty state while
   * the loader should be showing. A table has no "no data" message, but
   * a header row with nothing under it reads exactly as "no results" -
   * and it was showing that for the whole of every call. Treated as the
   * Repeating display is: a loader following the same connection takes
   * this space, and the table appears with its rows. Before any call,
   * the table shows its header row as it always has. */
  // 0.45.61 - Except while another page of the same search loads
  // (Pages from the API): the current page stays until the next arrives.
  if (e.source === 'api') {
    const st = ((ctx && ctx.apiState) || {})[(e.api_connection || '').trim()];
    if (st && st.status === 'pending' && !(st.paging && st.response !== undefined)) return null;
  }
  const apiPaging = apiPagingOf(e);
  let apiRowCount = 0;

  const apiTableElement = (() => {
    if (e.source !== 'api') return e;
    const cols = Array.isArray(e.api_columns) ? e.api_columns : [];
    // 0.41.37 - From ctx, not a closure: renderTable is a separate
    // function and cannot see the component's state. Same route
    // ruleState already takes.
    const entry = ((ctx && ctx.apiState) || {})[(e.api_connection || '').trim()];
    const headers = cols.map(c => c.header || c.path || '');
    /* 0.41.76 - Per-column sortability, as a list of column INDEXES,
     * which is the shape the manual renderer expects.
     *
     * Declared HERE, above the early return: it is used by both exit
     * paths, and a const referenced before its declaration is a
     * temporal-dead-zone throw - which would have crashed the page for
     * exactly the case the early return exists to serve, a connection
     * that has not been called yet. */
    const sortableColumns = cols
      .map((c, i) => (c.sortable !== false ? i : null))
      .filter(i => i !== null);
    if (!entry || entry.response === undefined) {
      // No call yet: the table renders its headers and no rows, rather
      // than vanishing. An empty result and "not asked yet" look the
      // same to a citizen otherwise.
      /* 0.41.79 - `source` is rewritten to 'manual' so the shared
       * renderer draws this, which means nothing downstream can tell an
       * API table from an authored one. `_api_source` says so, for the
       * one thing that still has to know: a filter's column is a PATH
       * here and an INDEX on a real manual table. */
      return { ...e, source: 'manual', _api_source: true, headers, rows: [], sortable_columns: sortableColumns };
    }
    // 0.49.22 - apiRows: a path through two lists gives a row per inner item.
    const rowList = apiRows(entry.response, e.api_rows_path || '', { keepEmpty: !!e.api_rows_keep_empty });
    const rows = rowList.map(row => cols.map(c => {
      const v = readPath(row, c.path || '');
      if (v === null || v === undefined) return '';
      // An array inside a row cannot fit a cell, so it is joined -
      // the alternative is showing "[object Object]" or nothing.
      if (Array.isArray(v)) return v.map(x => (x == null ? '' : String(x))).join(', ');
      if (typeof v === 'object') return JSON.stringify(v);
      return String(v);
    }));
    return { ...e, source: 'manual', _api_source: true, headers, rows, sortable_columns: sortableColumns, _api_rows: rowList };
  })();

  const tableEl = e.source === 'library'
    ? <LibraryBackedTable
        key={e.id}
        el={e}
        filterValues={filterValues}
        selectionModeValue={selectionModeValue}
        onSelectionPublish={onSelectionPublish}
        searchSourceValue={searchSourceValue}
        externalCommand={externalCommand}
        /* 0.40.06 - Header overrides go through the same resolver as
           every other admin-authored string, so `=lib(...)` and
           `{field}` work in a library table's headers. This is what
           makes per-language headers possible: the library row
           supplies the text for the citizen's language. */
        resolveHeader={(raw) => resolveAdminText(raw, ctx)}
        resolveCell={(raw) => resolveAdminText(raw, ctx)}
        /* 0.48.64 - The Edit link on each row. */
        rowEdit={rowEditSetup(e, ctx)}
      />
    : (() => {
        // 0.41.37 - Read from the synthesised element, so an
        // API-backed table takes this same path with rows built from
        // the response. Identical to `e` for a manual table.
        const rawHeaders = Array.isArray(apiTableElement.headers) ? apiTableElement.headers : [];
        /* 0.41.77 - Filters and search, applied BEFORE the rule
         * overlays and pagination below. A filtered-out row should not
         * be counted by the paginator or reachable by a rule's
         * set_cell: it is not in the table. */
        // 0.48.64 - With an Edit link, each row remembers its place in
        // the authored rows (or the response) through the filters below.
        const _re = rowEditSetup(e, ctx);
        const _unfilteredRows = (Array.isArray(apiTableElement.rows)
          ? apiTableElement.rows : []).map((r, i) => {
            // 0.48.75 - Also with a checkbox column: the ticks follow
            // the rows, not their places, through a filter.
            if ((!_re && !e.checkable) || !Array.isArray(r)) return r;
            const c = r.slice(); c.__src = i; return c;
          });
        const rawRows = applyInMemoryFilters(
          apiTableElement, _unfilteredRows, rawHeaders, ctx);
        apiRowCount = apiPaging ? _unfilteredRows.length : rawRows.length;
        // 0.36.673 — Widths dragged on the editor canvas persist to
        // e.col_widths; runtime just displays them (no handles).
        // 0.46.13 - An API table keeps each width on its column
        // (api_columns[i].width_px), so it moves with the column.
        const _apiCols = e.source === 'api' && Array.isArray(e.api_columns) ? e.api_columns : [];
        const colWidths = _apiCols.some(c => c && c.width_px)
          ? _apiCols.map(c => (c && c.width_px) || null)
          : (Array.isArray(e.col_widths) ? e.col_widths : null);
        // 0.36.677 — Per-column sortability, when admin has set any.
        const sortableColumns = Array.isArray(e.sortable_columns) && e.sortable_columns.length > 0
          ? e.sortable_columns
          : null;

        // 0.37.85 — Shape B: cell interpolation + expression eval.
        // Every cell now gets processed the same way title text does:
        // if it starts with `=`, evaluate as an expression; otherwise
        // interpolate `{field_name}` placeholders. Both mechanisms
        // work with the same ctx.values pool.
        //
        // 0.37.85 — Shape A overlays: rule-driven add_row, remove_row,
        // and set_cell effects land in ctx.ruleState.tableOverlays
        // (a Map keyed by element_id). At render time we splice
        // added rows in, drop removed row indices, and apply per-cell
        // overrides. Order: remove → add → cell-override, because
        // set_cell targets specific rows and shouldn't clobber
        // freshly-added rows unless admin explicitly asks.
        //
        // 0.37.88 — Headers are now dynamic too. Overrides keyed as
        // `r0c${col}` in overlay.cellOverrides target header text
        // (row=0 convention). Static headers also go through the
        // same interpolation + expression eval as body cells, so
        // `{field_name}` and `=expression` work uniformly.
        const overlayKey = getElementId(e);
        const overlay = overlayKey && ctx && ctx.ruleState
          && ctx.ruleState.tableOverlays
          && ctx.ruleState.tableOverlays.get(overlayKey);

        // Step 0: header overrides. Apply any `r0c${col}` entries
        // in the overlay to the header array. Column is 1-based;
        // out-of-range column indices are ignored (nothing to
        // override past headers.length).
        let effectiveHeaders = rawHeaders;
        if (overlay && overlay.cellOverrides && overlay.cellOverrides.size > 0) {
          effectiveHeaders = rawHeaders.map((h, ci) => {
            const key = `r0c${ci + 1}`;
            return overlay.cellOverrides.has(key)
              ? overlay.cellOverrides.get(key)
              : h;
          });
        }

        // Step 1: base data rows with removed indices dropped.
        let effectiveRows = rawRows;
        if (overlay && overlay.rowsRemoved && overlay.rowsRemoved.size > 0) {
          effectiveRows = rawRows.filter((_, i) =>
            !overlay.rowsRemoved.has(i + 1)); // 1-based indexing
        } else {
          effectiveRows = rawRows.slice();
        }
        // Step 2: append rule-added rows.
        if (overlay && Array.isArray(overlay.rowsAdded) && overlay.rowsAdded.length > 0) {
          for (const addedRow of overlay.rowsAdded) {
            const ar = Array.isArray(addedRow) ? addedRow.slice() : [];
            ar.__src = `a${overlay.rowsAdded.indexOf(addedRow)}`;
            effectiveRows.push(ar);
          }
        }
        // Step 3: per-cell overrides (1-based row + column, applied to
        // the effective row set post add/remove).
        if (overlay && overlay.cellOverrides && overlay.cellOverrides.size > 0) {
          effectiveRows = effectiveRows.map((row, ri) => {
            let nextRow = row;
            for (let ci = 0; ci < (Array.isArray(row) ? row.length : 0); ci++) {
              const key = `r${ri + 1}c${ci + 1}`;
              if (overlay.cellOverrides.has(key)) {
                if (nextRow === row) { nextRow = row.slice(); nextRow.__src = row.__src; }
                nextRow[ci] = overlay.cellOverrides.get(key);
              }
            }
            return nextRow;
          });
        }
        // Step 4: interpolation + expression evaluation in each cell
        //         (and each header — 0.37.88).
        const evalExpressionInCell = (raw) => {
          if (typeof raw !== 'string') return raw ?? '';
          if (isExpression(raw)) {
            const functions = (ctx && ctx.expressionFunctions) || {
              lib: (libKey, keyColumn, keyValue, valueColumn) =>
                lookupLibraryValue(libKey, keyColumn, keyValue, valueColumn,
                                   ctx && ctx.language),
            };
            const evaluated = evaluateExpression(
              extractExpression(raw),
              (ctx && ctx.values) || {},
              functions,
            );
            /* 0.49.04 - A formula's number is formatted as it is in any
               other text (the platform pattern, locale and grouping), and
               str() / year() keep a number as written. It was shown raw. */
            return evaluated === undefined || evaluated === null ? ''
              : String(_formatValueViaCascade(evaluated, ctx));
          }
          return ctx && ctx.values
            // 0.38.23 - Cascade formatter for table cells too, so
            // "{amount} AMD" in a cell renders formatted.
            ? interpolateValues(raw, ctx.values, (v) => _formatValueViaCascade(v, ctx))
            : raw;
        };
        let interpolatedHeaders = effectiveHeaders.map(evalExpressionInCell);
        // 0.48.64 - A row's values for the Edit link, with the values a
        // rule wrote into it laid over, and the link as the last cell.
        const _apiCols2 = e.source === 'api' && Array.isArray(e.api_columns) ? e.api_columns : null;
        const _apiRows = Array.isArray(apiTableElement._api_rows) ? apiTableElement._api_rows : [];
        const decorateRow = (cells, src) => {
          const key = String(src);
          const ed = _re.edits[key];
          let out = cells;
          let rowObj;
          if (_apiCols2) {
            const base = typeof src === 'number' && _apiRows[src] && typeof _apiRows[src] === 'object'
              ? _apiRows[src] : {};
            rowObj = applyRowEdits(base, ed);
            if (ed) {
              out = cells.slice();
              _apiCols2.forEach((c, ci) => {
                if (c && c.path && Object.prototype.hasOwnProperty.call(ed, c.path)) {
                  out[ci] = ed[c.path] == null ? '' : String(ed[c.path]);
                }
              });
            }
          } else {
            if (ed) {
              out = cells.slice();
              for (const [col, val] of Object.entries(ed)) {
                const m = /^c(\d+)$/.exec(col);
                if (m && Number(m[1]) >= 1) out[Number(m[1]) - 1] = val == null ? '' : String(val);
              }
            }
            rowObj = {};
            out.forEach((v, ci) => { rowObj[`c${ci + 1}`] = v; });
            /* 0.48.88 - Values a rule wrote into the row under other names
               (set clicked row of tbl: region to ...) stay with the row,
               unseen, and come back in <table>_row at its next click. */
            if (ed) {
              for (const [k, val] of Object.entries(ed)) {
                if (!/^c\d+$/.test(k)) rowObj = setPathCopy(rowObj, k, val);
              }
            }
          }
          return [...out, _re.cell(rowObj, key, typeof src === 'number' ? src : undefined)];
        };
        // 0.46.30 - Hide a row when a required cell is empty, after the
        // cells are worked out (a placeholder or a formula may give "").
        const _required = Array.isArray(e.col_required) ? e.col_required : null;
        const _hideOn = !!e.hide_rows_missing_required && !!_required && _required.some(Boolean);
        /* 0.49.01 / 0.49.02 - A column shown as a number or a date: a
           cell that reads as one is shown the platform's way, and sorts
           by its value. Other text stays as it is. */
        const _fmts = columnFormats(e);
        /* 0.49.03 - A Number column groups digits even when no number
           pattern is set (on the form or in Settings): #,##0.## then,
           with the platform's locale and "Group digits from". */
        const _fmtNum = (n) => {
          // 0.49.06 - Not before the platform's settings are known (a
          // first visit, for a moment): a guess would change a moment later.
          if (!numberFormatKnown()) return String(n);
          const perForm = (ctx && ctx.formNumberDefaults) || {};
          const hasPattern = !!(perForm.default_format || getNumberFormatDefaults().defaultFormat);
          if (!hasPattern && ctx && ctx.expressionFunctions && typeof ctx.expressionFunctions.format === 'function') {
            return ctx.expressionFunctions.format(n, '#,##0.##',
              perForm.default_locale || undefined, perForm.group_from_digits || undefined);
          }
          return _formatValueViaCascade(n, ctx);
        };
        const interpolatedRows = filterRequiredRows(effectiveRows.map(row => {
          if (!Array.isArray(row)) return row;
          let _sort = null;
          const cells = row.map((c, ci) => {
            const v = evalExpressionInCell(c);
            const sh = _fmts && _fmts[ci] ? shownCell(v, _fmts[ci], _fmtNum) : null;
            if (!sh) return v;
            if (sh.sort !== null) { if (!_sort) _sort = []; _sort[ci] = sh.sort; }
            return sh.text;
          });
          const out = _re && row.__src !== undefined ? decorateRow(cells, row.__src) : cells;
          if (row.__src !== undefined) out.__src = row.__src;
          if (_sort) out.__sort = _sort;
          return out;
        }), _required, _hideOn);
        /* 0.48.75 - Ticked rows of a checkbox column are kept by the
           row (its place in the authored rows or the response), and a
           change of a filter's source drops the ticks of rows it takes
           out, as a library table does. They stuck to positions: the
           first row ticked under Lori showed ticked under Kotayk. */
        const _rowKeys = e.checkable && interpolatedRows.every(r => r && r.__src !== undefined)
          ? interpolatedRows.map(r => String(r.__src)) : null;
        const _filterKey = e.checkable && Array.isArray(e.filters)
          ? JSON.stringify(e.filters.map(f => (f && ctx && ctx.values ? ctx.values[f.source_element_id] ?? '' : '')))
          : null;
        let _colWidths = colWidths;
        let _sortableColumns = sortableColumns;
        if (_re) {
          const w = withRowEditColumn(_re, interpolatedHeaders, colWidths, sortableColumns);
          interpolatedHeaders = w.headers; _colWidths = w.colWidths; _sortableColumns = w.sortableColumns;
        }
        // An API table reports its count through ApiPageDriver.
        if (_hideOn && e.source === 'api' && !apiPaging) apiRowCount = interpolatedRows.length;

        return (
          <PageTable
            key={e.id}
            name={getElementId(e)}
            headers={interpolatedHeaders}
            rows={interpolatedRows}
            sortValues={_fmts ? interpolatedRows.map(r => (r && r.__sort) || []) : null}
            sortable={e.sortable !== false}
            checkable={!!e.checkable}
            checkAllFirst={!!e.check_all_first}
            onCheckedRows={e.checkable && _rowKeys && ctx && ctx.publishTableChecked
              ? (keys) => {
                  /* 0.48.98 - The ticked rows, read like <table>_row:
                     the response's own row on an API table, c1, c2 on
                     a manual one, with values a rule wrote laid over. */
                  const byKey = new Map(interpolatedRows.map(r => [String(r.__src), r]));
                  const list = keys.map(k => {
                    const ed = _re ? _re.edits[k] : null;
                    if (_apiCols2) {
                      const src = Number(k);
                      const base = _apiRows[src] && typeof _apiRows[src] === 'object' ? _apiRows[src] : {};
                      return applyRowEdits(base, ed);
                    }
                    const cells = byKey.get(k) || [];
                    let o = {};
                    // Data cells only: the Edit link's cell is not a value.
                    (Array.isArray(cells) ? cells : []).forEach((v, ci) => {
                      if (v === null || ['string', 'number', 'boolean'].includes(typeof v)) o[`c${ci + 1}`] = v;
                    });
                    if (ed) for (const [col, val] of Object.entries(ed)) o = setPathCopy(o, col, val);
                    return o;
                  });
                  ctx.publishTableChecked(getElementId(e), list);
                }
              : null}
            rowKeys={_rowKeys}
            filterContextKey={_filterKey}
            width={e.width || 1168}
            colWidths={_colWidths}
            showHeader={!e.hide_header}
            hideTopBottomLines={edgeLinesOf(e).topBottom}
            hideSideLines={edgeLinesOf(e).sides}
            hideVerticalLines={!!e.hide_vertical_lines}
            hideHorizontalLines={!!e.hide_horizontal_lines}
            headerWeight={e.header_weight || ''}
            headerColor={e.header_color || ''}
            tableColors={e}
            columnTextStyles={Array.isArray(e.col_text_styles) ? e.col_text_styles : null}
            colAlign={Array.isArray(e.col_align) ? e.col_align : null}
            checkAlign={e.check_col_align || null}
            editAlign={e.edit_col_align || null}
            /* 0.46.22 - Column sizes for large and small screens. */
            colSizes={Array.isArray(e.col_sizes) ? e.col_sizes : null}
            lastColRest={lastColRestOf(e)}
            stackLarge={!!e.stack_large}
            stackSmall={!!e.stack_small}
            reportShownRowCount={_hideOn && e.source !== 'api'}
            sortableColumns={_sortableColumns}
            fitLastColumn={!!_re}
            // 0.45.61 - The API sent just this page.
            preSliced={!!apiPaging}
            /* 0.37.87 — Public-side tables always wrap header text.
             * Without this, long headers stay single-line (default
             * `.headerText { white-space: nowrap }` in the CSS
             * module) and produce a horizontal scrollbar when the
             * column widths can't accommodate every label on one
             * line. `.headerCell` already uses `vertical-align: top`
             * (from 0.37.22), so wrapped multi-line headers stay
             * anchored consistently and the row height grows to
             * accommodate. Admin tables (AdminLibraryDetail etc.)
             * pass their own explicit props and stay unaffected. */
            wrapAllHeaders={true}
          />
        );
      })();
  // 0.45.33 - Search highlight: the linked Search element's text is
  // coloured in the cells (Highlight matches, on by default for tables).
  const hlQuery = e.search_enabled && e.search_source_element_id && ctx && ctx.values
    ? String(ctx.values[e.search_source_element_id] ?? '') : '';
  const table = (
    <SearchHighlightZone key={e.id} query={hlQuery} enabled={!e.search_highlight_off}
                         color={(e.search_highlight_color && colorCss(e.search_highlight_color)) || null}>
      {tableEl}
    </SearchHighlightZone>
  );
  if (!e.pagination_enabled) return table;
  // 0.45.61 - An API table's paginator: its count, and for Pages from
  // the API, the calls for other pages.
  const apiDriver = e.source === 'api'
    ? <ApiPageDriver el={e} paginationKey={getElementId(e)} ctx={ctx} rowsOnPage={apiRowCount} />
    : null;
  // Synthesize a virtual pagination element that PaginationElement
  // treats exactly like a real standalone pagination linked to
  // this table. `type` is set to 'pagination' just for consistency;
  // PaginationElement doesn't actually check it.
  const paginationEl = {
    type: 'pagination',
    id: `${e.id}__pagination`,
    table_id: getElementId(e),
    per_page_default: Number(e.per_page_default) || 10,
    page_label: e.page_label ?? 'Page',
    of_label: e.of_label || 'of',
    show_per_page: !!e.show_per_page,
    per_page_label: e.per_page_label ?? 'Show per page',
    /* 0.42.27 - These three were offered in the table's settings and
       read by PaginationElement, but never passed through - so a
       table's own Next / Previous wording and page text never
       appeared. Found by building the repeating display's paginator
       from this one and comparing it with what the component reads. */
    next_label: e.next_label || '',
    prev_label: e.prev_label || '',
    page_text: e.page_text || '',
    pagination_small: !!e.pagination_small,
  };
  return (
    <React.Fragment key={e.id}>
      {/* 0.45.85 - Where a page change scrolls back to. */}
      <div data-pagination-anchor={getElementId(e)} aria-hidden="true" />
      {table}
      {apiDriver}
      {/* 0.36.686 — Gap between the table and its built-in
        * paginator. Matches the visual spacing admin would see if
        * they added a standalone Pagination element separately:
        * a standalone element gets its own wrapper div with
        * `marginBottom: colMarginBottomFor(el)` (see
        * renderElementInRow), and the table above it gets the same
        * — so the between-gap is one `colMarginBottomFor(table)`.
        * The built-in paginator shares a Fragment with the table,
        * so we apply the same gap here via marginTop. */}
      <div style={{ marginTop: colMarginBottomFor(e) }}>
        <PaginationElement el={paginationEl} />
      </div>
    </React.Fragment>
  );
}


// 0.39.16 - Public file_upload element. A small wrapper holds the
// selected File in state and forwards config to the canonical
// FileUpload component. Publishes the file name into the form values
// via the render context so rules / submission can see it (the raw
// File isn't serializable into the values map, so we store the name).
/* 0.42.44 - Every value a File upload publishes, by TYPE. One place,
 * used by both the pipeline's clearing and the unmount cleanup. */
const FILE_TEXT_KEYS = ['base64', 'extension', 'mime', 'size', 'size_bytes', 'count',
  'width', 'height', 'long_side', 'megapixels'];
// 0.45.97 - `files`: every file as {name, base64}, for an API that takes
// files with their names ({files:task:...} / {files:form:...}).
const FILE_LIST_KEYS = ['base64_list', 'extension_list', 'mime_list', 'size_list',
  'size_bytes_list', 'files'];

// 0.42.50 - exported for the admin Preview, so it behaves as the page does.
export function FileUploadField({ el, ctx }) {
  const eid = getElementId(el);
  const [file, setFile] = React.useState(null);

  /* 0.42.80 - Coming back to a step: take the files from the flow. */
  React.useEffect(() => {
    if (file) return;
    const kept = eid ? ctx?.values?.[`${eid}__files`] : null;
    if (Array.isArray(kept) && kept.length > 0) {
      setFile(el.multiple ? kept : kept[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* 0.42.11 - Clear the published name when this unmounts.
   *
   * The File lives in local state; the NAME lives in the form's values.
   * A rule hiding the element, or the citizen stepping back a page,
   * takes the file away and leaves the name - so the element shows "No
   * file chosen" while every rule reading that field still believes one
   * is attached. The visible state said nothing, the logic said
   * something, and only the logic decided what happened next.
   *
   * Published on unmount rather than guessed at: the two can no longer
   * disagree. */
  // 0.42.22 - Which selection may publish; see handleChange below.
  const generation = React.useRef(0);
  // 0.42.32 - True while a selection is being read; see the rule-clear
  // watcher below handleChange.
  const reading = React.useRef(false);

  React.useEffect(() => () => {
    /* 0.42.22 - Invalidate any read still in flight. Without this a
       file being read when the element unmounted would finish
       afterwards and re-publish its name and bytes - for an element
       that is no longer on the page. */
    generation.current += 1;
    /* 0.42.80 - In a flow, KEEP them. Stepping forward unmounts the
       page, and clearing here threw the citizen's file away: they came
       back to an empty field having chosen nothing since. */
    if (ctx && ctx.keepOnUnmount) return;
    if (eid && ctx && typeof ctx.updateValue === 'function') {
      ctx.updateValue(eid, '');
      // 0.42.15 - and the published image, for the same reason.
      // 0.42.18 - and every published fact, or a rule would read a
      // size and an extension for a file that is no longer there.
      // 0.42.44 - The SAME key lists clearAll uses. This cleanup kept a
      // copy of its own, which is how the two would drift apart.
      FILE_TEXT_KEYS.forEach(k => ctx.updateValue(`${eid}_${k}`, ''));
      FILE_LIST_KEYS.forEach(k => ctx.updateValue(`${eid}_${k}`, []));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eid]);

  /* 0.42.22 - ONE pipeline, and the name is published LAST.
   *
   * Before this the name went out immediately while the bytes and the
   * dimensions followed when the browser finished reading them. A rule
   * on "is filled" - which tests the name - therefore ran in the gap:
   * on a first file `_base64` was empty, and on a second it still held
   * the PREVIOUS file's bytes. An API call made on that rule sent the
   * wrong image. That is worse than failing, because nothing looks
   * wrong.
   *
   * Now, on every new selection:
   *
   *   1. everything is cleared at once, name included, so "is filled"
   *      is false while the file is read
   *   2. the bytes and, for an image, the dimensions are read
   *   3. every value is published, and the NAME goes last
   *
   * So "is filled" means "everything about this file is ready", and a
   * rule firing on it reads the bytes of the file it describes.
   *
   * A GENERATION number guards the other race: pick a file, then
   * another before the first is read, and the first reader can finish
   * second. Only the newest selection may publish; an older one that
   * finishes late is discarded.
   */
  const BASE64_DEFAULT_CAP_MB = 50;


  const put = (suffix, v) => {
    if (!eid || !ctx?.updateValue) return;
    ctx.updateValue(suffix ? `${eid}_${suffix}` : eid,
      v === undefined || v === null ? '' : String(v));
  };

  // 0.42.43 - An array stored AS an array. put() turns a value into
  // text, which would flatten a list of files into one comma string.
  const putList = (suffix, arr) => {
    if (!eid || !ctx?.updateValue) return;
    ctx.updateValue(`${eid}_${suffix}`, Array.isArray(arr) ? arr.slice() : []);
  };

  const clearAll = () => {
    put('', '');
    // 0.42.44 - Cleared to their own type: text to '', lists to [].
    // A list field that were sometimes '' would not be "a list, always".
    FILE_TEXT_KEYS.forEach(k => put(k, ''));
    FILE_LIST_KEYS.forEach(k => putList(k, []));
  };

  const readAsBase64 = (file) => new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => {
      const out = String(reader.result || '');
      const comma = out.indexOf(',');
      resolve(comma === -1 ? '' : out.slice(comma + 1));
    };
    // Unreadable resolves EMPTY, never stale: an Image showing the
    // previous citizen's photo would be a data-protection incident.
    reader.onerror = () => resolve('');
    reader.readAsDataURL(file);
  });

  const readDimensions = (file) => new Promise((resolve) => {
    if (!file.type?.startsWith('image/')) { resolve(null); return; }
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ w: img.naturalWidth, h: img.naturalHeight });
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
    img.src = url;
  });

  const handleChange = async (f) => {
    setFile(f);
    /* 0.42.80 - The chosen File objects, kept with the answers.
     *
     * The published values are text; the files themselves lived only in
     * this component's state, so returning to a step showed an empty
     * list even once the values survived. A flow holds these, so the
     * table, Remove and re-reading all work on the way back. Ordinary
     * pages set it too and simply never read it again. */
    if (eid && ctx?.updateValue) {
      ctx.updateValue(`${eid}__files`, Array.isArray(f) ? f : (f ? [f] : []));
    }
    const mine = ++generation.current;
    reading.current = true;

    // 1. Nothing about the new file is ready, so nothing claims to be.
    clearAll();

    const list = Array.isArray(f) ? f : (f ? [f] : []);
    const first = list[0];
    if (!first) { reading.current = false; return; }

    // 2. Read. The admin's size limit bounds the bytes; with none set,
    //    50 MB - beyond that they are left empty, as before.
    const capMb = Number(el.max_size_mb) > 0 ? Number(el.max_size_mb) : BASE64_DEFAULT_CAP_MB;
    const extOf = (f) => {
      const n = f.name || '';
      const d = n.lastIndexOf('.');
      return d === -1 ? '' : n.slice(d + 1).toLowerCase();
    };
    const mbOf = (f) => (f.size / (1024 * 1024)).toFixed(1);

    /* 0.42.44 - Every field has ONE type, whatever the setting.
     *
     * 0.42.43 made `_base64` a string for one file and a list for
     * several, so ticking "Allow more than one file" silently changed
     * the shape of every value an API was bound to - and broke it. Now
     * no field's type depends on that checkbox:
     *
     *   _base64, _extension, _mime, _size, _size_bytes
     *       TEXT, always: the FIRST file. What a single-document API and
     *       the Image element expect, and they keep working either way.
     *   _base64_list, _extension_list, _mime_list, _size_list,
     *   _size_bytes_list
     *       a LIST, always: every file in the table's order - a list of
     *       one when one file is chosen. What a many-documents API
     *       expects, and it too works either way.
     *
     * The admin picks the shape by picking the field, once, in the API
     * binding, instead of having it shift under them.
     *
     * Dimensions describe the first image with one file, and are left
     * empty with several: one width cannot describe a set.
     *
     * A file over the size cap keeps its place with an EMPTY entry, so
     * the list positions still line up. */
    const [all, dims] = await Promise.all([
      Promise.all(list.map(f =>
        (f.size > capMb * 1024 * 1024 ? Promise.resolve('') : readAsBase64(f)))),
      el.multiple ? Promise.resolve(null) : readDimensions(first),
    ]);

    // A newer selection started while this one was reading: drop it.
    // (The newer one owns the reading flag now, so leave it alone.)
    if (mine !== generation.current) return;

    // 3. Everything else first...
    put('base64', all[0] || '');
    put('extension', extOf(first));
    put('mime', first.type || '');
    put('size', mbOf(first));
    put('size_bytes', first.size);
    putList('base64_list', all);
    putList('extension_list', list.map(extOf));
    putList('mime_list', list.map(f => f.type || ''));
    putList('size_list', list.map(mbOf));
    putList('size_bytes_list', list.map(f => f.size));
    // 0.45.97 - Name and content together. A file over the size cap has
    // no content and is left out here: an archive cannot hold half a file.
    putList('files', list.map((f, i) => ({ name: f.name || '', base64: all[i] || '' }))
      .filter(x => x.base64));
    put('count', list.length);
    if (dims) {
      put('width', dims.w);
      put('height', dims.h);
      put('long_side', Math.max(dims.w, dims.h));
      put('megapixels', ((dims.w * dims.h) / 1000000).toFixed(1));
    }

    // ...and the name LAST, because it is what "is filled" tests.
    put('', list.map(x => x.name).join(', '));
    reading.current = false;
  };

  /* 0.42.32 - A rule's `clear value` drops the FILE, not only the text.
   *
   * Before this, clearing the field emptied its published value while
   * the element went on holding and showing the file - the logic said
   * nothing was attached, the screen said something was, and the bytes
   * were still published for the next API call.
   *
   * The element watches its own value: emptied from outside while it
   * holds a file means a rule cleared it, so the file goes and every
   * published value with it.
   *
   * Except while READING. The pipeline itself empties the value at the
   * start of every selection so "is filled" is false until the file is
   * ready - without the flag the element would take its own clearing
   * for a rule's and throw away the file it had just been given. */
  const ownValue = ctx && ctx.values ? ctx.values[eid] : undefined;
  /* Captured at RENDER time, not read live in the effect. An effect
     runs after paint, and a small file can finish reading first - so
     the live flag would already say "done" while this render's value
     is still the pipeline's own empty one, and the file just chosen
     would be thrown away. Each render's effect must judge by what was
     true when THAT render was made. */
  const readingAtRender = reading.current;
  React.useEffect(() => {
    if (readingAtRender || !file) return;
    const empty = ownValue === undefined || ownValue === null || ownValue === ''
      || (Array.isArray(ownValue) && ownValue.length === 0);
    if (!empty) return;
    generation.current += 1;   // abandon anything still in flight
    setFile(null);
    clearAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownValue, readingAtRender]);
  /* 0.42.37 - Every text RESOLVED, as other elements' are.
   *
   * These were passed to the component raw. 0.42.09 gave them library
   * buttons, which store a value as `=lib("...")` - and since nothing
   * here evaluated it, a citizen would have seen the formula itself
   * rather than the text in their language. resolveAdminText evaluates
   * the expression in the viewer's language and fills {field} values,
   * exactly as for any other element.
   *
   * The limit message is the exception. It holds placeholders the
   * COMPONENT fills - {uploaded_size} and the rest - and the resolver
   * blanks any placeholder it does not know. So it is resolved only
   * when it is an expression: a library row's text comes back with its
   * placeholders intact, because an expression's result is not
   * interpolated. Plain text is passed through untouched. */
  // 0.43.79 - An empty label stays empty; only an unset one falls back.
  const txt = (raw, fallback) => {
    if (raw == null) return fallback;
    const out = resolveAdminText(raw, ctx);
    return out == null ? '' : String(out);
  };
  /* 0.42.38 - The same careful resolution for any text that carries a
     placeholder the COMPONENT fills: evaluated only when it is a library
     expression, whose result keeps its placeholders; plain text passed
     through, because the resolver would blank them. */
  const template = (raw, fallback) => {
    const r = raw || '';
    if (!r) return fallback;
    const out = isExpression(r) ? String(resolveAdminText(r, ctx) || '') : r;
    return out || fallback;
  };
  const limitTemplate = template(el.error_limit, '');

  return (
    <FileUpload
      // 0.48.59 - Read-only, or locked by a rule (which did nothing here).
      disabled={!!el.read_only || !!(eid && ctx?.ruleState && isFieldLocked(eid, ctx.ruleState))}
      label={txt(el.label, '')}
      hint={txt(el.hint, '')}
      accept={el.accept || ''}
      buttonLabel={txt(el.button_label, 'Choose file')}
      noFileText={txt(el.no_file_text, 'No file chosen')}
      constraint={txt(el.constraint_text, '')}
      // 0.42.08 - Limits from the element's configuration.
      limitMessage={limitTemplate}
      // 0.42.50 - One message per kind of failure. Resolved as templates:
      // each carries placeholders the upload fills in.
      limitMessages={{
        type: template(el.error_type, ''),
        size: template(el.error_size, ''),
        dimension: template(el.error_dimension, ''),
        megapixels: template(el.error_megapixels, ''),
        count: template(el.error_count, ''),
      }}
      removeLabel={txt(el.remove_label, 'Remove')}
      // 0.42.43 - how many files a multi-file upload accepts; 0 = any.
      maxFiles={Number(el.max_files) || 0}
      // 0.48.62 - Emptied by the admin: no text at all (it fell back to
      // the English default). Only an element that never had one gets it.
      uploadedText={el.uploaded_text == null ? '{uploaded_count} file(s) uploaded' : template(el.uploaded_text, '')}
      // 0.42.37 - The size units, so KB and MB can come from a library.
      sizeUnitKb={txt(el.size_unit_kb, 'KB')}
      sizeUnitMb={txt(el.size_unit_mb, 'MB')}
      multiple={!!el.multiple}
      // 0.48.58 - Chosen files as a table or as tags with an x.
      listStyle={el.file_list === 'tags' ? 'tags' : 'table'}
      maxSizeMb={Number(el.max_size_mb) || 0}
      maxMegapixels={Number(el.max_megapixels) || 0}
      maxDimensionPx={Number(el.max_dimension_px) || 0}
      value={file}
      onChange={handleChange}
    />
  );
}

function renderFileUpload(e, ctx) {
  return <FileUploadField key={e.id} el={e} ctx={ctx} />;
}


/* 0.47.28 - Chip (components/Chip.jsx). Its text, and colors from rules
 * (set text on <id>, <id>.fill_color, <id>.text_color, <id>.stroke_color). */
function renderChip(e, ctx) {
  const eid = e && (e.element_id || e.name);
  const texts = ctx && ctx.ruleState && ctx.ruleState.texts;
  const pick = (k) => (eid && texts && texts.has(k) ? texts.get(k) : null);
  const raw = pick(eid);
  const text = resolveAdminText(raw != null ? raw : (e.text || ''), ctx);
  // 0.47.38 - A formula from a rule gives the color.
  const pickColor = (k) => { const v = pick(k); return typeof v === 'string' && /^\s*=/.test(v) ? resolveAdminText(v, ctx) : v; };
  const colors = eid ? { fill: pickColor(`${eid}.fill_color`), text: pickColor(`${eid}.text_color`), stroke: pickColor(`${eid}.stroke_color`) } : {};
  return <div key={e.id}><Chip e={e} text={text} colors={colors} /></div>;
}

/* 0.49.16 - Progress bar. The value, the min and the max may each be a
   number, {field} or =formula, worked out on every render. A rule's set
   text on the bar's own ID replaces the value. */
export function progressLabelText(e, n, resolve, formatNumber = null) {
  const fmt = (x) => {
    if (x === null || x === undefined) return '';
    const r = Math.round(x * 100) / 100;
    const f = formatNumber ? formatNumber(r) : null;
    return f == null ? String(r) : String(f);
  };
  const tpl = String(e.progress_text || '{percent}%')
    .split('{percent}').join(String(Math.round(n.percent)))
    .split('{value}').join(fmt(n.value))
    .split('{min}').join(fmt(n.min))
    .split('{max}').join(fmt(n.max));
  return resolve(tpl);
}
function renderProgressBar(e, ctx) {
  const eid = e && (e.element_id || e.name);
  const texts = ctx && ctx.ruleState && ctx.ruleState.texts;
  const override = eid && texts && texts.has(eid) ? texts.get(eid) : null;
  const res = (v) => (v === undefined || v === null ? '' : resolveAdminText(String(v), ctx));
  const numbers = progressNumbers(res(override != null ? override : e.progress_value),
    res(e.progress_min), res(e.progress_max));
  return (
    <div key={e.id}>
      <ProgressBar e={e} numbers={numbers}
        label={e.progress_label === 'custom'
          ? progressLabelText(e, numbers, (t) => resolveAdminText(t, ctx), (v) => _formatValueViaCascade(v, ctx))
          : (e.progress_percent === false && numbers.value !== null
            ? String(_formatValueViaCascade(Math.round(numbers.value * 100) / 100, ctx)) : null)}
        ariaLabel={e.label ? resolveAdminText(e.label, ctx) : undefined} />
    </div>
  );
}

const RENDERERS = {
  progress_bar: renderProgressBar,
  title: renderTitle,
  chip: renderChip,
  paragraph: renderParagraph,
  markdown: renderMarkdown,
  hint: renderHint,
  alert: renderAlert,
  panel: renderPanel,
  separator: renderSeparator,
  faq: renderFaq,
  table: renderTable,
  pagination: renderPagination,
  search: renderSearch,
  button: renderButton,
  image: renderImage,
  // 0.44.55
  google_map: renderGoogleMap,
  // 0.42.40
  loader: renderLoader,
  // 0.42.66
  stepper: renderStepper,
  spacer: renderSpacer,
  scroll_marker: renderScrollMarker,
  payment_card: renderPaymentCard,
  information_card: renderInformationCard,
  guide_card: renderGuideCard,
  file_upload: renderFileUpload,
  // 0.36.43 — Layers are no longer registered here. They're ALWAYS
  // consumed by bandsFromStructure as band starts; the full_bleed
  // value affects only the band wrapper's width at render time
  // (not whether it groups content).
};

// 0.40.98 - Password field with a reveal toggle.
//
// A separate component because the renderer's element switch is inside
// a render function: it cannot hold the useState the toggle needs.
//
// The button is type="button" so it never submits the form, and it is
// aria-hidden from the label association while carrying its own
// aria-label, so a screen reader announces "Show password" rather than
// reading it as part of the field's label. aria-pressed reports state.
//
// Revealing is a deliberate act by the person at the keyboard; the
// field still renders masked on every load, and nothing persists the
// revealed state.
function PasswordInput({ inputProps, minLength, maxLength }) {
  const [revealed, setRevealed] = useState(false);
  return (
    <span className="password-field">
      <input
        {...inputProps}
        type={revealed ? 'text' : 'password'}
        autoComplete="new-password"
        {...(inputProps.autoComplete === 'current-password' ? { autoComplete: 'current-password' } : {})}
        /* 0.41.02 - Third-party password managers ignore
           autocomplete="new-password"; these are their documented
           opt-outs. Without them LastPass / 1Password / Bitwarden will
           offer to fill a citizen's saved account into a government
           form. */
        {...(inputProps.autoComplete === 'current-password' ? {}
          : { 'data-lpignore': 'true', 'data-1p-ignore': '', 'data-bwignore': 'true' })}
        minLength={minLength || undefined}
        maxLength={maxLength || undefined}
      />
      <button
        type="button"
        className="password-reveal"
        onClick={() => setRevealed(v => !v)}
        aria-label={revealed ? 'Hide password' : 'Show password'}
        aria-pressed={revealed}
        title={revealed ? 'Hide' : 'Show'}
      >
        <BuiltinIcon name={revealed ? 'visibility_off' : 'visibility'} size={20} />
      </button>
    </span>
  );
}

const INPUT_TYPES = new Set([
  'text', 'password', 'textarea', 'number', 'email',
  'dropdown', 'multiselect', 'radio', 'checkbox',
  'date', 'phone',
  // 0.44.74 - The toggle has a case in renderRealInput but was in
  // neither this list nor RENDERERS, so it rendered nothing on pages.
  'toggle',
  // 0.48.71
  'cascade',
]);

// 0.38.19 - Excel-style formatted number input for read-only /
// computed display. Number stays raw in state (so downstream
// arithmetic works); the input just shows a formatted string.
//
// Used only when the field either has a set_value expression rule
// (formula cell) OR is locked (output display). Editable number
// inputs with no such rule keep their existing raw behavior so
// citizens who type into a normal number field see exactly what
// they typed.
//
// Behavior:
//
//   * not focused (default): shows the raw stored number formatted
//     via the same cascade resolveAdminText uses - per-form settings
//     first, platform-wide default second, raw String() last
//   * focused (citizen clicks in): shows the raw digits with
//     the browser's canonical "." decimal separator, cursor at
//     end, ready to overtype
//   * on blur: parse the typed text (strip grouping, accept both
//     "." and "," as decimal separator so Armenian/German citizens
//     don't have to hunt for the period key), store the resulting
//     Number in form state, revert to formatted display
//
// The switch to type="text" + inputMode="decimal" is necessary
// because HTML <input type="number"> rejects any value containing
// a thousands separator - the browser strips or refuses to accept
// "120,000" as the DOM value. type="text" lets us display the
// formatted string; inputMode="decimal" preserves the mobile
// keypad that "number" would have surfaced.
function _parseFormattedNumber(input) {
  if (input == null) return null;
  const s = String(input).trim();
  if (s === '') return null;
  // Remove EVERYTHING that isn't a digit, a sign, a period, or a
  // comma. This strips thousands separators (which may be U+00A0,
  // U+202F, regular space, dot, or comma depending on locale)
  // without needing to know the locale in advance.
  const cleaned = s.replace(/[^\d.,\-+]/g, '');
  if (cleaned === '' || cleaned === '-' || cleaned === '+') return null;
  const dotCount = (cleaned.match(/\./g) || []).length;
  const commaCount = (cleaned.match(/,/g) || []).length;
  let normalized;
  if (dotCount === 0 && commaCount === 0) {
    normalized = cleaned;
  } else if (dotCount > 0 && commaCount > 0) {
    // Both separators present - the one appearing LAST is the
    // decimal (matches "1.234,56" and "1,234.56" conventions).
    const lastDot = cleaned.lastIndexOf('.');
    const lastComma = cleaned.lastIndexOf(',');
    if (lastDot > lastComma) {
      normalized = cleaned.replace(/,/g, '');
    } else {
      normalized = cleaned.replace(/\./g, '').replace(',', '.');
    }
  } else {
    // Only one separator type. Ambiguous without locale context -
    // "120,000" could be 120000 (English grouping) or 120.0
    // (European decimal). Heuristic: if the separator appears
    // more than once (1,234,567) OR appears exactly once followed
    // by exactly three digits at the end (120,000), treat it as
    // grouping. Otherwise treat as decimal.
    const sep = dotCount > 0 ? '.' : ',';
    const sepCount = dotCount + commaCount;
    const lastSep = cleaned.lastIndexOf(sep);
    const afterLast = cleaned.length - lastSep - 1;
    const looksLikeGrouping = sepCount > 1 || afterLast === 3;
    if (looksLikeGrouping) {
      // Strip all occurrences (they're all grouping).
      normalized = cleaned.split(sep).join('');
    } else {
      // Decimal separator. Normalize comma to dot for JS parsing.
      normalized = sep === ',' ? cleaned.replace(',', '.') : cleaned;
    }
  }
  const n = Number(normalized);
  return Number.isFinite(n) ? n : null;
}

// 0.38.25 - Detect the decimal separator character a given locale
// uses ("." for en-*, "," for hy-AM / de-DE / most of Europe, etc.).
// Extracted by formatting 1.1 in the locale and stripping the two
// digits - whatever is left is the separator. Falls back to "." if
// Intl rejects the locale.
function _localeDecimalSeparator(locale) {
  try {
    const formatted = new Intl.NumberFormat(locale || undefined).format(1.1);
    // Strip digits; what's left is the separator character.
    const sep = formatted.replace(/[\d\s]/g, '');
    return sep || '.';
  } catch (_e) {
    return '.';
  }
}

// 0.38.25 - Strict input filter for the number-input onChange.
// Removes any character that isn't a digit, a leading minus, or
// the locale's decimal separator - and enforces at most one
// decimal separator. Applied on every keystroke and paste, so
// invalid chars never make it into the visible input value.
//
// The separator character comes from the platform/form cascade
// locale (or the browser locale if neither is set), so a citizen
// on an Armenian deployment can only type "," as decimal while
// one on a US deployment can only type ".".
//
// Empty result is fine - the citizen has cleared the field. The
// blur handler stores '' in that case (no NaN poisoning).
function _filterNumberInput(text, decimalSep) {
  if (typeof text !== 'string' || text === '') return text || '';
  let result = '';
  let hasDecimal = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (c >= '0' && c <= '9') {
      result += c;
    } else if (c === '-' && result === '') {
      // Minus only allowed at the very start.
      result += c;
    } else if (c === decimalSep && !hasDecimal) {
      // One decimal separator max.
      result += c;
      hasDecimal = true;
    }
    // Every other character (letters, spaces, symbols, the OTHER
    // separator character) gets silently dropped.
  }
  return result;
}

function FormattedNumberInput({
  id, name, value, onChange, disabled, required, placeholder,
  className, min, max, step,
  // Cascade dependencies plumbed through so this component doesn't
  // need its own context wiring; parent passes what it already has.
  formatFn, formatPattern, formatLocale,
  // 0.47.73 - Min / Max: told the value when the field is left, and
  // the key / input / paste handlers that refuse a minus sign.
  onLeave = null, onKeyDown = undefined, onBeforeInput = undefined, onPaste = undefined,
}) {
  const [isFocused, setIsFocused] = useState(false);
  // Local buffer for what the citizen has typed during focus.
  // Committed on blur; also live-propagated to parent state via
  // onChange during typing so downstream rules (totals,
  // validation) react without waiting for blur.
  const [editingText, setEditingText] = useState('');

  // 0.38.25 - Effective locale for the decimal separator. Priority
  // matches the cascade the display uses: explicit form/platform
  // locale first, then citizen's browser locale, then en-US as a
  // last resort. Computed each render so a mid-session platform-
  // locale change picks up immediately.
  const effectiveLocale = formatLocale
    || (typeof navigator !== 'undefined' && navigator.language)
    || 'en-US';
  const decimalSep = _localeDecimalSeparator(effectiveLocale);

  // Numeric interpretation of the stored value. Empty string,
  // null, undefined, or unparseable content all yield null - we
  // render empty or fall back to the raw string in that case
  // rather than showing "NaN".
  let numericValue = null;
  if (typeof value === 'number' && Number.isFinite(value)) {
    numericValue = value;
  } else if (typeof value === 'string' && value !== '') {
    const parsed = _parseFormattedNumber(value);
    if (parsed !== null) numericValue = parsed;
  }

  // Formatted for display when not editing. Priority:
  //   1. numericValue exists + cascade pattern set -> format it
  //   2. numericValue exists but no pattern -> raw String(number)
  //   3. value is a non-empty string that didn't parse -> show as-is
  //      (fallback so an admin-set string doesn't disappear)
  //   4. otherwise blank
  let displayValue = '';
  if (numericValue !== null) {
    if (formatPattern && typeof formatFn === 'function') {
      try {
        displayValue = formatFn(numericValue, formatPattern, formatLocale || '');
      } catch (_e) {
        displayValue = String(numericValue);
      }
    } else {
      displayValue = String(numericValue);
    }
  } else if (typeof value === 'string' && value !== '') {
    displayValue = value;
  }

  const shownValue = isFocused
    ? editingText
    : displayValue;

  return (
    <input
      type="text"
      inputMode="decimal"
      id={id}
      name={name || undefined}
      value={shownValue}
      onChange={(ev) => {
        // 0.38.25 - Strict char filter: drop anything that isn't a
        // digit, a leading minus, or the locale's decimal separator.
        // Applied on every keystroke AND paste, so invalid chars
        // never appear in the visible input value. Filter is safe on
        // empty string; safe on browser-injected content; idempotent
        // on already-clean input.
        const text = _filterNumberInput(ev.target.value, decimalSep);
        setEditingText(text);
        // 0.38.20 - Live-parse so rules that reference this field
        // (totals, validation, set_value chains) react as the
        // citizen types. Blur-only commit was too slow: a form
        // that says "total = qty * price" wouldn't update the
        // total until the citizen clicked away, which felt
        // broken. Now typing "5" into qty updates state to 5
        // immediately; blur just guarantees a final parse and
        // handles the reformat.
        if (text === '') {
          onChange('');
          return;
        }
        const parsed = _parseFormattedNumber(text);
        if (parsed !== null) onChange(parsed);
        // else: unparseable mid-typing (e.g. "1,"); leave last
        // valid parent state alone until they finish typing or
        // blur clears it.
      }}
      onFocus={() => {
        // Reveal raw digits, using the locale's decimal separator
        // so a European citizen doesn't see the value flip from
        // "1,5" (displayed) to "1.5" (edit) and back. String(n)
        // always uses JS canonical "." so we replace it when the
        // locale uses "," instead. Integer values (no fractional
        // part) are unaffected by this substitution.
        if (numericValue === null) {
          setEditingText('');
        } else {
          const raw = String(numericValue);
          setEditingText(decimalSep === '.' ? raw : raw.replace('.', decimalSep));
        }
        setIsFocused(true);
      }}
      onBlur={() => {
        setIsFocused(false);
        // Final parse. Empty input clears the field; parseable
        // input already committed via the onChange path above but
        // we re-commit here for safety; unparseable input is
        // discarded so state never contains a string like "abc".
        const trimmed = editingText.trim();
        if (trimmed === '') {
          onChange('');
          if (onLeave) onLeave('');
        } else {
          const parsed = _parseFormattedNumber(trimmed);
          if (parsed !== null) onChange(parsed);
          if (onLeave) onLeave(parsed !== null ? parsed : '');
        }
      }}
      onKeyDown={onKeyDown}
      onBeforeInput={onBeforeInput}
      onPaste={onPaste}
      disabled={disabled}
      required={required}
      placeholder={placeholder}
      className={className}
      min={min}
      max={max}
      step={step}
    />
  );
}

// 0.36.106 — Recursively walk the structure and call `cb` for every
// 0.36.648 / 0.36.720 — Repeater helpers moved to utils/repeater.js.
// After Ship A, only rewriteElementIdsRecursive remains — the old
// __count / instance-shift helpers were removed with the runtime.
// Ship B will re-import this for template scoping.
import {
  rewriteElementIdsRecursive as _rewriteElementIdsRecursive,
} from '../utils/repeater';
// 0.36.721 — Ship B repeater runtime component.
import RepeaterRuntime from './RepeaterRuntime';
// 0.41.84 - A template rendered once per row of a data source.
import RepeatingDisplay from './RepeatingDisplay';
// 0.42.39 - Image frame, shared by the page and the preview.
import { imageFrameStyle, imageAlignStyle, imageAlignOf, imageSizeStyle, imageSizeModeOf, imageSpaceStyle, imageCornerStyle, roundImageOnLoad, imageCornersOf } from '../lib/imageFrame';
import { withElementPosition } from '../lib/elementPosition';
import { filterRequiredRows } from './PageTable';
// 0.42.40 - The canonical Henaket dots loader.
import HenaketLoader from './HenaketLoader';
// 0.42.54 - The Henaket back-to-top button.
import HenaketScrollTop from './HenaketScrollTop';
// 0.42.66 - The Henaket Step navigator.
import HenaketStepper from './HenaketStepper';
import { useFlow } from '../context/FlowContext';
import ElementBoundary from './ElementBoundary';
import { localizeRows } from '../lib/visitorDates';
import { useFormatVersion } from '../hooks/useFormatVersion';
// 0.45.02 - Markdown's color: a palette token or a custom hex.
import { colorCss } from '../lib/colorCss';
import { columnFormats, shownCell } from '../lib/cellFormat';
import ProgressBar, { progressNumbers } from './ProgressBar';


// input-type element, INCLUDING those nested inside column_group /
// line_group children. Used to seed initialValuesFromStructure (one
// entry per named input) and to find an element by name when an
// edge-triggered set_value/clear_value effect fires (need the type
// to decide between '' and []).
function walkInputs(structure, cb, skipModals = false) {
  if (!Array.isArray(structure)) return;
  for (const el of structure) {
    if (!el) continue;
    if (INPUT_TYPES.has(el.type)) cb(el);
    if (skipModals && el.type === 'column_group' && el.modal) continue;
    if (Array.isArray(el.children)) walkInputs(el.children, cb, skipModals);
  }
}

function initialValuesFromStructure(structure) {
  const init = {};
  walkInputs(structure, (el) => {
    if (!getElementId(el)) return;
    if (el.type === 'checkbox' || el.type === 'multiselect') {
      // Multi-value inputs default to an array. el.default may be an
      // array (per the schema) or absent → empty array.
      // 0.45.67 - Blank entries (a typed list ending in a comma) dropped.
      init[getElementId(el)] = Array.isArray(el.default)
        ? el.default.filter(v => v !== '' && v != null) : [];
    } else {
      // Scalar inputs default to '' unless an explicit default exists.
      // 0.43.74 - A Default value written as a formula ("=...") is not
      // evaluated here, so it starts empty rather than showing the
      // formula to citizens. Computed starting values belong in a
      // "set value" rule (when the page loads).
      init[getElementId(el)] = (typeof el.default === 'string' && isExpression(el.default))
        ? '' : (el.default ?? '');
    }
  });
  return init;
}

// 0.37.28 — Field equality used by the changedFields diff. Treats
// '', null and undefined as one class ("empty") so a field with no
// default that's still empty doesn't show as changed. Arrays
// compare element-by-element in original order (order matters for
// multiselect — we don't try to normalise selections). Anything
// else falls through to strict equality.
function _formValueEqual(a, b) {
  if (a === b) return true;
  const aEmpty = a === '' || a === null || a === undefined
              || (Array.isArray(a) && a.length === 0);
  const bEmpty = b === '' || b === null || b === undefined
              || (Array.isArray(b) && b.length === 0);
  if (aEmpty && bEmpty) return true;
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) if (!_formValueEqual(a[i], b[i])) return false;
    return true;
  }
  /* 0.48.96 - Objects (a variable holding a row, a Cascade's value)
     compare by content, an empty part the same as a missing one. */
  if (a && b && typeof a === 'object' && typeof b === 'object' && !Array.isArray(a) && !Array.isArray(b)) {
    const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
    for (const k of keys) if (!_formValueEqual(a[k], b[k])) return false;
    return true;
  }
  return false;
}

/* 0.48.87 - A rule's value for one part of a Cascade
 * (`set address.region to ...`): the part is written inside the
 * Cascade's value, the other parts kept. Any other name is written as
 * it is. Returns whether something changed. */
function cascadePartOf(structure, name) {
  const s = String(name || '');
  const i = s.indexOf('.');
  if (i <= 0) return null;
  const el = findElementByName(structure, s.slice(0, i));
  return el && el.type === 'cascade' ? [s.slice(0, i), s.slice(i + 1)] : null;
}
function assignFieldValue(next, structure, name, v) {
  const part = cascadePartOf(structure, name);
  if (!part) {
    if (next[name] === v) return false;
    next[name] = v;
    return true;
  }
  const [base, key] = part;
  const cur = next[base] && typeof next[base] === 'object' ? next[base] : {};
  const val = v === null || v === undefined ? '' : String(v);
  if (cur[key] === val) return false;
  next[base] = { ...cur, [key]: val };
  return true;
}

function findElementByName(structure, name) {
  if (!Array.isArray(structure) || !name) return null;
  for (const el of structure) {
    if (!el) continue;
    if (getElementId(el) === name) return el;
    if (Array.isArray(el.children)) {
      const found = findElementByName(el.children, name);
      if (found) return found;
    }
  }
  return null;
}


/**
 * Renders a structure array. Returns null when structure is empty
 * (caller decides what to show in that case).
 *
 * 0.36.35 — Layout is the Henaket 12-column grid. Each element gets
 * wrapped in `.col-{span}` with optional `.offset-{N}` for left
 * positioning. Caller is responsible for the outer `.container`.
 *
 * 0.36.38 — Per-element col_span (1-12, default 12) and col_offset
 * (0-11, default 0) honored. Missing fields default to span=12 /
 * offset=0 so existing element structures continue to render
 * identically (full-width, no offset).
 *
 * 0.36.39 — Responsive overrides: col_md_span and col_md_offset
 * (optional). When set, the element emits `col-md-{N}` and
 * `offset-md-{N}` alongside its base `col-{N}` / `offset-{N}`.
 * Below the md breakpoint (480 / 768 / 991 / 1200 — md = 768+) the
 * element uses its xs layout; at md and above, the md override
 * takes effect. Citizens get a real responsive layout: stacked on
 * mobile, side-by-side on tablet+, etc.
 *
 * 0.36.60 — Third tier: col_lg_span / col_lg_offset (optional). lg
 * = 991+ (the existing Henaket lg breakpoint from grid.css). Same
 * inheritance rule as md: each tier cascades from the smaller one
 * unless explicitly overridden. So an element can be 12-col stacked
 * on mobile (xs), 6-col paired on tablet (md), and 4-col triple-
 * stacked on desktop (lg). All three values resolve independently.
 */
function colClassFor(el) {
  // 0.36.88 — Span range relaxed to 0-12. col-0 = hidden at that
  // tier (display: none from grid.css). Offset is meaningless when
  // an element is hidden, so we skip emitting offset classes when
  // the effective span at that tier is 0.
  const xsSpan = clampInt(el.col_span, 0, 12, 12);
  const xsOffset = xsSpan === 0 ? 0
    : clampInt(el.col_offset, 0, 12 - xsSpan, 0);

  const parts = [`col-${xsSpan}`];
  if (xsOffset > 0) parts.push(`offset-${xsOffset}`);

  // md override — only emit when col_md_span is set. col_md_offset
  // alone (without an md span) is a no-op as an override; we'd need
  // to know the md span to validate the offset. If only md offset is
  // given, treat it as a tweak applied at md+ using the xs span:
  // emit offset-md-X but no col-md-X.
  const hasMdSpan = Number.isFinite(el.col_md_span);
  const hasMdOffset = Number.isFinite(el.col_md_offset) && el.col_md_offset > 0;
  let mdEffectiveSpan = xsSpan;
  if (hasMdSpan) {
    // 0.36.88 — 0 is valid (hide at md+).
    mdEffectiveSpan = clampInt(el.col_md_span, 0, 12, xsSpan);
    parts.push(`col-md-${mdEffectiveSpan}`);
    const mdOffset = (mdEffectiveSpan > 0 && hasMdOffset)
      ? clampInt(el.col_md_offset, 0, 12 - mdEffectiveSpan, 0)
      : 0;
    if (mdOffset > 0) parts.push(`offset-md-${mdOffset}`);
  } else if (hasMdOffset && xsSpan > 0) {
    const mdOffset = clampInt(el.col_md_offset, 0, 12 - xsSpan, 0);
    if (mdOffset > 0) parts.push(`offset-md-${mdOffset}`);
  }

  // lg override — same logic.
  const hasLgSpan = Number.isFinite(el.col_lg_span);
  const hasLgOffset = Number.isFinite(el.col_lg_offset) && el.col_lg_offset > 0;
  if (hasLgSpan) {
    // 0.36.88 — 0 is valid (hide at lg+).
    const lgSpan = clampInt(el.col_lg_span, 0, 12, mdEffectiveSpan);
    parts.push(`col-lg-${lgSpan}`);
    const lgOffset = (lgSpan > 0 && hasLgOffset)
      ? clampInt(el.col_lg_offset, 0, 12 - lgSpan, 0)
      : 0;
    if (lgOffset > 0) parts.push(`offset-lg-${lgOffset}`);
  } else if (hasLgOffset && mdEffectiveSpan > 0) {
    const lgOffset = clampInt(el.col_lg_offset, 0, 12 - mdEffectiveSpan, 0);
    if (lgOffset > 0) parts.push(`offset-lg-${lgOffset}`);
  }

  return parts.join(' ');
}

/**
 * 0.38.62 - Column-group child sizing.
 *
 * A child inside a column_group historically inherited the standard
 * `.col-N` behavior: N/12 of its PARENT container's width. Since the
 * group is itself a col-N slot in the outer .row, a child at col-6
 * inside a group at col-md-10 rendered as 6/12 * 10/12 = 5/12 of the
 * page = "5 columns", not the 6 the admin picked. Admin feedback:
 * col-6 should mean "6 columns of the page's 12-col grid", full stop,
 * regardless of which column_group it sits in.
 *
 * This helper returns a { className, style } pair to apply to a
 * column_group child's wrapper. The style sets CSS custom properties
 * (--pgs-xs / --pgs-md / --pgs-lg) with the page-relative percentage
 * that the child should occupy inside the group. A CSS class
 * `.gt18ColGroupChild` (defined in styles/grid.css) reads those vars
 * per breakpoint and overrides flex-basis + max-width.
 *
 * Math per breakpoint:
 *   pct = (childSpan / groupSpan) * 100
 * Where childSpan and groupSpan are what admin set for that breakpoint,
 * falling through to the next-smaller breakpoint if unset.
 *
 * When a child asks for more columns than the group's own span (e.g.
 * child col-8 inside group col-6), pct exceeds 100% and the child
 * overflows the group's right edge intentionally - admin sees exactly
 * what they configured. If they wanted clamping, they'd shrink the
 * child span.
 *
 * The wrapper is applied by wrapping/cloning the child's rendered
 * outer element (which still carries a col-N class from colClassFor);
 * the .gt18ColGroupChild class overrides just the sizing without
 * touching padding / box-sizing / other row-child styling.
 */
function colGroupChildProps(child, groupSpans) {
  if (!child || !groupSpans) return { className: 'gt18ColGroupChild', style: {} };
  // 0.38.63 - Clamp per-breakpoint percentage at 100% so a child
  // wider than the group visually fits INSIDE the group instead of
  // overflowing its right edge. Stored col_span values are NOT
  // modified - admin's authored value is preserved and re-appears
  // if they widen the group later. Rationale: without clamping, any
  // narrowing of the group would require admin to also narrow every
  // child that now exceeds it. Clamping keeps admin's work when
  // they iterate on group widths. Same _clamp helper used in both
  // dimensions.
  const _clamp = (pct) => Math.min(pct, 100);
  const xsChildRaw = Number.isFinite(child.col_span) ? child.col_span : 12;
  const xsChild = Math.max(0, xsChildRaw);
  const groupXs = groupSpans.xs > 0 ? groupSpans.xs : 12;
  const pctXs = _clamp((xsChild / groupXs) * 100);

  const style = { '--pgs-xs': pctXs + '%' };

  if (Number.isFinite(child.col_md_span)) {
    const mdChild = Math.max(0, child.col_md_span);
    const mdGroup = (groupSpans.md > 0 ? groupSpans.md : groupSpans.xs) || 12;
    style['--pgs-md'] = _clamp(mdChild / mdGroup * 100) + '%';
  }
  if (Number.isFinite(child.col_lg_span)) {
    const lgChild = Math.max(0, child.col_lg_span);
    const lgGroup = (groupSpans.lg > 0 ? groupSpans.lg
                  : groupSpans.md > 0 ? groupSpans.md
                  : groupSpans.xs) || 12;
    style['--pgs-lg'] = _clamp(lgChild / lgGroup * 100) + '%';
  }
  return { className: 'gt18ColGroupChild', style };
}

/**
 * 0.38.62 - Read a column_group's own effective spans at each
 * breakpoint (fall-through: md defaults to xs, lg defaults to md).
 * Returns { xs, md, lg } where undefined for md/lg means "use xs".
 * Consumed by colGroupChildProps to compute page-relative percents
 * for each child.
 */
function groupSpansFor(group) {
  const xs = clampInt(group.col_span, 0, 12, 12);
  const md = Number.isFinite(group.col_md_span)
    ? clampInt(group.col_md_span, 0, 12, xs) : undefined;
  const lg = Number.isFinite(group.col_lg_span)
    ? clampInt(group.col_lg_span, 0, 12, md !== undefined ? md : xs)
    : undefined;
  return { xs, md, lg };
}
// 0.36.40 — Hex (and #shorthand) → rgba() helper for the layer
// element. Mirrors the helper in AdminFormEditor.jsx (kept local
// here so this module has no upward dependency). Exported because
// AdminFormPreview renders bands too.
/* 0.45.02 - Whether a layer's color (as painted: over the white page,
 * at its opacity) is dark enough that white text reads better than the
 * platform's near-black (--color-text-800, #1a1a1a). WCAG relative
 * luminance and contrast: white wins below about 0.20. */
export function isDarkLayer(hex, alpha) {
  let h = (hex || '').replace('#', '').trim();
  if (h.length === 3) h = h.split('').map(c => c + c).join('');
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return false;
  const a = Math.max(0, Math.min(1, Number.isFinite(alpha) ? alpha : 1));
  const lin = (i) => {
    const c = (parseInt(h.slice(i, i + 2), 16) * a + 255 * (1 - a)) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  const L = 0.2126 * lin(0) + 0.7152 * lin(2) + 0.0722 * lin(4);
  return (1.05 / (L + 0.05)) > ((L + 0.05) / 0.0603);
}

export function hexToRgba(hex, alpha) {
  let h = (hex || '#FFFFFF').replace('#', '').trim();
  if (h.length === 3) h = h.split('').map(c => c + c).join('');
  if (!/^[0-9a-fA-F]{6}$/.test(h)) h = 'FFFFFF';
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  const a = Math.max(0, Math.min(1, Number.isFinite(alpha) ? alpha : 1));
  return `rgba(${r}, ${g}, ${b}, ${a})`;
}

/* 0.47.01 - Corner radius of a band, in pixels (0 = square, the
 * default). For a Layer it rounds the band's colored area, most
 * useful with "Spans full viewport width" off. */
export function bandRadius(n) {
  const r = Number(n);
  return Number.isFinite(r) && r > 0 ? Math.min(r, 200) : 0;
}

/* 0.47.17 - A Layer's side padding: the space between its colored
 * edge and its content, left and right. Empty keeps the page's own
 * (--container-padding-x, 16px, 12px on narrow phones). */
export function layerSidePadding(layer) {
  if (!layer) return null;
  const n = Number(layer.side_padding);
  if (layer.side_padding == null || layer.side_padding === '' || !Number.isFinite(n) || n < 0) return null;
  const v = `${Math.min(Math.round(n), 200)}px`;
  return { paddingLeft: v, paddingRight: v };
}

/* 0.47.02 - A band's frame: a border, an inner stroke and a shadow.
 * Shared by a Layer (keys without a prefix) and a Column group's color
 * band (keys with "band_").
 *   border   <prefix>border_width (0 = none) + <prefix>border_color
 *   stroke   <prefix>stroke_width (0 = none) + <prefix>stroke_color, a
 *            line drawn INSIDE the band, <prefix>stroke_gap pixels in
 *            from the border's inner edge (from the edge when there is
 *            no border). An outline with a negative offset, so it
 *            follows the corner radius and takes no layout space.
 *   shadow   <prefix>shadow: 'small' is Henaket's static-card shadow
 *            (0 2px 4px, 15%: an Information card at rest, the Image
 *            element's shadow), 'large' its card shadow (0 4px 8px,
 *            30%: a Guide or Information card on hover). */
export const BAND_FRAME_DEFAULTS = { border_color: '#DCDCDC', stroke_color: '#DCDCDC', stroke_gap: 8 };
export const BAND_SHADOWS = {
  small: 'var(--shadow-card-static, 0 2px 4px 0 rgba(0, 0, 0, 0.15))',
  large: 'var(--shadow-card, 0 4px 8px 0 rgba(0, 0, 0, 0.3))',
};
function framePx(v, max) {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? Math.min(Math.round(n), max) : 0;
}
function frameColor(v, fallback) {
  return typeof v === 'string' && /^#[0-9a-fA-F]{3}([0-9a-fA-F]{3})?$/.test(v.trim()) ? v.trim() : fallback;
}
/* 0.47.06 - Whether the band draws an inner stroke. The stroke is the
 * band's ::after (global.css, [data-band-stroke]), with the band's own
 * corner radius: an outline's corners shrink by its offset. */
export function bandHasStroke(el, prefix = '') {
  return !!el && framePx(el[prefix + 'stroke_width'], 32) > 0;
}
export function bandFrameStyle(el, prefix = '', radiusPx = 0) {
  if (!el) return null;
  const k = (name) => el[prefix + name];
  const bw = framePx(k('border_width'), 32);
  const sw = framePx(k('stroke_width'), 32);
  const gap = k('stroke_gap') == null ? BAND_FRAME_DEFAULTS.stroke_gap : framePx(k('stroke_gap'), 200);
  const shadow = BAND_SHADOWS[k('shadow')] || null;
  const out = {};
  if (bw) out.border = `${bw}px solid ${frameColor(k('border_color'), BAND_FRAME_DEFAULTS.border_color)}`;
  if (sw) {
    out['--band-stroke'] = `${sw}px solid ${frameColor(k('stroke_color'), BAND_FRAME_DEFAULTS.stroke_color)}`;
    // Measured from the border's inner edge (the ::after is placed in
    // the padding box, inside the border).
    out['--band-stroke-inset'] = `${gap}px`;
    out['--band-stroke-radius'] = `${Math.max(0, radiusPx || 0)}px`;
  }
  if (shadow) out.boxShadow = shadow;
  return Object.keys(out).length ? out : null;
}

/* 0.47.01 - A Column group can carry its own color band: a colored
 * frame drawn around its elements, with its own corner radius and
 * inner padding. Returns the frame's style, or null when the group
 * has no band. Text turns white on a dark color, as on a Layer. */
export const GROUP_BAND_DEFAULTS = { color: '#F2F2F2', opacity: 1, radius: 8, padding: 16 };
export function groupBandStyle(el) {
  if (!el) return null;
  // 0.47.14 - Inner padding without a color band: the group's own
  // spacing (0 by default, so groups without a band are unchanged).
  if (!el.band) {
    const n = Number(el.band_padding);
    return el.band_padding != null && el.band_padding !== '' && Number.isFinite(n) && n > 0
      ? { padding: `${Math.min(n, 120)}px`, boxSizing: 'border-box' } : null;
  }
  const color = el.band_color || GROUP_BAND_DEFAULTS.color;
  const opacity = Number.isFinite(el.band_opacity) ? el.band_opacity : GROUP_BAND_DEFAULTS.opacity;
  const radius = el.band_radius == null ? GROUP_BAND_DEFAULTS.radius : bandRadius(el.band_radius);
  const padRaw = Number(el.band_padding);
  const padding = el.band_padding == null || !Number.isFinite(padRaw)
    ? GROUP_BAND_DEFAULTS.padding : Math.max(0, Math.min(padRaw, 120));
  const dark = isDarkLayer(color, opacity);
  return {
    background: hexToRgba(color, opacity),
    borderRadius: radius ? `${radius}px` : undefined,
    padding: `${padding}px`,
    boxSizing: 'border-box',
    // 0.47.02 - Border, inner stroke and shadow.
    ...(bandFrameStyle(el, 'band_', radius) || {}),
    ...(dark ? { '--layer-text-color': 'var(--color-white)', color: 'var(--color-white)' } : {}),
  };
}

// 0.36.40 — Group a structure array into "bands". Each band has an
// optional `layer` (config from a layer element — bg color + opacity)
// and an `elements` list (everything between this layer element and
// the next layer element, exclusive on both ends).
//
// Structures with no layer elements yield a single transparent band
// containing all elements — which renders identically to the pre-
// 0.36.40 behaviour (backward compat).
//
// Exported because AdminFormPreview's inline render loop uses the
// same grouping to render bands with editable form fields (instead
// of going through this read-only renderer).
//
// 0.36.42 — Honors layer_end element (closes the current band).
//
// 0.36.43 — Layer elements ALWAYS start a band regardless of their
// full_bleed value. full_bleed now controls only the band's WIDTH
// at render time (full viewport vs container-constrained); it no
// longer affects band grouping. This is the correct semantics: a
// layer's purpose is to group content with a background, and that
// purpose is preserved in both width modes.
/* 0.47.33 - Space below a colored band that an End layer closes: the
 * End layer's Space below, or else the Layer's. None by default. A
 * band with no End layer runs to the footer and has none. */
export function bandSpaceBelow(band) {
  if (!band || !band.layer || !band.end) return undefined;
  const pick = [band.end.margin_bottom, band.layer.margin_bottom].find(v => VALID_SPACE_STEPS.has(v));
  return pick === undefined || pick === 0 ? undefined : `var(--space-${pick})`;
}

/* 0.49.18 - A Layer and End layer inside a Column group color a section
 * of the group: the elements between them, on the group's own grid.
 * `children` are the group's elements and `rendered` what each drew (a
 * Layer or End layer draws nothing). Returns the nodes for the group's
 * row: elements outside a section as they were, each section as one
 * full-width colored box with its elements on a row of their own. The
 * box takes the Layer's color and opacity, picture or video, corner
 * radius, border, inner stroke and shadow; its side padding is the
 * space all round (16px when empty). A section whose elements are all
 * hidden is not drawn. A Layer with no End layer runs to the end of
 * the group. */
export function withGroupSections(children, rendered) {
  const list = Array.isArray(children) ? children : [];
  if (!list.some(c => c && c.type === 'layer')) return rendered;
  const out = [];
  let layer = null;
  let items = [];
  const flush = (end) => {
    if (layer) {
      if (items.some(n => n !== null && n !== undefined && n !== false)) out.push(renderGroupSection(layer, end, items));
    } else {
      out.push(...items);
    }
    items = [];
  };
  list.forEach((c, i) => {
    if (c && c.type === 'layer') { flush(null); layer = c; return; }
    if (c && c.type === 'layer_end') { flush(c); layer = null; return; }
    items.push(rendered[i]);
  });
  flush(null);
  return out;
}

function renderGroupSection(layer, end, items) {
  const bg = hexToRgba(layer.background_color, layer.opacity);
  const radius = bandRadius(layer.corner_radius);
  const dark = isDarkLayer(layer.background_color, layer.opacity);
  const padN = Number(layer.side_padding);
  const pad = layer.side_padding == null || layer.side_padding === '' || !Number.isFinite(padN) || padN < 0
    ? 16 : Math.min(Math.round(padN), 120);
  const mediaSrc = layer.bg_media_src || '';
  const mediaType = layer.bg_media_type || 'image';
  const mediaPosition = layer.bg_media_position || 'cover';
  const hasMedia = !!mediaSrc;
  const below = bandSpaceBelow({ layer, end });
  const style = {
    background: hasMedia ? 'transparent' : bg,
    padding: `${pad}px`,
    boxSizing: 'border-box',
    position: 'relative',
    ...(hasMedia ? { overflow: 'hidden' } : null),
    ...(radius ? { borderRadius: `${radius}px` } : null),
    ...(bandFrameStyle(layer, '', radius) || {}),
    ...(dark ? { '--layer-text-color': 'var(--color-white)', color: 'var(--color-white)' } : null),
  };
  return (
    <div className="col-12" key={`section-${layer.id}`} data-group-section=""
         style={below ? { marginBottom: below } : undefined}>
      <div className="hen-group-band" style={style}
           data-band-stroke={bandHasStroke(layer) ? '' : undefined}>
        {hasMedia && (mediaType === 'video' ? (
          <BandVideo layer={layer} style={{
              position: 'absolute', inset: 0, width: '100%', height: '100%',
              objectFit: mediaPosition === 'contain' ? 'contain' : 'cover',
              objectPosition: bandMediaFocus(layer), zIndex: 0 }} />
        ) : (
          <div aria-hidden="true" style={{
              position: 'absolute', inset: 0,
              backgroundImage: `url(${withBase(mediaSrc)})`,
              backgroundSize: mediaPosition === 'tile' ? 'auto' : mediaPosition,
              backgroundRepeat: mediaPosition === 'tile' ? 'repeat' : 'no-repeat',
              backgroundPosition: bandMediaFocus(layer), zIndex: 0 }} />
        ))}
        {hasMedia && <div aria-hidden="true" style={{ position: 'absolute', inset: 0, background: bg, zIndex: 1 }} />}
        <div className="row" style={hasMedia ? { position: 'relative', zIndex: 2 } : undefined}>
          {items}
        </div>
      </div>
    </div>
  );
}

export function bandsFromStructure(structure) {
  const bands = [];
  let currentLayer = null;
  let currentElements = [];
  // 0.47.33 - `end`: the End layer that closed the band, if any (its
  // Space below the band).
  const pushBand = (end = null) => {
    if (currentLayer !== null || currentElements.length > 0) {
      bands.push({ layer: currentLayer, elements: currentElements, ...(end ? { end } : null) });
    }
  };
  for (const el of structure) {
    if (el.type === 'layer') {
      // Always start a new band.
      pushBand();
      currentLayer = el;
      currentElements = [];
    } else if (el.type === 'layer_end') {
      // Close the current band; return to default no-layer state.
      pushBand(currentLayer ? el : null);
      currentLayer = null;
      currentElements = [];
    } else {
      currentElements.push(el);
    }
  }
  pushBand();
  return bands;
}

/* 0.42.62 - A page can be driven from OUTSIDE.
 *
 * A flow shows several pages one after another and must keep the
 * answers alive across them, so the answers cannot live in the page.
 * Given `externalValues` and `onValueChange`, this renderer uses those
 * and keeps none of its own; given neither, it behaves exactly as it
 * always has, which is what an ordinary page still does.
 *
 * The same for the API state: a response fetched at one step has to be
 * readable at a later one, so a flow holds it.
 *
 * The renderer knows NOTHING about steps or prefixes. The flow hands it
 * this step's own fields under their plain names plus every other
 * step's under theirs, and re-prefixes anything written back. */
/* 0.47.05 - A Layer with no End layer after it runs to the page's
 * footer: its colored area reaches the footer's top edge, however
 * short its content. The space between the band and the footer is
 * measured and added as the band's bottom padding, with the same
 * amount as a negative bottom margin, so the colored area grows while
 * the page's layout (and the footer's place) stays exactly as it was.
 * Measured again whenever the page changes size. */
function useExtendBandToFooter(ref, on) {
  useLayoutEffect(() => {
    let last = null;
    const reset = (el) => { if (el) { el.style.paddingBottom = ''; el.style.marginBottom = ''; } };
    if (!on) { reset(ref.current); return undefined; }
    let raf = 0;
    const measure = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const el = ref.current;
        if (last && last !== el) reset(last);
        last = el;
        if (!el) return;
        const footers = document.querySelectorAll('footer');
        const footer = footers.length ? footers[footers.length - 1] : null;
        const cur = parseFloat(el.style.paddingBottom) || 0;
        if (!footer) { if (cur) reset(el); return; }
        const contentBottom = el.getBoundingClientRect().bottom - cur;
        const gap = Math.max(0, Math.round(footer.getBoundingClientRect().top - contentBottom));
        if (gap !== cur) {
          el.style.paddingBottom = gap ? `${gap}px` : '';
          el.style.marginBottom = gap ? `${-gap}px` : '';
        }
      });
    };
    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (ro) ro.observe(document.body);
    window.addEventListener('resize', measure);
    return () => {
      cancelAnimationFrame(raf);
      if (ro) ro.disconnect();
      window.removeEventListener('resize', measure);
      reset(last || ref.current);
    };
  }, [ref, on]);
}

function _PageContentRendererImpl({ structure, buttonTemplates = [], rules = [], settings = null, topSlot = null,
                                    externalValues = null, onValueChange = null,
                                    externalApiState = null, onApiStateChange = null,
                                    flowSlug = '', flowCopies = null,
                                    system = null, pageSlug = '',
                                    urlValues = null }) {
  // 0.36.106 — Controlled state + runtime rule evaluation on the
  // citizen side. Hooks must be at the top level — calling them
  // before the early-return that handles empty structure.
  /* 0.49.20 - The address's values (lib/urlValues.js) as `url`, so
     url.training reads in formulas, texts and conditions. */
  const [ownValues, setOwnValues] = useState(() => ({
    ...initialValuesFromStructure(structure), ...(urlValues ? { url: urlValues } : {}) }));
  const urlSig = urlValues ? JSON.stringify(urlValues) : '';
  useEffect(() => {
    if (externalValues !== null || !urlValues) return;
    setOwnValues(p => (JSON.stringify((p && p.url) || {}) === urlSig ? p : { ...p, url: urlValues }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlSig]);
  // 0.42.62 - Controlled when a flow supplies them.
  const controlled = externalValues !== null && typeof onValueChange === 'function';
  const values = controlled ? externalValues : ownValues;
  const setValues = controlled
    ? ((updater) => onValueChange(updater))
    : setOwnValues;

  // Re-init values when the structure changes (route change to a
  // different page, structure prop swapped). Without this, navigating
  // from page A to page B would leak A's field values into B.
  //
  // 0.42.62 - NOT when a flow owns them: moving to the next step swaps
  // the structure, and clearing here would wipe the answers the flow
  // exists to carry. The flow decides what a step starts with.
  /* 0.48.96 - Only when the elements really change (another page, the
     editor's canvas). The same page arriving again (fetched once more
     in the visitor's language) used to reset every field, so a value a
     rule set as the page opened was lost, while mark unchanged had
     already taken it as the unchanged value. */
  const structureSig = useMemo(() => {
    const ids = [];
    const walk = (list) => (Array.isArray(list) ? list : []).forEach(el => {
      if (!el) return;
      ids.push(`${el.type}:${getElementId(el) || el.id || ''}:${el.default ?? ''}`);
      walk(el.children);
    });
    walk(structure);
    return `${pageSlug || ''}|${ids.join(',')}`;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [structure, pageSlug]);
  const lastStructureSig = useRef(structureSig);
  useEffect(() => {
    if (controlled) return;
    if (lastStructureSig.current === structureSig) return;
    lastStructureSig.current = structureSig;
    // 0.49.20 - The address's values stay (they belong to the address).
    setOwnValues(prev => ({ ...initialValuesFromStructure(structure),
      ...(prev && prev.url ? { url: prev.url } : {}) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [structureSig, controlled]);

  /* 0.45.68 - Defaults in a flow.
   *
   * A flow owns the values and starts them empty, so no field on a flow
   * step ever showed its Default value: it worked in the form's Preview
   * and on a page of its own, not in the live service. Each step now
   * fills in the defaults of fields the flow has no value for yet. A
   * field the citizen already answered (coming back to a step) keeps
   * its answer. `defaultsSeeded` tells the option-label matching below
   * that the defaults are now in place. */
  const [defaultsSeeded, setDefaultsSeeded] = useState(0);
  // Start again empties the flow's answers on the same step: seed again.
  const flowValuesEmpty = controlled && !!values && Object.keys(values).length === 0;
  useEffect(() => {
    if (!controlled) return;
    const init = initialValuesFromStructure(structure);
    const missing = Object.keys(init).filter(k => {
      const d = init[k];
      const empty = d === '' || d == null || (Array.isArray(d) && d.length === 0);
      return !empty && (values == null || values[k] === undefined);
    });
    if (missing.length === 0) return;
    setValues(prev => {
      const out = { ...prev };
      for (const k of missing) if (out[k] === undefined) out[k] = init[k];
      return out;
    });
    setDefaultsSeeded(n => n + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [structure, controlled, flowValuesEmpty]);

  // ruleState: { hidden, required, locked, forcedValues, forcedEmpty,
  //              byField, rulesFired }. Recomputed every time `values`
  // changes (the rule conditions are functions of the live values).
  const safeRules = Array.isArray(rules) ? rules : [];
  // 0.37.25 — Rule expressions can call `lib(...)` which reads from
  // the shared library cache. Two things that must be true for
  // that call to work in this component:
  //   1. Language must be available at ruleState-compute time, so
  //      lookupLibraryValue can localise translatable columns.
  //      Hoisted this useLanguage() call from later in the file for
  //      this purpose; the earlier usage at ~line 1662 was
  //      collapsed into it.
  //   2. When a lib() call misses the cache, lookupLibraryValue
  //      kicks off an async fetch. We need this component to
  //      re-render when the fetch settles so ruleState recomputes
  //      with real data. The `libTick` state + subscribeLibraryCache
  //      effect below is a plain counter that bumps on every cache
  //      change and threads into ruleState's dep list.
  const { language: pcrLanguage, t: pcrT } = useLanguage();
  const [libTick, setLibTick] = useState(0);
  useEffect(() => {
    const unsubscribe = subscribeLibraryCache(() => setLibTick(t => t + 1));
    return () => unsubscribe();
  }, []);
  // 0.37.27 — Prefetch libraries referenced from expressions in
  // button `action_value` and rule `set_value` effects. Without
  // this, the first click on a `=lib("foo", ...)` button (or the
  // first firing of a rule that calls lib()) misses the cache and
  // returns 0; only on the second click / re-eval does the value
  // arrive. Same UX bug the dropdown prefetch (further down)
  // already avoids for dropdown option lists.
  //
  // Regex-based scan — see extractLibKeysFromExpression. Best-
  // effort by design: only string-literal library names get
  // prefetched. A `lib(fieldRef, ...)` expression whose library
  // name is dynamic can't be pre-warmed anyway (we don't know
  // the value until the expression runs), so the async fetch-on-
  // miss + subscribeLibraryCache re-render path (0.37.25) still
  // handles that case, at the cost of a one-render delay.
  useEffect(() => {
    // 0.37.92 — Timing mark for the debug console.
    if (typeof window !== 'undefined') {
      try {
        const q = new URLSearchParams(window.location.search || '');
        if (q.get('libdebug') === '1' || window.__DEBUG_LIBS === true) {
          const t = typeof performance !== 'undefined' && performance.now
            ? Math.round(performance.now())
            : Date.now();
          // eslint-disable-next-line no-console
          console.log(`[libcache t=${t}ms] prewarm useEffect firing — structure has ${Array.isArray(structure) ? structure.length : 0} top-level elements`);
        }
      } catch (_e) { /* silent */ }
    }
    const keys = new Set();
    // 0.37.90 — Comprehensive walk. Was: only button action_value
    // and rule set_value effects. Now: every text field the
    // renderer might send through resolveAdminText, plus every
    // rule effect whose value/text/label/message field carries an
    // expression, plus every manual table cell/header.
    //
    // Rationale: shipping 0.37.85 (table cells), 0.37.88 (table
    // headers), and 0.37.89 (labels/hints/placeholders/errors/
    // button text) added ~a dozen new sites where `=lib(...)` can
    // appear. Without prewarm those sites hit the cache on first
    // render, showed empty text for ~200ms, then re-rendered with
    // the fetched value — a visible delay admin reported. This
    // walker feeds `extractLibKeysFromExpression` every string
    // that might contain an expression, so fetches start at
    // mount-time (as early as we can go outside of a Suspense
    // rewrite).
    //
    // Helper: scan a single string and add any library keys the
    // expression references. Guard on non-strings.
    // 0.37.93 — Also collects fully-literal lib() calls for point-
    // lookup prewarming. Extra structure per expression: keys (for
    // full-library fetch fallback) + literalCalls (for targeted
    // point-lookups).
    const literalCalls = []; // { libKey, keyColumn, keyValue, valueColumn }
    // 0.37.97 — Batch collector: {libKey → [calls...]} filled in the
    // dispatch loop after the full-library-cache guard runs.
    const batchedByLib = new Map();
    const scanString = (s) => {
      if (typeof s !== 'string' || !s) return;
      for (const k of extractLibKeysFromExpression(s)) keys.add(k);
      for (const call of extractLiteralLibCallsFromExpression(s)) literalCalls.push(call);
    };

    // Walk the structure recursively. For every element, scan
    // every text-bearing field. Manual tables also get their
    // rows and headers walked cell-by-cell.
    const walkAll = (nodes) => {
      if (!Array.isArray(nodes)) return;
      for (const el of nodes) {
        if (!el) continue;
        // Common text fields (present on many element types).
        scanString(el.label);
        scanString(el.text);
        scanString(el.hint);
        scanString(el.help);
        scanString(el.placeholder);
        scanString(el.button_label);       // search element, buttons
        scanString(el.summary_heading);    // checkbox summary block
        scanString(el.summary_value);
        // Buttons specifically may put an expression in
        // action_value — kept from the pre-0.37.90 walker.
        if (el.type === 'button') scanString(el.action_value);
        // Manual tables: scan headers + every cell.
        if (el.type === 'table' && (!el.source || el.source === 'manual')) {
          if (Array.isArray(el.headers)) {
            for (const h of el.headers) scanString(h);
          }
          if (Array.isArray(el.rows)) {
            for (const row of el.rows) {
              if (!Array.isArray(row)) continue;
              for (const cell of row) scanString(cell);
            }
          }
        }
        if (Array.isArray(el.children)) walkAll(el.children);
      }
    };
    walkAll(structure);

    // Rules: scan every effect on every branch. Effects that
    // carry an expression value use different property names
    // (value / label / text / message / values), so we probe
    // each with scanString and skip anything not a string.
    //
    // labels_of() prewarm stays here for set_value only — it
    // needs to walk the structure to find the referenced field
    // element and pull its `library`; other effects don't have
    // that pattern.
    if (Array.isArray(rules)) {
      for (const rule of rules) {
        if (!rule) continue;
        for (const branchName of ['then', 'else']) {
          const branch = Array.isArray(rule[branchName]) ? rule[branchName] : [];
          for (const eff of branch) {
            if (!eff) continue;
            scanString(eff.value);   // set_value, set_action_value, set_cell
            scanString(eff.label);   // set_label, add_option
            scanString(eff.text);    // set_text
            scanString(eff.message); // set_error_message
            if (Array.isArray(eff.values)) {   // add_row
              for (const v of eff.values) scanString(v);
            }
            // 0.37.57 — labels_of(...) inside set_value only.
            if (eff.effect === 'set_value' && typeof eff.value === 'string') {
              for (const fieldName of extractLabelsOfFieldsFromExpression(eff.value)) {
                const el = findElementByName(structure, fieldName);
                if (el && el.library) keys.add(el.library);
              }
            }
          }
        }
      }
    }
    // Fire fetches.
    //
    // 0.37.93 — Prefer point-lookups over full-library fetches. For
    // every fully-literal `lib()` call we found, fire a targeted
    // `fetchLibraryRow` — that's a ~50ms round-trip for a ~200-byte
    // payload instead of the ~3000ms / ~2MB full-library fetch.
    // Only fall back to `fetchLibrary` for library keys that are
    // referenced by lib() calls with DYNAMIC arguments (where we
    // don't know the keyValue at parse time), OR by
    // labels_of(<field>) which needs the whole row set.
    //
    // Track library keys that are already satisfied by point-lookups
    // so we don't ALSO fetch the whole library redundantly.
    const litKeysCovered = new Set();
    for (const call of literalCalls) {
      litKeysCovered.add(call.libKey);
    }
    if (typeof window !== 'undefined') {
      try {
        const q = new URLSearchParams(window.location.search || '');
        if (q.get('libdebug') === '1' || window.__DEBUG_LIBS === true) {
          const t = typeof performance !== 'undefined' && performance.now
            ? Math.round(performance.now()) : Date.now();
          // eslint-disable-next-line no-console
          console.log(`[libcache t=${t}ms] prewarm collected ${keys.size} lib keys + ${literalCalls.length} literal-call point-lookups; lang="${pcrLanguage || ''}"`);
        }
      } catch (_e) { /* silent */ }
    }
    for (const call of literalCalls) {
      // 0.37.95 — Skip if the full-library cache already resolves
      // this key. Common on repeat page loads: localStorage hydrate
      // populated the full library at t=~1000ms, so by the time
      // prewarm useEffect fires the answer is already available and
      // a point-lookup fetch would just be wasted network traffic.
      // Also skip if we've already refreshed this exact row this
      // session (dedup across multiple prewarm firings triggered by
      // structure / rules / pcrLanguage dep changes).
      const fullLib = getCachedLibrary(call.libKey, pcrLanguage);
      if (fullLib) {
        const target = String(call.keyValue);
        const row = fullLib.rows.find(r => r && String(r[call.keyColumn]) === target);
        if (row) continue; // already answerable from cache
      }
      // 0.37.97 — Group into per-library batches instead of
      // dispatching individually. batchedByLib is filled here;
      // the actual fetch call is dispatched below (once per group).
      if (!batchedByLib.has(call.libKey)) batchedByLib.set(call.libKey, []);
      batchedByLib.get(call.libKey).push(call);
    }
    // 0.37.97 — Fire one batch call per library. Each batch collapses
    // N /row round-trips to 1 /batch-lookup round-trip.
    if (typeof window !== 'undefined') {
      try {
        const q = new URLSearchParams(window.location.search || '');
        if (q.get('libdebug') === '1' || window.__DEBUG_LIBS === true) {
          const t = typeof performance !== 'undefined' && performance.now
            ? Math.round(performance.now()) : Date.now();
          // eslint-disable-next-line no-console
          console.log(`[libcache t=${t}ms] prewarm batching into ${batchedByLib.size} library group(s)`);
        }
      } catch (_e) { /* silent */ }
    }
    for (const [libKey, calls] of batchedByLib) {
      fetchLibraryRowsBatch(libKey, calls, pcrLanguage)
        .catch(() => { /* handled by cache module */ });
    }
    // Full library only for keys NOT covered by a point-lookup —
    // typically dynamic lib() calls or labels_of() references.
    for (const k of keys) {
      if (litKeysCovered.has(k)) continue;
      fetchLibrary(k, pcrLanguage).catch(() => { /* handled by cache module */ });
    }
  }, [structure, rules, pcrLanguage]);
  // 0.37.28 — "was changed" / "was not changed" rule operators need
  // to know each field's initial value so a diff can identify which
  // are currently modified. `initialValues` is recomputed on
  // structure change (admin editing defaults in the form editor
  // takes effect immediately); `changedFields` is a Set of names
  // whose current value differs from the initial. Empty (''),
  // null and undefined are treated as one class ("empty") so a
  // field with no default that's still empty doesn't show as
  // changed. Arrays (multiselect / checkbox) compare
  // element-by-element in order.
  const initialValues = useMemo(
    () => initialValuesFromStructure(structure),
    [structure],
  );
  // 0.37.48 — Baseline overrides for the `is_changed` /
  // `is_unchanged` diff. Updated by the `mark_unchanged` effect's
  // edge trigger below when a rule fires. Layered on top of the
  // structure-derived initialValues so a field the admin has
  // "accepted the change of" reads as unchanged from that point
  // on, until either another mark_unchanged fires or the citizen
  // edits the value to something new. Persistent state — not
  // reset on structure changes; stale entries for missing fields
  // are harmless because the changedFields diff loops by keys
  // that exist in `values` or `initialValues`.
  const [baselineOverrides, setBaselineOverrides] = useState({});
  const effectiveInitial = useMemo(
    () => ({ ...initialValues, ...baselineOverrides }),
    [initialValues, baselineOverrides],
  );
  const changedFields = useMemo(() => {
    const s = new Set();
    const keys = new Set([...Object.keys(values), ...Object.keys(effectiveInitial)]);
    for (const k of keys) {
      // 0.48.96 - A server copy is changed when the server says it
      // differs from the API's answer (<copy>_dirty).
      if (typeof values[`${k}_dirty`] === 'boolean') {
        if (values[`${k}_dirty`]) s.add(k);
        continue;
      }
      if (!_formValueEqual(values[k], effectiveInitial[k])) s.add(k);
    }
    return s;
  }, [values, effectiveInitial]);
  // 0.37.32 — Edge-triggered "button was just clicked" state. Set
  // to the button's element_id by the button click handler (via
  // ctx.signalClick), cleared to null by a post-render useEffect
  // one React tick later. Rules using the just_clicked operator
  // see the id during the render triggered by the click; on the
  // very next render (from the clear) the state is null again so
  // the rule does not re-fire. Same edge-trigger idea as rule
  // fire/unfire tracking, applied to click events.
  /* 0.42.51 - "when the page opens", momentary like a click.
   *
   * True for the first evaluation only: an effect turns it off straight
   * after. A condition that stayed true would make `call API` fire on
   * every evaluation, and each call causes another evaluation - the page
   * would call the far end forever. */
  const [pageJustLoaded, setPageJustLoaded] = useState(true);
  useEffect(() => {
    if (pageJustLoaded) setPageJustLoaded(false);
  }, [pageJustLoaded]);

  const [justClickedButton, setJustClickedButton] = useState(null);
  useEffect(() => {
    if (justClickedButton === null) return;
    // Schedule the clear as a microtask so the current render's
    // effects (including the edge-triggered set_value applier and
    // any set_label / set_action_value that were computed with
    // clickedButtonId set) have fully committed before we flip it
    // off.
    const id = window.setTimeout(() => setJustClickedButton(null), 0);
    return () => window.clearTimeout(id);
  }, [justClickedButton]);
  // 0.37.43 — Hoisted from its old spot ~380 lines below to sit
  // ABOVE the ruleState useMemo. Ship 0.37.40 added librariesData
  // as a dependency of ruleState (so labels_of() can resolve
  // library-backed dropdown options), but the state declaration
  // stayed near the useEffect that mutates it. React hooks
  // execute in the order they're written — reading librariesData
  // from a useMemo above its declaration left it in the temporal
  // dead zone. Public form crashed on load with "Cannot access
  // 'b' before initialization". The setLibrariesData effect
  // (line ~2078 originally) still lives at its old spot; only
  // the state declaration moved.
  // 0.44.72 - Kept as sent; date columns are formatted here, on every
  // change of format, page language or month names (they were formatted
  // once on arrival, so names that loaded later never showed).
  const [librariesRaw, setLibrariesData] = useState({});
  const fmtVersion = useFormatVersion();
  const librariesData = useMemo(() => {
    // 0.47.51 - Until the rows arrive, the copy kept from the last visit
    // (lib/libraryRowsCache.js), so a short library-backed list is
    // drawn as radio buttons at once instead of a dropdown first.
    const raw = { ...(librariesRaw || {}) };
    if (Array.isArray(structure)) {
      walkInputs(structure, (el) => {
        if (el && (el.type === 'dropdown' || el.type === 'multiselect')
            && el.source === 'library' && el.library && !raw[el.library]) {
          const kept = readLibraryRows(el.library, pcrLanguage);
          if (kept) raw[el.library] = kept;
        }
      });
    }
    const out = {};
    for (const [k, v] of Object.entries(raw)) {
      out[k] = v && v.column_types ? { ...v, rows: localizeRows(v.rows || [], v.column_types) } : v;
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [librariesRaw, fmtVersion, structure, pcrLanguage]);
  // 0.41.17 - Result of each connection's most recent call, keyed by
  // the connection's reference name:
  //     { tm_similarity: { status, failure, response } }
  // status is 'pending' | 'ok' | 'failed'. Rules read it through the
  // api_pending / api_ok / api_failed conditions.
  // 0.48.86 - Starting with the answers kept for the visit.
  const [ownApiState, setOwnApiState] = useState(() => loadVisitApi());
  /* 0.42.62 - A flow holds the API state so a response fetched at one
     step is still readable at a later one. */
  const apiControlled = externalApiState !== null && typeof onApiStateChange === 'function';
  const apiState = apiControlled ? externalApiState : ownApiState;
  const setApiState = apiControlled ? ((updater) => onApiStateChange(updater)) : setOwnApiState;
  // Connections with a call in flight. A ref, not state: it must be
  // read and written synchronously inside the dispatch effect, and a
  // state update would arrive too late to stop a second dispatch in
  // the same tick.
  const apiInFlight = useRef(new Set());
  // 0.48.51 - The signed-in user's values for rule expressions:
  // =user_ssn, =user_email, =user_first_name, =user_last_name, =user_id.
  // For showing only: an API request reads them on the server
  // ({user:ssn}), never from the page. A page element with the same
  // Element ID wins.
  const pcrUser = useOptionalUser();
  /* 0.48.86 - Answers kept for the visit belong to the person they were
     kept for. Once the session is known, a page that started with
     someone else's (a session that ended without Log out) drops them. */
  const authLoading = useAuthLoading();
  useEffect(() => {
    if (authLoading) return;
    const me = pcrUser ? String(pcrUser.id) : null;
    if (visitOwner() === me) return;
    setApiState(prev => {
      const kept = Object.keys(prev).filter(k => prev[k] && prev[k].kept);
      if (!kept.length) return prev;
      const next = { ...prev };
      for (const k of kept) delete next[k];
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, pcrUser && pcrUser.id]);
  const ruleValues = useMemo(() => {
    if (!pcrUser) return values;
    return {
      user_id: pcrUser.public_id || '', user_ssn: pcrUser.ssn || '',
      user_email: pcrUser.email || '', user_first_name: pcrUser.first_name || '',
      user_last_name: pcrUser.last_name || '',
      ...values,
    };
  }, [pcrUser, values]);
  const ruleState = useMemo(
    () => evaluateRules(safeRules, ruleValues, pcrLanguage, changedFields, justClickedButton, structure, librariesData, apiState, pageJustLoaded),
    // safeRules identity changes with the rules prop; values is the
    // main driver. structure isn't in the deps because changing it
    // already re-init's values via the effect above. libTick forces
    // re-evaluation after a lib() cache miss finishes fetching.
    // changedFields for is_changed / is_unchanged operators.
    // justClickedButton for the just_clicked operator (0.37.32).
    // structure passed to evaluateRules so labels_of() can look up
    // element options (0.37.38); referenced through closure so it
    // doesn't need to be in the deps.
    // 0.37.40 — librariesData IS in the deps because labels_of()
    // needs it to resolve library-backed dropdown options; when a
    // library fetch resolves and librariesData mutates, the rule
    // state should recompute so any labels_of() call reflects the
    // new data.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // 0.41.17 - apiState IS in the deps: without it, api_pending and
    // api_failed would never re-evaluate when a call's status changed,
    // so a spinner would never appear and a failure would never show.
    [rules, ruleValues, pcrLanguage, libTick, changedFields, justClickedButton, librariesData, apiState, pageJustLoaded]
  );

  // 0.41.17 - The dispatcher.
  //
  // evaluateRules is a pure useMemo and cannot make a request, so it
  // records what it wants in ruleState.apiCalls. This effect performs
  // those calls and feeds results back as apiState, which re-runs the
  // evaluation - the same loop librariesData already uses.
  //
  // The in-flight guard matters: a citizen pressing a button twice
  // during a four-second call would otherwise cost two third-party
  // queries and leave two responses racing to land last.
  /* 0.42.59 - Elements that ask for their own data when the page opens.
   *
   * An API-backed dropdown could only show what a rule had already
   * fetched, so every such dropdown needed a rule of its own. Ticking
   * "Ask when the page opens" on the element puts its connection into
   * the SAME queue the dispatcher below drains - one fetching path, not
   * two, which is what the 0.41.78 note asked for once a page-load
   * trigger existed.
   *
   * Once per page: a connection that already has any state - pending,
   * answered or failed - is not asked again, so the effect settling
   * cannot start it a second time. */
  /* 0.42.85 - A rule asked to move through the flow.
   *
   * Carried out here, not in the engine, which stays free of side
   * effects. Only in a flow: on a page by itself there is nowhere to
   * go, and the effect does nothing rather than erroring. */
  const flowNav = useFlow();
  // 0.43.90 - A rule set the flow's error state: the flow shows the
  // page set for that Error ID (outside a flow, nothing happens).
  useEffect(() => {
    const id = ruleState.flowError;
    if (!id || !flowNav || typeof flowNav.fail !== 'function') return;
    flowNav.fail(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ruleState.flowError]);

  useEffect(() => {
    const move = ruleState.flowMove;
    if (!move || !flowNav) return;
    // 0.43.98 - Start again: the flow clears its answers first.
    if (move === 'restart') {
      if (typeof flowNav.restart === 'function') flowNav.restart();
      else if (flowNav.steps.length) flowNav.move(0);
      return;
    }
    const to = move === 'first' ? 0
      : move === 'next' ? flowNav.currentIndex + 1 : flowNav.currentIndex - 1;
    if (to >= 0 && to < flowNav.steps.length) flowNav.move(to);
  }, [ruleState, flowNav]);

  const autoFetchSlugs = useMemo(() => {
    const out = [];
    const walk = (els) => {
      for (const el of (els || [])) {
        if (!el || typeof el !== 'object') continue;
        if (el.source === 'api' && el.fetch_on_load && (el.api_connection || '').trim()) {
          const slug = el.api_connection.trim();
          if (!out.includes(slug)) out.push(slug);
        }
        if (Array.isArray(el.children)) walk(el.children);
        if (Array.isArray(el.columns)) {
          for (const col of el.columns) if (col && Array.isArray(col.children)) walk(col.children);
        }
      }
    };
    walk(structure);
    return out;
  }, [structure]);

  /* 0.45.67 - A Dropdown or Multi-select default written as an option's
   * LABEL ("Beta") rather than its value ("b") selects that option.
   *
   * Defaults are picked from the options in the editor now, but older
   * ones were typed, and options from a library or an API are only
   * known here. Only a field still holding its default is touched, so
   * a citizen's choice is never rewritten. Library dropdowns that fetch
   * their own rows (server source) are matched by value only. */
  useEffect(() => {
    if (!Array.isArray(structure)) return;
    const fixes = [];
    walkInputs(structure, (el) => {
      if (el.type !== 'dropdown' && el.type !== 'multiselect') return;
      const name = getElementId(el);
      if (!name || el.default == null || el.default === '') return;
      if (_isServerSourceEligible(el, ruleState)) return;
      const multi = el.type === 'multiselect';
      const dflt = multi
        ? (Array.isArray(el.default) ? el.default.filter(v => v !== '' && v != null).map(String) : [])
        : String(el.default);
      const cur = values ? values[name] : undefined;
      const untouched = multi
        ? (Array.isArray(cur) && cur.length === dflt.length && cur.every((v, i) => String(v) === dflt[i]))
        : String(cur ?? '') === dflt;
      if (!untouched) return;
      const opts = _resolveElementOptions(el, { librariesData, values: values || {}, language: pcrLanguage });
      if (!Array.isArray(opts) || opts.length === 0) return;
      const byValue = new Set(opts.map(o => String(o.value)));
      const norm = (t) => String(t ?? '').trim().toLowerCase();
      const map = (v) => {
        if (byValue.has(v)) return v;
        const hit = opts.find(o => norm(o.label) === norm(v));
        return hit ? String(hit.value) : v;
      };
      if (multi) {
        const next = dflt.map(map);
        if (next.some((v, i) => v !== dflt[i])) fixes.push([name, next]);
      } else {
        const next = map(dflt);
        if (next !== dflt) fixes.push([name, next]);
      }
    });
    if (fixes.length) setValues(prev => {
      const out = { ...prev };
      for (const [n, v] of fixes) out[n] = v;
      return out;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [structure, librariesData, pcrLanguage, defaultsSeeded]);

  // 0.45.61 - Pages from the API: the first-page inputs every paged
  // element adds to a call of its connection (lib/apiPaging.js).
  const firstPageInputs = useMemo(() => firstPageInputsBySlug(structure), [structure]);

  // One call. `keep` (0.45.61, another page of the same search) keeps
  // the answer on show while the next page loads, so the table does
  // not vanish and jump back.
  const runApiCall = (call, keep = false) => {
    const slug = call.connection;
    if (apiInFlight.current.has(slug)) return;
    apiInFlight.current.add(slug);
    const inputs = keep
      ? { ...(call.inputs || {}) }
      : { ...(firstPageInputs[slug] || {}), ...(call.inputs || {}) };
    /* 0.46.00 - `base`: the inputs the RULE asked with, without the
       paging the paginator adds. A step opened again compares it to
       decide whether the answer it already has is the one it wants. */
    const ownBase = keep ? null : { ...(call.inputs || {}) };
    setApiState(prev => {
      const before = prev[slug];
      const pending = { status: 'pending', inputs,
        base: keep ? (before && before.base) : ownBase };
      if (keep && before && before.response !== undefined) {
        pending.response = before.response;
        pending.paging = true;
      }
      return { ...prev, [slug]: pending };
    });

    const body = new FormData();
    body.append('inputs', JSON.stringify(inputs));
    body.append('language', pcrLanguage || '');
    // 0.43.66 - `connection.request` calls that request; the state
    // stays keyed by the whole key, so api(), conditions and
    // set_from_api read the right request's answer.
    const [connSlug, reqSlug] = splitApiTarget(slug);
    if (reqSlug) body.append('request', reqSlug);
    // 0.48.84 - A fresh answer: the server skips its cache (and renews it).
    if (call.fresh) body.append('fresh', '1');

    fetch(`/api/api-connections/${encodeURIComponent(connSlug)}/call`, {
      method: 'POST', body, credentials: 'include',
    })
      .then(async (res) => {
        if (res.ok) return { status: 'ok', response: await res.json() };
        // The failure CLASS, not the far end's message - a raw error
        // can name internal hosts, and the citizen-facing wording
        // belongs in an Alert the admin writes and translates.
        /* 0.42.52 - The SERVER's class when it sends one.
         *
         * It alone knows the far end's status - every upstream
         * failure arrives here as our 502 - and it applies the
         * connection's own named classes. Reading it is wrapped:
         * a refusal that carries no JSON (a proxy's error page, a
         * dropped connection) must still classify. */
        let named = '';
        try {
          const b = await res.json();
          const d = b && b.detail;
          if (d && typeof d === 'object' && typeof d.failure === 'string') {
            named = d.failure;
          }
        } catch { /* no JSON body: fall back to the status below */ }
        const failure = named
          || (res.status === 429 ? 'rate_limited'
            : res.status === 413 ? 'too_large'
            : res.status === 503 ? 'misconfigured'
            : res.status === 502 ? 'unavailable'
            : res.status === 400 ? 'bad_request'
            : 'unavailable');
        return { status: 'failed', failure };
      })
      .catch(() => ({ status: 'failed', failure: 'network' }))
      .then((result) => {
        apiInFlight.current.delete(slug);
        // 0.48.86 - Kept for the visit: asked so, or renewing an answer
        // kept earlier. Another page of a paged answer is not kept.
        if (result.status === 'ok' && !keep && (call.keep_visit || isKeptForVisit(slug))) {
          keepForVisit(slug, result.response, inputs, ownBase);
        }
        setApiState(prev => ({ ...prev, [slug]: { ...result, inputs,
          base: keep ? (prev[slug] && prev[slug].base) : ownBase } }));
      });
  };
  // 0.45.61 - The paginator of an API-paged element asks for a page.
  const callApiPage = (slug, inputs) => runApiCall({ connection: slug, inputs }, true);

  useEffect(() => {
    const auto = autoFetchSlugs
      .filter(slug => !apiState[slug] && !apiInFlight.current.has(slug))
      .map(slug => ({ connection: slug, inputs: {} }));
    /* 0.46.00 - A step opened again (Back in a flow) keeps the answer
       it has. A call made as the page opens is skipped when the same
       connection already answered the same inputs, so the citizen
       returns to the results they left, on the page they were on,
       without the API being asked again. Inputs that differ (a value
       changed on an earlier step) still call, as since 0.45.80. */
    const sameInputs = (a, b) => JSON.stringify(sortKeys(a || {})) === JSON.stringify(sortKeys(b || {}));
    const fromRules = (ruleState.apiCalls || []).filter(call => {
      if (!pageJustLoaded || call.fresh) return true;
      const had = apiState[call.connection];
      return !(had && had.status === 'ok' && had.base && sameInputs(had.base, call.inputs));
    });
    /* 0.48.00 - A call reading a field whose value has characters the
       field does not allow is not made: the field shows its error
       instead, and the API never receives the value. */
    const idx = numberFieldIndex(structure);
    const blocked = [];
    const allowed = fromRules.filter(call => {
      const bad = (call.fields || []).map(f => idx.get(f)).filter(el => el && patternError(el, values[getElementId(el)]));
      if (bad.length) { blocked.push(...bad); return false; }
      return true;
    });
    if (blocked.length) {
      setSticky(prev => blocked.reduce((s, el) => flagOneRangeError(s, el, values[getElementId(el)], values), prev));
    }
    const wanted = [...allowed, ...auto];
    if (wanted.length === 0) return;
    for (const call of wanted) runApiCall(call);
    // ruleState is the trigger; apiCalls is only non-empty on the
    // evaluation where a trigger fired. autoFetchSlugs adds the
    // elements that ask for themselves (0.42.59).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ruleState, autoFetchSlugs]);

  /* 0.48.29 - "create account".
   *
   * The engine records the wish in ruleState.accountCreate; the request
   * is made here. Only the page, the rule, the effect's place and the
   * values of the fields it names are sent: the server reads the rest
   * from the saved page, so the API, the comparison and the approval
   * cannot be changed from the browser. Nothing the API answered comes
   * back, only the outcome, which rules read as "account was created",
   * "account was not created with not_matched" and so on.
   *
   * A click sends every time (the server limits attempts). A rule fired
   * by a condition sends once per set of values. */
  /* 0.48.30 - A Password field that checks the password policy needs
     the policy: fetched once, and the page drawn again when it lands. */
  const [, setPolicyTick] = useState(0);
  const needsPolicy = useMemo(() => JSON.stringify(structure || []).includes('"password_policy":true'), [structure]);
  useEffect(() => {
    if (!needsPolicy) return undefined;
    const off = subscribePasswordPolicy(() => setPolicyTick(n => n + 1));
    loadPasswordPolicy();
    return off;
  }, [needsPolicy]);
  const hasCreateAccount = useMemo(() => (Array.isArray(rules) ? rules : [])
    .some(r => r && Array.isArray(r.then) && r.then.some(e => e && e.effect === 'create_account')), [rules]);
  const signupGuard = useSignupGuard(hasCreateAccount);
  const accountInFlight = useRef(false);
  const accountLastKey = useRef('');
  /* 0.48.38 - The create account that sent an email code: Confirm and
     Send again use it, so the code goes back to the same rule (and its
     approval setting). */
  const accountCodeFor = useRef(null);

  // The values of the fields a create account effect reads, as text.
  const accountValues = (eff, codeField) => {
    const names = new Set([eff.email_field, eff.password_field, eff.confirm_field, codeField,
      ...((eff.inputs || []).filter(r => r && r.source !== 'literal').map(r => r.field)),
      ...((eff.match || []).map(m => m && m.field)),
      // 0.48.52 - update account's page fields.
      ...Object.values(eff.set && typeof eff.set === 'object' ? eff.set : {})
        .filter(sp => sp && sp.source === 'field').map(sp => sp.field)].filter(Boolean));
    const sent = {};
    for (const n of names) {
      const v = values ? values[n] : undefined;
      sent[n] = v == null ? '' : (Array.isArray(v) ? v.join(',') : String(v));
    }
    return sent;
  };

  /* One request to the server's create account. `code` (0.48.38) is the
     emailed code for the second step, '' to ask for a (new) code. */
  const sendAccount = (want, sent, code) => {
    accountInFlight.current = true;
    const codeStage = code !== undefined;
    // 0.48.52 - update account: its own endpoint and result
    // (__account_update), no bot check (the user is signed in).
    const isUpdate = !!(want.effect && want.effect.effect === 'update_account');
    if (isUpdate) {
      setApiState(prev => ({ ...prev, __account_update: { status: 'pending', code: codeStage } }));
    } else {
      setApiState(prev => ({ ...prev, __account: { status: 'pending', code: codeStage },
        __account_expected: { status: 'failed', failure: 'none' } }));
    }
    fetch(isUpdate ? '/api/auth/form-account-update' : '/api/auth/form-signup', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ page: pageSlug || '', lang: pcrLanguage || '', rule_id: want.ruleId,
                             effect: want.index, values: sent,
                             ...(code ? { code } : {}), ...(isUpdate ? {} : signupGuard.extra()) }),
    })
      .then(async (res) => {
        let b = null;
        try { b = await res.json(); } catch { /* no JSON body */ }
        if (res.ok) {
          // 0.48.38 - A code was emailed: nothing is created yet.
          if (b && b.status === 'code_sent') {
            accountCodeFor.current = want;
            return { status: 'code', code: true };
          }
          accountCodeFor.current = null;
          if (isUpdate) return { status: 'ok', changed: (b && b.changed) || [] };
          return { status: 'ok', pending: !!(b && b.status === 'pending') };
        }
        const d = b && b.detail;
        const failure = (d && typeof d === 'object' && typeof d.failure === 'string') ? d.failure
          : (res.status === 429 ? 'too_many' : 'failed');
        // 0.48.34 - Test mode: what the API holds at the match paths.
        const expected = d && typeof d === 'object' && d.expected && typeof d.expected === 'object'
          && Object.keys(d.expected).length ? d.expected : null;
        // 0.48.38 - A failure while a code is pending keeps the code step
        // on screen (a wrong code, too soon to resend).
        return { status: 'failed', failure, expected, code: codeStage && !!accountCodeFor.current };
      })
      .catch(() => ({ status: 'failed', failure: 'failed', code: codeStage && !!accountCodeFor.current }))
      .then((result) => {
        accountInFlight.current = false;
        if (isUpdate) {
          const { expected: _x, ...done } = result;
          setApiState(prev => ({ ...prev, __account_update: done }));
          return;
        }
        // A new token for the next try: the old one may be refused now.
        if (result.status === 'failed') signupGuard.refresh();
        /* 0.48.34 - Test mode's values, readable as an API answer:
           the condition "API __account_expected succeeded" and
           api("__account_expected", "result.email"). Failed otherwise,
           so a rule can tell "details did not match, here is the right
           value" from "did not match" with nothing to show. */
        const { expected, ...outcome } = result;
        setApiState(prev => ({ ...prev, __account: outcome,
          __account_expected: expected
            ? { status: 'ok', response: expected }
            : { status: 'failed', failure: 'none' } }));
      });
  };

  useEffect(() => {
    if (accountInFlight.current) return;
    // 0.48.38 - Confirm the code, or send a new one, for the create
    // account that sent it.
    const confirm = ruleState.accountConfirm;
    const codeFor = accountCodeFor.current;
    if (confirm && codeFor && confirm.byClick) {
      const sent = accountValues(codeFor.effect || {}, confirm.codeField);
      const code = String(sent[confirm.codeField] || '').replace(/\s/g, '');
      if (code) sendAccount(codeFor, sent, code);
      return;
    }
    if (ruleState.accountResend && codeFor) {
      sendAccount(codeFor, accountValues(codeFor.effect || {}), '');
      return;
    }
    const want = ruleState.accountCreate || ruleState.accountUpdate;
    if (!want) return;
    const sent = accountValues(want.effect || {});
    const key = JSON.stringify([want.ruleId, want.index, sent]);
    if (!want.byClick && key === accountLastKey.current) return;
    accountLastKey.current = key;
    sendAccount(want, sent, undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ruleState]);

  // 0.37.36 → 0.37.37 — Sticky reactive-effect state for rules
  // triggered by `just_clicked`. Reactive effects normally apply
  // only while their rule is firing — combined with the one-render
  // lifetime of just_clicked, they'd flash on and off in a single
  // frame with no visible change. Sticky state persists across
  // renders, updated by the edge-triggered useEffect further down
  // when a click-triggered rule fires. The rule's transient
  // ruleState overlays sticky at render time — a condition-based
  // rule that also targets the same element wins while its
  // condition is true, and sticky remains as the fallback for the
  // rest of the time.
  //
  // 0.37.36 shipped labels only. 0.37.37 extends to every
  // reactive effect the platform supports: hide/show,
  // set_required, lock, set_action_value, set_sort_order,
  // add_option. set_value / clear_value stay outside sticky —
  // they're already edge-triggered (write to the values map,
  // which IS persistent).
  //
  // One state object holds all seven layers so setSticky calls
  // and the merge memo below stay in one place. Merge semantics:
  //   - labels / required / actionValues / sortOrder:
  //       reactive wins where present; sticky fills in.
  //   - hidden / locked: union — either layer hides / locks.
  //   - addedOptions: array append, dedup by value.
  //
  // Clear:
  //   - Another click-triggered rule with the reverse effect
  //     (show clears sticky.hidden; set_label to '' clears
  //     sticky.labels; set_action_value to '' clears; there's
  //     no unlock effect so lock is one-way; add_option can't
  //     be individually removed today).
  //   - Page reload resets everything.
  const [sticky, setSticky] = useState(() => ({
    labels:       new Map(),
    // 0.37.79 — Sticky mirror of ruleState.texts (fed by
    // set_text). Wired into the same click-triggered sticky
    // pipeline as labels/etc so set_text on a title behaves
    // symmetrically with set_label on an input — reactive
    // triggers auto-revert; click triggers stick past the
    // one-render just_clicked window. Was missing from the
    // 0.37.76 ship that introduced set_text.
    texts:        new Map(),
    // 0.40.00 - Sticky mirror of set_variant so a click-triggered
    // alert state change persists past the just_clicked window,
    // exactly like texts above.
    variants:     new Map(),
    // 0.37.85 — Sticky mirror of ruleState.tableOverlays (fed
    // by set_cell / add_row / remove_row). Wired into the same
    // click-triggered sticky pipeline as labels/texts so a
    // click that adds a row (or overrides a cell) doesn't
    // flash-and-revert. Structure mirrors ruleState.tableOverlays:
    // Map<element_id, {cellOverrides, rowsAdded, rowsRemoved}>.
    tableOverlays: new Map(),
    hidden:       new Set(),
    required:     new Map(),
    locked:       new Set(),
    actionValues: new Map(),
    sortOrder:    new Map(),
    addedOptions: new Map(),
    // 0.37.47 — Sticky mirrors of the rule-set error state so a
    // click-triggered set_error_message / show_error_message
    // effect persists past the one-render just_clicked window.
    errors:       new Map(),
    showError:    new Set(),
  }));
  const effectiveRuleState = useMemo(() => {
    if (!ruleState) return ruleState;
    const anySticky = sticky.labels.size + sticky.texts.size
      + sticky.variants.size
      + sticky.tableOverlays.size
      + sticky.hidden.size
      + sticky.required.size + sticky.locked.size
      + sticky.actionValues.size + sticky.sortOrder.size
      + sticky.addedOptions.size
      + sticky.errors.size + sticky.showError.size > 0;
    if (!anySticky) return ruleState;
    const merged = { ...ruleState };
    // Reactive-wins layers (single value per key).
    const overlayMap = (base, stick) => {
      if (stick.size === 0) return base;
      const out = new Map(base || new Map());
      for (const [k, v] of stick) if (!out.has(k)) out.set(k, v);
      return out;
    };
    merged.labels       = overlayMap(ruleState.labels,       sticky.labels);
    // 0.37.79 — Same reactive-wins overlay for set_text
    // overrides. A currently-firing reactive rule beats a
    // sticky click-set text on the same title; otherwise the
    // sticky text shows.
    merged.texts        = overlayMap(ruleState.texts,        sticky.texts);
    // 0.40.00 - Same reactive-wins overlay for alert variants.
    merged.variants     = overlayMap(ruleState.variants,     sticky.variants);
    // 0.37.85 — Table overlays merged with deep union semantics
    // rather than reactive-wins-per-key. Each sub-map (cellOverrides,
    // rowsAdded, rowsRemoved) is unioned so that a currently-firing
    // rule's cell override AND a sticky click-added row can
    // co-exist on the same table without stomping each other.
    if (sticky.tableOverlays.size > 0) {
      const mergedTables = new Map(ruleState.tableOverlays || new Map());
      for (const [tableId, stickyOverlay] of sticky.tableOverlays) {
        const reactive = mergedTables.get(tableId);
        if (!reactive) {
          mergedTables.set(tableId, stickyOverlay);
          continue;
        }
        // Merge: reactive wins on any cell key it already sets;
        // sticky's cell keys fill in the rest. Rows added by
        // both layers are concatenated. Removed row indices
        // union.
        const nextCells = new Map(reactive.cellOverrides);
        for (const [k, v] of stickyOverlay.cellOverrides) {
          if (!nextCells.has(k)) nextCells.set(k, v);
        }
        const nextRowsAdded = [
          ...(Array.isArray(reactive.rowsAdded) ? reactive.rowsAdded : []),
          ...(Array.isArray(stickyOverlay.rowsAdded) ? stickyOverlay.rowsAdded : []),
        ];
        const nextRowsRemoved = new Set(reactive.rowsRemoved);
        for (const r of stickyOverlay.rowsRemoved) nextRowsRemoved.add(r);
        mergedTables.set(tableId, {
          cellOverrides: nextCells,
          rowsAdded:     nextRowsAdded,
          rowsRemoved:   nextRowsRemoved,
        });
      }
      merged.tableOverlays = mergedTables;
    }
    merged.required     = overlayMap(ruleState.required,     sticky.required);
    merged.actionValues = overlayMap(ruleState.actionValues, sticky.actionValues);
    merged.sortOrder    = overlayMap(ruleState.sortOrder,    sticky.sortOrder);
    // 0.37.47 — Error text is reactive-wins: a currently-firing
    // rule's set_error_message beats a sticky one from a click.
    merged.errors       = overlayMap(ruleState.errors,       sticky.errors);
    // Union layers (a hide from either layer = hidden).
    if (sticky.hidden.size > 0) {
      merged.hidden = new Set(ruleState.hidden);
      for (const e of sticky.hidden) merged.hidden.add(e);
    }
    if (sticky.locked.size > 0) {
      merged.locked = new Set(ruleState.locked);
      for (const e of sticky.locked) merged.locked.add(e);
    }
    // 0.37.47 — showError is a union: either layer says "show" wins.
    if (sticky.showError.size > 0) {
      merged.showError = new Set(ruleState.showError);
      for (const e of sticky.showError) merged.showError.add(e);
    }
    // Append layer for added options — dedup by value so the same
    // option doesn't appear twice if two rules add it.
    if (sticky.addedOptions.size > 0) {
      const nextAO = new Map(ruleState.addedOptions || new Map());
      for (const [k, arr] of sticky.addedOptions) {
        const existing = nextAO.get(k) || [];
        const seen = new Set(existing.map(o => o.value));
        const combined = [...existing];
        for (const opt of arr) {
          if (!seen.has(opt.value)) {
            combined.push(opt);
            seen.add(opt.value);
          }
        }
        nextAO.set(k, combined);
      }
      merged.addedOptions = nextAO;
    }
    return merged;
  }, [ruleState, sticky]);

  // 0.36.106 — Edge-triggered set_value / clear_value. Mirrors the
  // AdminFormPreview pattern: track which rules were fired last
  // render; when a rule transitions false → true, apply its `then`
  // set_value/clear_value effects once (so the citizen sees the
  // value pre-fill / clear). When it transitions true → false, apply
  // the matching `else` effects (the rule "un-fires"). show / hide /
  // set_required / lock are reactive (recomputed every render via
  // ruleState) and don't need edge handling.
  // 0.36.109 — Track BOTH branches' active sets across renders. The
  // edge-triggered set_value/clear_value effects apply when a branch
  // transitions inactive → active. Tracking only `rulesFired` (when-
  // matches) was sufficient when `else` fired automatically on the
  // inverse, but with `else_when` the else branch can be inactive
  // even though the when is false (because else_when doesn't match).
  const prevFiredRulesRef = useRef(new Set());
  const prevElseFiredRulesRef = useRef(new Set());
  useEffect(() => {
    if (safeRules.length === 0) {
      prevFiredRulesRef.current = new Set();
      prevElseFiredRulesRef.current = new Set();
      return;
    }
    const prevThen = prevFiredRulesRef.current;
    const currThen = ruleState.rulesFired;
    const prevElse = prevElseFiredRulesRef.current;
    const currElse = ruleState.rulesElseFired || new Set();

    const newlyFiredThen = [];
    const newlyFiredElse = [];
    for (const id of currThen) if (!prevThen.has(id)) newlyFiredThen.push(id);
    for (const id of currElse) if (!prevElse.has(id)) newlyFiredElse.push(id);

    if (newlyFiredThen.length === 0 && newlyFiredElse.length === 0) {
      prevFiredRulesRef.current = new Set(currThen);
      prevElseFiredRulesRef.current = new Set(currElse);
      return;
    }
    setValues(prevValues => {
      const next = { ...prevValues };
      let changed = false;
      const applyEffects = (effectsList) => {
        if (!Array.isArray(effectsList)) return;
        for (const eff of effectsList) {
          if (!eff || !Array.isArray(eff.fields)) continue;
          // 0.41.33 - set_from_api writes a value like set_value does.
          //
          // This edge-triggered loop is what actually moves a forced
          // value into the form state; the effect only records it in
          // ruleState.forcedValues. Without this branch the value was
          // computed correctly and never displayed.
          if (eff.effect === 'set_from_api') {
            for (const f of eff.fields) {
              if (!ruleState.forcedValues.has(f)) continue;
              // 0.48.87 - A Cascade's part is written inside its value.
              if (assignFieldValue(next, structure, f, ruleState.forcedValues.get(f))) changed = true;
            }
          } else if (eff.effect === 'set_value') {
            for (const f of eff.fields) {
              // 0.37.24 — Read the resolved value from ruleState
              // instead of `eff.value` directly. For plain-literal
              // set_values these are the same; for expression
              // set_values (=field1+field2) ruleState has the
              // evaluated number while eff.value is still the raw
              // expression source string. Skip fields in
              // expressionFields — the continuous useEffect below
              // handles them so they can recompute on every input
              // change (formula semantics).
              if (ruleState.expressionFields && ruleState.expressionFields.has(f)) continue;
              // 0.43.74 - A formula with no value yet (an api() whose
              // call is still running, a lib() still loading) sets
              // nothing: the field keeps what it had. It used to fall
              // back to the rule's value AS WRITTEN, so citizens saw
              // "=api(...)" until the answer arrived.
              if (!ruleState.forcedValues.has(f) && isExpression(eff.value)) continue;
              const v = ruleState.forcedValues.has(f) ? ruleState.forcedValues.get(f) : eff.value;
              if (cascadePartOf(structure, f)) { if (assignFieldValue(next, structure, f, v)) changed = true; continue; }
              next[f] = v;
              changed = true;
            }
          } else if (eff.effect === 'restore_value') {
            /* 0.48.96 - Back to the value it had when last unchanged:
               its default, or what mark unchanged kept (Discard). */
            for (const f of eff.fields) {
              const v = effectiveInitial[f];
              next[f] = v === undefined ? '' : v;
              changed = true;
            }
          } else if (eff.effect === 'clear_value') {
            for (const f of eff.fields) {
              const el = findElementByName(structure, f);
              if (el && (el.type === 'checkbox' || el.type === 'multiselect')) {
                next[f] = [];
              } else {
                next[f] = '';
              }
              changed = true;
            }
          }
        }
      };
      for (const rule of safeRules) {
        if (!rule || rule.enabled === false) continue;
        if (newlyFiredThen.includes(rule.id)) applyEffects(rule.then);
        if (newlyFiredElse.includes(rule.id)) applyEffects(rule.else);
      }
      // 0.37.48 — mark_unchanged edge trigger. Runs AFTER
      // set_value / clear_value in the same fire transition so
      // that a rule combining set_value + mark_unchanged on the
      // same field snapshots the POST-set_value value into the
      // baseline (matches the intent: "compute this value, then
      // treat it as unchanged so is_changed reads false"). Reads
      // ruleState.markedUnchanged (populated by the evaluator's
      // effect case) intersected with only the newly-fired rules'
      // targets — the cumulative set from earlier-firing rules
      // was already applied on their fire transition. Uses `next`
      // (the fresh values map) so post-set_value snapshots are
      // captured correctly.
      const markedFromNewlyFired = new Set();
      for (const rule of safeRules) {
        if (!rule || rule.enabled === false) continue;
        const collect = (list) => {
          if (!Array.isArray(list)) return;
          for (const eff of list) {
            if (eff && eff.effect === 'mark_unchanged' && Array.isArray(eff.fields)) {
              for (const f of eff.fields) {
                if (typeof f === 'string' && f !== '') markedFromNewlyFired.add(f);
              }
            }
          }
        };
        if (newlyFiredThen.includes(rule.id)) collect(rule.then);
        if (newlyFiredElse.includes(rule.id)) collect(rule.else);
      }
      if (markedFromNewlyFired.size > 0) {
        const snapshot = {};
        for (const f of markedFromNewlyFired) snapshot[f] = next[f];
        setBaselineOverrides(prev => ({ ...prev, ...snapshot }));
      }
      return changed ? next : prevValues;
    });
    // 0.38.84 - scroll_to edge trigger. When a newly-fired rule
    // carries a scroll_to effect, scroll the page once. Runs after
    // the setValues above so any set_value/set_text in the same rule
    // has been queued; the actual DOM scroll is deferred to the next
    // frame via requestAnimationFrame so the target (e.g. the info
    // card whose title was just set, or a field whose error just
    // rendered) exists and is laid out before we scroll to it.
    // first_error scans the live DOM, so it reflects real validation
    // state at scroll time regardless of what set the errors.
    {
      const scrolls = [];
      for (const rule of safeRules) {
        if (!rule || rule.enabled === false) continue;
        const collect = (list) => {
          if (!Array.isArray(list)) return;
          for (const eff of list) {
            if (eff && eff.effect === 'scroll_to') {
              const kind = eff.scroll_kind || 'first_error';
              const elId = (Array.isArray(eff.fields) && eff.fields[0]) || '';
              scrolls.push({ kind, elId });
            }
          }
        };
        if (newlyFiredThen.includes(rule.id)) collect(rule.then);
        if (newlyFiredElse.includes(rule.id)) collect(rule.else);
      }
      if (scrolls.length > 0) {
        // Only the last scroll wins if a rule (or rules) queued more
        // than one in the same transition - scrolling to several
        // places at once is meaningless.
        const s = scrolls[scrolls.length - 1];
        requestAnimationFrame(() => {
          requestAnimationFrame(() => scrollToTarget(s.kind, s.elId));
        });
      }
    }
    // 0.37.36 → 0.37.37 — Sticky reactive effects for click-
    // triggered rules. Extended from labels-only to all reactive
    // effect types the platform supports. See the sticky useState
    // above for the merge semantics; applyStickyEffects here is
    // the writer.
    const applyStickyEffects = (effectsList, target) => {
      if (!Array.isArray(effectsList)) return false;
      let mutated = false;
      // 0.38.88 - show_required_errors: fieldless sticky effect. See
      // AdminFormPreview for the full rationale. Marks empty required
      // visible fields as sticky-errored so they persist past the
      // just_clicked edge; a cleanup effect clears each once filled.
      for (const eff of effectsList) {
        if (eff && eff.effect === 'show_required_errors') {
          const _isEmpty = (v) => (
            v === undefined || v === null || v === ''
            || (Array.isArray(v) && v.length === 0)
          );
          const reqState = (ruleState && ruleState.required) || new Map();
          const hiddenState = (ruleState && ruleState.hidden) || new Set();
          // 0.48.63 - Not the fields of a Modal (drafts, checked by its OK).
          walkInputs(structure, (el) => {
            const eid = getElementId(el);
            if (!eid) return;
            let isRequired = !!el.required;
            if (reqState.has(eid)) isRequired = !!reqState.get(eid);
            if (!isRequired) return;
            if (!!el.hidden || hiddenState.has(eid)) return;
            if (!_isEmpty(values[eid])) return;
            if (!target.errors.has(eid)) {
              const msg = (el.error_required && String(el.error_required))
                || 'This field is required';
              target.errors.set(eid, msg);
            }
            if (!target.showError.has(eid)) target.showError.add(eid);
            mutated = true;
          }, true);
          // 0.47.73 - and every visible Number field outside its Min / Max.
          if (flagRangeErrors(structure, values, ruleState, target)) mutated = true;
        }
      }
      for (const eff of effectsList) {
        if (!eff || !Array.isArray(eff.fields)) continue;
        for (const f of eff.fields) {
          if (typeof f !== 'string' || !f) continue;
          switch (eff.effect) {
            case 'set_label': {
              const label = String(eff.label ?? '');
              if (label === '') { if (target.labels.delete(f)) mutated = true; }
              else { target.labels.set(f, label); mutated = true; }
              break;
            }
            case 'set_text': {
              // 0.37.79 — Sticky mirror of set_text, parallel to
              // set_label above. Empty string clears the sticky
              // entry (so admin can "undo" a click-set text by
              // wiring a second click rule that writes empty
              // text — falls back to the element's static text).
              const text = String(eff.text ?? '');
              if (text === '') { if (target.texts.delete(f)) mutated = true; }
              else { target.texts.set(f, text); mutated = true; }
              break;
            }
            case 'set_variant': {
              // 0.40.00 — Sticky mirror of set_variant, parallel to
              // set_text above. Empty string clears the sticky entry
              // so a second click rule can hand the alert back to its
              // configured variant.
              const variant = String(eff.variant ?? '');
              if (variant === '') { if (target.variants.delete(f)) mutated = true; }
              else { target.variants.set(f, variant); mutated = true; }
              break;
            }
            case 'set_cell': {
              // 0.37.85 — Sticky mirror of the set_cell effect.
              // `f` is the target table's element_id. Row/column
              // 1-based; skip on invalid input.
              // 0.37.88 — row=0 addresses the header row (mapped
              // to e.headers[col-1] at render time). Convention
              // documented in the evaluator's case 'set_cell'.
              const rowN = Number(eff.row);
              const colN = Number(eff.column);
              if (!Number.isFinite(rowN) || rowN < 0
                || !Number.isFinite(colN) || colN < 1) break;
              const overlay = ensureStickyTableOverlay(target, f);
              const key = `r${rowN}c${colN}`;
              const value = String(eff.value ?? '');
              const existing = overlay.cellOverrides.get(key);
              if (existing !== value) {
                overlay.cellOverrides.set(key, value);
                mutated = true;
              }
              break;
            }
            case 'add_row': {
              // 0.37.85 — Sticky mirror of add_row. Appends a
              // row to the target table's sticky overlay. Same
              // shape coercion as the evaluator's case: array
              // stays as-is, string wraps, null → [].
              const values = Array.isArray(eff.values)
                ? eff.values.map(v => String(v ?? ''))
                : (eff.values == null ? [] : [String(eff.values)]);
              const overlay = ensureStickyTableOverlay(target, f);
              overlay.rowsAdded.push(values);
              mutated = true;
              break;
            }
            case 'remove_row': {
              // 0.37.85 — Sticky mirror of remove_row. Adds the
              // 1-based row index to the target table's
              // sticky remove-set.
              const rowN = Number(eff.row);
              if (!Number.isFinite(rowN) || rowN < 1) break;
              const overlay = ensureStickyTableOverlay(target, f);
              if (!overlay.rowsRemoved.has(rowN)) {
                overlay.rowsRemoved.add(rowN);
                mutated = true;
              }
              break;
            }
            case 'hide':
              if (!target.hidden.has(f)) { target.hidden.add(f); mutated = true; }
              break;
            case 'show':
              // Symmetric with the reactive `show` effect: removes
              // from sticky hidden. Cannot un-hide an element that's
              // hidden by its own config (matches current reactive
              // semantics — `show` only overrides other rules).
              if (target.hidden.delete(f)) mutated = true;
              break;
            case 'set_required': {
              // effect.required is boolean per the schema. Coerce
              // defensively in case admin-authored JSON is loose.
              const req = !!eff.required;
              const prev = target.required.get(f);
              if (prev !== req) { target.required.set(f, req); mutated = true; }
              break;
            }
            case 'lock':
              if (!target.locked.has(f)) { target.locked.add(f); mutated = true; }
              break;
            case 'set_action_value': {
              const val = String(eff.value ?? '');
              if (val === '') { if (target.actionValues.delete(f)) mutated = true; }
              else { target.actionValues.set(f, val); mutated = true; }
              break;
            }
            case 'set_sort_order': {
              const order = (eff.order === 'asc' || eff.order === 'desc' || eff.order === 'none')
                ? eff.order : null;
              if (order) {
                const prev = target.sortOrder.get(f);
                if (prev !== order) { target.sortOrder.set(f, order); mutated = true; }
              }
              break;
            }
            case 'add_option': {
              // Same guards as the reactive evaluator: skip
              // ghost-option entries (missing value or label).
              if (eff.value === undefined || eff.value === null || eff.value === '') break;
              if (eff.label === undefined || eff.label === null || eff.label === '') break;
              const list = target.addedOptions.get(f) || [];
              const value = String(eff.value);
              if (!list.some(o => o.value === value)) {
                list.push({ value, label: String(eff.label) });
                target.addedOptions.set(f, list);
                mutated = true;
              }
              break;
            }
            case 'set_error_message': {
              // 0.37.47 — Sticky mirror of the reactive
              // set_error_message case. Empty message clears
              // both the text and the show flag.
              const msg = eff.message === undefined || eff.message === null
                ? '' : String(eff.message);
              if (msg === '') {
                if (target.errors.delete(f)) mutated = true;
                if (target.showError.delete(f)) mutated = true;
              } else {
                target.errors.set(f, msg);
                target.showError.add(f);
                mutated = true;
              }
              break;
            }
            case 'show_error_message': {
              if (!target.showError.has(f)) {
                target.showError.add(f);
                mutated = true;
              }
              break;
            }
            case 'hide_error_message': {
              // 0.37.47 — Sticky mirror of the reactive
              // hide_error_message case. Removes the display
              // flag but leaves target.errors intact so a
              // later show_error_message can re-surface it.
              if (target.showError.delete(f)) mutated = true;
              break;
            }
            // set_value / clear_value are edge-triggered above
            // (they write to the values map, which is already
            // persistent) — no sticky handling needed.
          }
        }
      }
      return mutated;
    };
    setSticky(prev => {
      let next = null;
      const ensureNext = () => {
        if (next === null) {
          next = {
            labels:       new Map(prev.labels),
            // 0.37.79 — Sticky texts copy alongside labels.
            texts:        new Map(prev.texts),
            // 0.40.05 — variants MUST be copied here. This clone
            // rebuilds the sticky object field by field, so any layer
            // omitted is simply gone from the next state. 0.40.00
            // added variants to the initialiser and to the readers
            // (anySticky, overlayMap) but missed this clone, so the
            // first click-triggered rule produced a sticky object
            // without it and the next render threw
            // "Cannot read properties of undefined (reading 'size')".
            variants:     new Map(prev.variants),
            // 0.37.85 — Sticky table overlays deep-cloned per
            // element_id so mutations to one table don't
            // observe another's operations mid-update.
            tableOverlays: cloneTableOverlays(prev.tableOverlays),
            hidden:       new Set(prev.hidden),
            required:     new Map(prev.required),
            locked:       new Set(prev.locked),
            actionValues: new Map(prev.actionValues),
            sortOrder:    new Map(prev.sortOrder),
            addedOptions: new Map(prev.addedOptions),
            errors:       new Map(prev.errors),
            showError:    new Set(prev.showError),
            // 0.47.73 - Fields flagged for their Number range.
            rangeFlags:   new Set(prev.rangeFlags || []),
            rangeTouched: new Set(prev.rangeTouched || []),
          };
        }
        return next;
      };
      let anyChange = false;
      for (const rule of safeRules) {
        if (!rule || rule.enabled === false) continue;
        if (!ruleHasClickTrigger(rule)) continue;
        if (newlyFiredThen.includes(rule.id)) {
          if (applyStickyEffects(rule.then, ensureNext())) anyChange = true;
        }
        if (newlyFiredElse.includes(rule.id)) {
          if (applyStickyEffects(rule.else, ensureNext())) anyChange = true;
        }
      }
      return anyChange ? next : prev;
    });
    prevFiredRulesRef.current = new Set(currThen);
    prevElseFiredRulesRef.current = new Set(currElse);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ruleState.rulesFired, ruleState.rulesElseFired, rules, structure]);

  // 0.38.88 - Release valve for sticky required-field errors. When a
  // field that show_required_errors flagged becomes non-empty, drop
  // it from sticky.errors + sticky.showError so its error bar clears
  // as the citizen fills it. Mirrors AdminFormPreview. Without this
  // the errors would persist forever after the first failed submit.
  useEffect(() => {
    if (!values) return;
    setSticky(prev => releaseFlags(prev, values, structure));
  // 0.47.73 - releaseFlags (lib/numberRange.js): as before, plus a
  // Number field still outside its range keeps its error.
  }, [values]);

  // 0.37.24 — Continuous applier for expression-sourced set_values.
  // Unlike the edge-triggered useEffect above (which fires once on
  // rule transition), this one fires on every render and re-applies
  // whatever ruleState says the field should be. That's how a
  // formula field like `=field1 + field2` stays in sync as admin or
  // citizen types into field1 / field2 — the expression recomputes
  // in evaluateRules, its result lands in ruleState.forcedValues,
  // and this effect propagates it to actual form state.
  //
  // The equality check (`prev[f] === v`) prevents infinite loops:
  // if the recomputed value is unchanged, setValues returns
  // prevValues by reference and React skips the update.
  //
  // Consequence: expression fields become effectively read-only.
  // Any manual edit by the citizen is overwritten on the next
  // render (same tick, from the setValues that follows their
  // input). That's spreadsheet behavior — you can't "type over" a
  // cell containing a formula. Admin who wants a literal-with-
  // override should write set_value without the '=' prefix.
  useEffect(() => {
    if (!ruleState.expressionFields || ruleState.expressionFields.size === 0) return;
    setValues(prev => {
      let next = null;
      for (const f of ruleState.expressionFields) {
        if (!ruleState.forcedValues.has(f)) continue;
        const v = ruleState.forcedValues.get(f);
        // 0.48.87 - A Cascade's part, inside its value.
        if (cascadePartOf(structure, f)) {
          const trial = { ...(next || prev) };
          if (assignFieldValue(trial, structure, f, v)) next = trial;
          continue;
        }
        if (prev[f] === v) continue;
        if (!next) next = { ...prev };
        next[f] = v;
      }
      return next || prev;
    });
  }, [ruleState.forcedValues, ruleState.expressionFields]);

  // updateValue — single onChange callback threaded through ctx.
  const updateValue = (name, value) => {
    if (!name) return;
    setValues(prev => ({ ...prev, [name]: value }));
  };
  /* 0.48.98 - A checkbox table's ticked rows: <table>_checked (a list of
     rows) and <table>_checked_count. Written only when they differ.
     0.49.11 - Compared with the page's values as they are now, not with
     what was written last. Opening the same page again (back, then
     forward) starts its values afresh, and the remembered "already
     written" kept the ticked rows from being written again: the total
     read 0 until a box was clicked. */
  const publishTableChecked = useCallback((eid, list) => {
    if (!eid) return;
    const key = `${eid}_checked`;
    const sig = JSON.stringify(list);
    setValues(prev => {
      const cur = prev ? prev[key] : undefined;
      if (cur !== undefined && JSON.stringify(cur) === sig
          && prev[`${eid}_checked_count`] === list.length) return prev;
      return { ...prev, [key]: list, [`${eid}_checked_count`]: list.length };
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 0.36.266 — Lazy-load countries (only when the structure contains a
  // phone element). HenaketPhoneInput requires the list for its
  // country picker. Most public pages don't have a phone field, so
  // skipping the fetch when not needed keeps the page snappy.
  const [countries, setCountries] = useState([]);
  useEffect(() => {
    if (!Array.isArray(structure)) return;
    let hasPhone = false;
    walkInputs(structure, (el) => {
      if (el && el.type === 'phone') hasPhone = true;
    });
    if (!hasPhone) return;
    let cancelled = false;
    countriesApi.list()
      .then(list => {
        if (cancelled) return;
        setCountries(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        // Silent failure — phone widget falls back to typing the
        // full number without country picker. Better than blocking
        // the whole page if the lookup endpoint is unavailable.
      });
    return () => { cancelled = true; };
  }, [structure]);

  // 0.36.278 / 0.36.284 — Lazy-load library data for any dropdown /
  // multiselect element whose `source === 'library'`. Refetches on
  // language change so the citizen sees library labels in their
  // chosen language. Storage is per-language: when admin enables a
  // second language and the citizen switches, librariesData carries
  // the latest fetched bundle for the active language only —
  // simpler than maintaining a Map<lang, data> for a citizen-facing
  // view that only renders one language at a time.
  // 0.37.25 — Reuse the `pcrLanguage` hoisted to the top of this
  // function (see the ruleState useMemo). Was: another
  // useLanguage() call here; kept its variable name `language` in
  // scope for the code below via alias.
  const language = pcrLanguage;
  // 0.37.43 — librariesData useState was previously declared HERE
  // (line ~2077). Moved up to sit alongside the other early-declared
  // state (justClickedButton, sticky) because the ruleState useMemo
  // above now references it as a dependency (0.37.40 added
  // librariesData to labels_of's data sources). React hooks execute
  // in declaration order — having librariesData declared here left
  // it in the TDZ when the ruleState useMemo ran, and the whole
  // component crashed with "cannot access 'b' before
  // initialization" on the public form.
  useEffect(() => {
    if (!Array.isArray(structure)) return;
    // 0.36.657 — Skip the batch prefetch for libraries whose every
    // consumer element is server-source eligible. Those consumers
    // will fetch on demand (page 1 of 100 rows) instead of the
    // page load pulling every row up front. For a 10k-row library
    // like NCL goods/services with a single vanilla dropdown, that
    // saves ~10k rows × ~200 bytes = ~2 MB per page load.
    //
    // Rule: a library key is prefetched only if AT LEAST ONE of
    // its consumer elements has an advanced feature that requires
    // the full row set (cascade parent, extras, exclude, etc.).
    // See `_isServerSourceEligible` for the full list.
    const keys = new Set();
    const keyNeedsFull = new Map();
    /* 0.42.24 - A repeating display fed by a library needs its FULL
       rows, like a dropdown that is not server-searched. It is not an
       input, so walkInputs never visits it - hence a walk of its own. */
    const walkAll = (list) => {
      for (const node of (list || [])) {
        if (!node) continue;
        if (node.type === 'repeating_display' && node.source === 'library' && node.library) {
          keys.add(node.library);
          keyNeedsFull.set(node.library, true);
        }
        if (Array.isArray(node.children)) walkAll(node.children);
      }
    };
    walkAll(structure);
    walkInputs(structure, (el) => {
      if (el && (el.type === 'dropdown' || el.type === 'multiselect')
          && el.source === 'library' && el.library) {
        keys.add(el.library);
        // Note: ruleState isn't available inside this effect (rules
        // run in a separate hook chain). We pass null here — a rule
        // that dynamically adds options for a specific element name
        // is a runtime concern, and if it fires the child element
        // will fall back to the options path via the render check.
        if (!_isServerSourceEligible(el, null)) {
          keyNeedsFull.set(el.library, true);
        }
      }
      // Cascading child references its own library — but if it
      // itself has filter_by_element, _isServerSourceEligible
      // already returned false, so `keyNeedsFull` is already set.
    });
    // Also: cascade PARENTS trigger child auto-clear which reads
    // librariesData for the CHILD's library. So if a library has
    // any cascading-child consumer (filter_by_element set), that
    // library needs to stay prefetched. The check above already
    // catches this — cascading children are non-eligible → their
    // library goes into keyNeedsFull.
    const keysToFetch = [...keys].filter(k => keyNeedsFull.get(k));
    if (keysToFetch.length === 0) {
      // Still clear stale data (language change with all keys now
      // eligible → previous rows shouldn't leak through).
      setLibrariesData({});
      return undefined;
    }
    let cancelled = false;
    Promise.all(keysToFetch.map(k =>
      formLibraryRowsApi.list(k, { lang: language })
        // 0.44.67 - Date columns in the Public site format (stored as <col>_iso).
        .then(data => {
          const v = { rows: data.rows || [], pk: data.pk, column_types: data.column_types || null };
          // 0.47.51 - Kept for the next visit (small libraries only).
          writeLibraryRows(k, language, v);
          return [k, v];
        })
        .catch(() => [k, { rows: [], pk: null }])  // soft-fail: empty list
    )).then(entries => {
      if (cancelled) return;
      // REPLACE (not merge) — when language changes, stale rows in
      // the previous language must not leak through for keys NOT
      // included this round.
      setLibrariesData(Object.fromEntries(entries));
    });
    return () => { cancelled = true; };
  }, [structure, language]);

  if (!Array.isArray(structure) || structure.length === 0) {
    return null;
  }

  // 0.36.626 — Cascading auto-clear. When a parent dropdown's value
  // changes, any child dropdown/multiselect that was showing a
  // value no longer valid under the new parent gets cleared. Guards
  // against invalid submissions and matches the "hide options"
  // empty-behavior admin picked.
  /* 0.48.75 - A child whose own rows are not loaded here (a library
     dropdown that searches on the server, which most do, or an API
     list) could not be checked against them, so it kept its value
     after its parent changed: Community still held a Lori community
     with Kotayk chosen. Now a change of the parent clears the child
     (and so on down), unless the child itself changed in the same
     step (a rule setting both). The first values a page gets (opening,
     a flow coming back) are only remembered. */
  const prevCascadeValues = useRef(null);
  useEffect(() => {
    if (!Array.isArray(structure)) return;
    const prev = prevCascadeValues.current;
    prevCascadeValues.current = values;
    if (!prev || prev === values) return;
    const same = (a, b) => JSON.stringify(a ?? '') === JSON.stringify(b ?? '');
    const clear = {};
    const walk = (nodes) => {
      if (!Array.isArray(nodes)) return;
      for (const n of nodes) {
        if (!n || typeof n !== 'object') continue;
        const name = getElementId(n);
        if (name && n.filter_by_element && (n.type === 'dropdown' || n.type === 'multiselect')) {
          const parentChanged = !same(prev[n.filter_by_element], values[n.filter_by_element])
            || Object.prototype.hasOwnProperty.call(clear, n.filter_by_element);
          const childChanged = !same(prev[name], values[name]);
          const childVal = values[name];
          const filled = Array.isArray(childVal) ? childVal.length > 0 : (childVal !== '' && childVal != null);
          if (parentChanged && !childChanged && filled) clear[name] = Array.isArray(childVal) ? [] : '';
        }
        if (Array.isArray(n.children)) walk(n.children);
        if (Array.isArray(n.columns)) {
          for (const c of n.columns) if (Array.isArray(c.children)) walk(c.children);
        }
      }
    };
    // Twice, so a grandchild placed above its parent is reached too.
    walk(structure); walk(structure);
    for (const [name, v] of Object.entries(clear)) updateValue(name, v);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values, structure]);

  useEffect(() => {
    if (!Array.isArray(structure)) return;
    const toClearScalar = [];
    const toReplaceArray = [];
    const walk = (nodes) => {
      if (!Array.isArray(nodes)) return;
      for (const n of nodes) {
        if (!n || typeof n !== 'object') continue;
        if (n.filter_by_element && n.filter_by_column
            && n.source === 'library' && n.library) {
          const parentVal = values[n.filter_by_element];
          const childName = getElementId(n);
          if (!childName) continue;
          const childVal = values[childName];
          const libData = librariesData && librariesData[n.library];
          const rows = libData && Array.isArray(libData.rows) ? libData.rows : null;
          const valueField = n.value_column || (libData && libData.pk);
          const parentEmpty = parentVal === '' || parentVal == null
            || (Array.isArray(parentVal) && parentVal.length === 0);
          if (Array.isArray(childVal) && childVal.length > 0) {
            // multiselect child: keep only values whose row's
            // filter_by_column matches parentVal.
            // 0.36.658 — parentVal may itself be an array
            // (multiselect parent). Union match: keep a child value
            // if its row's filter_by_column equals ANY of the
            // selected parent values.
            if (parentEmpty) {
              if (childVal.length > 0) toReplaceArray.push([childName, []]);
              continue;
            }
            if (!rows || !valueField) continue;
            const targets = new Set(
              Array.isArray(parentVal)
                ? parentVal.map(v => String(v))
                : [String(parentVal)]
            );
            const kept = childVal.filter(v => {
              const row = rows.find(r => String(r[valueField]) === String(v));
              return row && cascadeMatches(row[n.filter_by_column], parentVal, cascadeOp(n));
            });
            if (kept.length !== childVal.length) toReplaceArray.push([childName, kept]);
          } else if (childVal !== '' && childVal != null) {
            if (parentEmpty) {
              toClearScalar.push(childName);
              continue;
            }
            if (!rows || !valueField) continue;
            // 0.36.658 — Same OR semantics for scalar child.
            const targets = new Set(
              Array.isArray(parentVal)
                ? parentVal.map(v => String(v))
                : [String(parentVal)]
            );
            const row = rows.find(r => String(r[valueField]) === String(childVal));
            if (!row || !cascadeMatches(row[n.filter_by_column], parentVal, cascadeOp(n))) {
              toClearScalar.push(childName);
            }
          }
        }
        if (Array.isArray(n.children)) walk(n.children);
        if (Array.isArray(n.columns)) {
          for (const c of n.columns) if (Array.isArray(c.children)) walk(c.children);
        }
      }
    };
    walk(structure);
    for (const name of toClearScalar) updateValue(name, '');
    for (const [name, arr] of toReplaceArray) updateValue(name, arr);
  }, [values, structure, librariesData, updateValue]);

  // 0.36.76 — Set the module-scoped templates array so renderButton
  // can resolve `variant: 'template:N'` references during this render
  // pass. Synchronous React render → no concurrency concerns.
  templatesForRender = Array.isArray(buttonTemplates) ? buttonTemplates : [];

  // 0.36.106 — ctx threaded through renderElementInRow / renderLineGroupChild
  // / renderRealInput. Carries the controlled state + the ruleState
  // helpers without bloating every function signature.
  // 0.37.32 — signalClick lets any element's click handler tell
  // the rule engine "this button was just clicked" via its
  // element_id. The rule engine reads justClickedButton on the
  // next render, evaluates just_clicked rules, then clears it a
  // tick later. See the useState + useEffect near the ruleState
  // useMemo above. Buttons (and any other element that wants to
  // participate in the click-trigger flow) call
  // ctx.signalClick(elementId) inside their onClick.
  const signalClick = useCallback((elementId) => {
    if (typeof elementId !== 'string' || elementId === '') return;
    setJustClickedButton(elementId);
  }, []);
  /* 0.48.63 - Modals (Column groups shown as a modal window): which are
     open, changed by the rule effects open modal / close modal and by
     the window's own OK, Cancel, x and Escape. */
  const [openModals, setOpenModals] = useState(() => new Set());
  const setModalOpen = useCallback((id, on) => {
    setOpenModals(prev => {
      if (!id || prev.has(id) === !!on) return prev;
      const next = new Set(prev);
      if (on) next.add(id); else next.delete(id);
      return next;
    });
  }, []);
  useEffect(() => {
    const opens = (ruleState && ruleState.modalOpen) || [];
    const closes = (ruleState && ruleState.modalClose) || [];
    if (!opens.length && !closes.length) return;
    setOpenModals(prev => {
      const next = new Set(prev);
      closes.forEach(id => next.delete(id));
      opens.forEach(id => next.add(id));
      return next;
    });
  }, [ruleState]);
  /* 0.48.64 - The Edit link on a table's rows. The clicked row is
     published as <table>_row; values written by "set clicked row" are
     kept per row and published as <table>_edited (the edited rows). */
  const [rowEdits, setRowEdits] = useState({});
  const rowEditsRef = useRef({});
  const clickedRowsRef = useRef({});
  const editedRowsRef = useRef({});
  const clickRowEdit = useCallback((eid, key, rowObj) => {
    clickedRowsRef.current[eid] = { key, row: rowObj };
    setValues(prev => ({ ...prev, [`${eid}_row`]: rowObj }));
    signalClick(`${eid}.edit`);
  }, [signalClick]);
  // The required fields a Modal's OK found empty: shown as errors, as
  // "show errors on empty required fields" does (cleared once filled).
  const flagFieldErrors = useCallback((list) => {
    setSticky(prev => {
      const showError = new Set(prev.showError);
      const errors = new Map(prev.errors);
      (list || []).forEach(([id, msg]) => { showError.add(id); errors.set(id, msg); });
      return { ...prev, showError, errors };
    });
  }, []);
  // 0.37.38 → 0.37.40 — Prebuilt expression functions registry
  // 0.38.16 - Form-level number-format defaults threaded into ctx
  // so resolveAdminText can auto-format Number-valued expression
  // results without every render site having to know about the
  // format() function. Platform-level defaults are read directly
  // inside resolveAdminText from the module singleton
  // (getNumberFormatDefaults) so they pick up post-mount settings
  // fetches without needing a re-memoization signal.
  //
  // 0.38.24 - Moved BEFORE expressionFunctions and now passed into
  // buildExpressionFunctions so concat() and the `&` operator can
  // reach the form-level cascade tier from inside the expression
  // evaluator. Platform defaults still come from the singleton
  // (read fresh at fragment-format time inside the function).
  const formNumberDefaults = useMemo(() => {
    const perForm = (settings && settings.number) || {};
    return {
      default_format: perForm.default_format || '',
      default_locale: perForm.default_locale || '',
    };
  }, [settings]);

  // 0.36.106 → 0.37.24 — Expression functions (lib, labels_of, etc.)
  // passed via ctx so element renderers (renderButton's click
  // handler in particular) can evaluate `=` expressions with the
  // same set of functions available in rule set_values. Rebuilt
  // when values / structure / language / librariesData / form
  // settings change — labels_of() needs librariesData to resolve
  // library-backed dropdown options; concat/& need
  // formNumberDefaults for the per-form cascade tier.
  const expressionFunctions = useMemo(
    // 0.41.34 - apiState so api() works in expressions here too, not
    // only inside rule evaluation. Without it the function would exist
    // and silently return undefined, which is worse than not existing.
    () => buildExpressionFunctions({
      lang: language, values, structure, librariesData, formNumberDefaults,
      apiState,
    }),
    // 0.40.04 - libTick added. Library-backed lookups inside
    // expressions (options_count, lib(...)) can fire before the
    // library has loaded; they kick off a fetch and subscribeLibraryCache
    // bumps libTick when it lands. Without libTick here the memo was
    // never rebuilt, so the text kept the first, empty result - a
    // dropdown with 45 options rendered as 0 forever. ruleState
    // already depends on libTick, which is why labels_of recovered
    // and expression-driven text did not.
    // 0.41.34 - apiState IS a dependency: without it an expression
    // using api() would keep the value it had when the memo was built,
    // so a label would never update when the response arrived.
    [language, values, structure, librariesData, formNumberDefaults, libTick, apiState],
  );

  /* 0.48.89 - Server copies (Settings, Server copies; the server's
     services/server_copy.py). The page holds a read copy in the value
     named like the copy (=adv.result.email), with <name>_state loading,
     ready or failed, and <name>_failure. Values are worked out here, at
     the moment the rule ran; the server checks every path and answers
     with the copy as it now is. One copy's requests run one after
     another, in the order the rules asked. */
  const copyChains = useRef({});
  // The same request asked again by a re-evaluation of the same moment
  // (a click's rules run more than once while it is "just clicked").
  const copyRecent = useRef({});
  useEffect(() => {
    const ops = (ruleState && ruleState.copyOps) || [];
    if (!ops.length) return;
    for (const op of ops) {
      const name = op.name;
      let body = { page: pageSlug || '', name, lang: pcrLanguage || '' };
      // 0.48.90 - A copy of the flow, shared by its steps.
      if (flowSlug && Array.isArray(flowCopies) && flowCopies.includes(name)) body.flow = flowSlug;
      if (op.op === 'fill' && op.fresh) body.fresh = true;
      if (op.op === 'set') {
        const changes = [];
        for (const v of op.values || []) {
          const raw = v.value;
          let val;
          if (typeof raw === 'string' && isExpression(raw)) {
            val = evaluateExpression(extractExpression(raw), values || {}, expressionFunctions);
          } else if (typeof raw === 'string') {
            val = interpolateValues(raw, values || {});
          } else {
            val = raw;
          }
          changes.push({ path: String(v.path).trim(), value: val === undefined ? null : val });
        }
        if (!changes.length) continue;
        body = { ...body, changes };
      }
      const url = `/api/server-copy/${op.op === 'set' ? 'change' : op.op}`;
      const sig = `${url}|${JSON.stringify(body)}`;
      const now = Date.now();
      if (copyRecent.current[sig] && now - copyRecent.current[sig] < 1500) continue;
      copyRecent.current[sig] = now;
      const prev = copyChains.current[name] || Promise.resolve();
      copyChains.current[name] = prev.then(() => {
        // 0.48.93 - A new request clears the last failure.
        setValues(p => ({ ...p, [`${name}_state`]: op.op === 'fill' ? 'loading' : 'saving',
                          [`${name}_failure`]: '', [`${name}_refused`]: '' }));
        return fetch(url, {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
        }).then(async (res) => {
          let d = null;
          try { d = await res.json(); } catch { d = null; }
          if (res.ok && d && d.data !== undefined) {
            /* 0.48.93 - A change ends as "saved" for one moment (a rule
               closes the modal then), and is "ready" after it. */
            setValues(p => ({ ...p, [name]: d.data, [`${name}_state`]: op.op === 'set' ? 'saved' : 'ready',
                              [`${name}_failure`]: '',
                              // 0.48.96 - Differs from the API's answer.
                              [`${name}_dirty`]: !!d.dirty }));
          } else {
            const failure = (d && d.detail && d.detail.failure) || (res.status === 401 ? 'not_signed_in' : 'unavailable');
            // 0.48.93 - and the path that may not be changed, when that was why.
            setValues(p => ({ ...p, [`${name}_state`]: 'failed', [`${name}_failure`]: failure,
                              [`${name}_refused`]: (d && d.detail && d.detail.path) || '' }));
          }
        }).catch(() => {
          setValues(p => ({ ...p, [`${name}_state`]: 'failed', [`${name}_failure`]: 'network' }));
        });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ruleState]);

  // 0.48.93 - "saved" lasts one evaluation, then the copy is "ready".
  useEffect(() => {
    const done = Object.keys(values || {}).filter(k => k.endsWith('_state') && values[k] === 'saved');
    if (!done.length) return;
    setValues(p => {
      const n = { ...p };
      for (const k of done) if (n[k] === 'saved') n[k] = 'ready';
      return n;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values]);

  // 0.48.64 - "set clicked row of <table>": the values are worked out
  // here, with the page's values at the moment the rule ran.
  useEffect(() => {
    const sets = (ruleState && ruleState.rowSets) || [];
    if (!sets.length) return;
    let next = rowEditsRef.current;
    const published = {};
    for (const rs of sets) {
      const clicked = clickedRowsRef.current[rs.table];
      if (!clicked) continue;
      const patch = {};
      for (const v of rs.values || []) {
        const raw = v.value;
        let val;
        if (typeof raw === 'string' && isExpression(raw)) {
          val = evaluateExpression(extractExpression(raw), values || {}, expressionFunctions);
        } else if (typeof raw === 'string') {
          val = interpolateValues(raw, values || {});
        } else {
          val = raw;
        }
        patch[String(v.column).trim()] = val == null ? '' : val;
      }
      const tableEdits = { ...(next[rs.table] || {}) };
      const before = tableEdits[clicked.key] || {};
      const merged = { ...before, ...patch };
      if (JSON.stringify(merged) === JSON.stringify(before)) continue;
      tableEdits[clicked.key] = merged;
      next = { ...next, [rs.table]: tableEdits };
      const row = applyRowEdits(clicked.row, patch);
      clicked.row = row;
      editedRowsRef.current[rs.table] = { ...(editedRowsRef.current[rs.table] || {}), [clicked.key]: row };
      published[`${rs.table}_row`] = row;
      published[`${rs.table}_edited`] = Object.values(editedRowsRef.current[rs.table]);
    }
    if (next === rowEditsRef.current) return;
    rowEditsRef.current = next;
    setRowEdits(next);
    setValues(prev => ({ ...prev, ...published }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ruleState]);

  // 0.38.17 - Subscribe to platform number-format changes so the
  // renderer re-runs when /api/public/settings resolves post first
  // render. Without this, a form that mounts before the fetch
  // completes captures the initial empty platform defaults and
  // never picks up the real values until some unrelated state
  // change forces a re-render (user typing, navigation, etc.).
  // The tick is a version counter; its value is unused (we just
  // need React to know something changed so the render pipeline
  // reruns and resolveAdminText picks up the fresh platform
  // defaults on its next call).
  const [_platformDefaultsTick, setPlatformDefaultsTick] = useState(0);
  useEffect(() => {
    return subscribeToNumberFormatDefaults(() => {
      setPlatformDefaultsTick(t => t + 1);
    });
  }, []);

  // 0.37.36 → 0.37.37 — ctx.ruleState carries the sticky-merged
  // version (see effectiveRuleState useMemo above) so renderers
  // pick up labels / hidden / etc. set by click-triggered rules
  // that persist past the one-render just_clicked window. When
  // no click-triggered rules have fired (sticky empty),
  // effectiveRuleState === ruleState identity-wise, so no extra
  // work downstream.
  // 0.41.37 - apiState added so an API-backed table can read the last
  // response. renderTable is a separate function and has no other way
  // to reach it.
  /* 0.42.80 - `keepOnUnmount` tells an element that leaving this page is
     not leaving the form. In a FLOW the answers belong to the flow and
     the citizen is coming back; on an ordinary page an element that
     goes away should take its values with it, or a rule would read a
     size and an extension for a file that is no longer there. */
  // 0.46.82 - system: a system page's behavior for its built-in
  // elements (the login page, LoginPage.jsx). Absent everywhere else.
  // 0.47.73 - A Number field left (or pasted into) outside its Min / Max.
  const flagNumberRange = (el, v) => setSticky(prev => flagOneRangeError(prev, el, v, values));
  const ctx = { t: pcrT, flagNumberRange, values, updateValue, publishTableChecked, signalClick, rowEdits, clickRowEdit, openModals, setModalOpen, flagFieldErrors, ruleState: effectiveRuleState, countries, librariesData, language, expressionFunctions, formNumberDefaults, apiState, callApiPage, keepOnUnmount: controlled, system };

  const bands = bandsFromStructure(structure);
  // 0.47.05 - The last band is a Layer left open (no End layer after
  // it): it runs to the footer.
  const lastMarker = [...(structure || [])].reverse()
    .find(el => el && (el.type === 'layer' || el.type === 'layer_end'));
  const openLastLayer = !!(lastMarker && lastMarker.type === 'layer');
  const openBandRef = useRef(null);
  useExtendBandToFooter(openBandRef, openLastLayer);
  // 0.46.93 - The first band shown is marked (global.css): a plain one
  // starts 8px under the navigation menu, and a title at its top drops
  // its own space above. Gaps beyond that come from the Toolbox.
  let firstBandShown = false;
  return (
    <>
      {/* 0.48.29 - The bot check's hidden field, on a page that creates accounts. */}
      {hasCreateAccount && signupGuard.field}
      {bands.map((band, bandIdx) => {
        // 0.36.107 — Pre-render the band's elements so we can check
        // if anything is actually visible BEFORE deciding whether to
        // render the whole section/container/row scaffolding. When
        // every element in a band is hidden by a rule AND the band
        // has no layer (no background color or media), the band is
        // entirely invisible — but the section still occupies space
        // via the row's margins. Skip it.
        //
        // Bands with a layer (bg color / media) are KEPT even when
        // empty — the colored area is intentional decoration, not
        // dependent on content. Admins who want a colored band to
        // disappear with its content should structure it so the
        // layer's children include the rule-driven content.
        const renderedElements = band.elements.map(
          (el) => renderElementInRow(el, ctx)
        );
        const hasVisibleContent = renderedElements.some(e => e !== null);
        if (!hasVisibleContent && !band.layer) return null;

        // Layer config → background style. No layer = transparent
        // (the default band, before any layer or in layer-less forms).
        const bg = band.layer
          ? hexToRgba(band.layer.background_color, band.layer.opacity)
          : 'transparent';
        // 0.36.43 — full_bleed (default true) decides the band's
        // edge behavior. full_bleed=true → full-viewport-width
        // colored area. full_bleed=false → colored area constrained
        // to the 1200px container (still wraps content, just with
        // the colored edges pulled in to match the container width).
        const contained = band.layer && band.layer.full_bleed === false;
        // 0.36.60 — Optional background media (image or video).
        // When bg_media_src is set, render the media absolutely
        // positioned behind the band's content, with the color
        // (and its opacity) acting as a tint overlay on top.
        // Position mode: 'cover' (default) fills the band, may crop;
        // 'contain' shows the full media inside; 'tile' repeats.
        const mediaSrc = band.layer ? (band.layer.bg_media_src || '') : '';
        const mediaType = band.layer ? (band.layer.bg_media_type || 'image') : 'image';
        const mediaPosition = band.layer ? (band.layer.bg_media_position || 'cover') : 'cover';
        const hasMedia = !!mediaSrc;
        // When media is present, the section itself goes transparent
        // (so the media shows through) and the color is rendered as
        // an overlay div ABOVE the media. Otherwise, the color is
        // the section background as before.
        // 0.39.96 / 0.39.99 - A flush_top first band had 24px of
        // colored top padding to replace the header's bottom margin.
        // 0.45.43 - The header has no bottom margin and every band has
        // 24px top padding, so a flush band is drawn like any other.
        // 0.45.02 - On a dark layer, text set to the platform default
        // turns white: the layer passes --layer-text-color down, and
        // Markdown's default color reads it.
        // 0.45.04 - So do titles, labels, hints, help and summary
        // lines, option texts and plain text (the band's own color).
        // Light surfaces inside it (cards, alerts, tables, dropdown and
        // calendar panels) reset it and keep dark text.
        const darkLayer = band.layer
          && isDarkLayer(band.layer.background_color, band.layer.opacity);
        const textVar = darkLayer
          ? { '--layer-text-color': 'var(--color-white)', color: 'var(--color-white)' } : null;
        // 0.47.01 - The band's own corner radius.
        const radius = band.layer ? bandRadius(band.layer.corner_radius) : 0;
        const radiusStyle = { ...(radius ? { borderRadius: `${radius}px` } : null),
          // 0.47.02 - Border, inner stroke and shadow.
          ...(band.layer ? bandFrameStyle(band.layer, '', radius) : null) };
        const below = bandSpaceBelow(band);
        const sectionStyle = hasMedia
          ? { background: 'transparent', position: 'relative', overflow: 'hidden',
              ...textVar, ...radiusStyle }
          : { background: bg, ...textVar, ...radiusStyle };
        if (below) sectionStyle.marginBottom = below;
        const firstBand = !firstBandShown;
        firstBandShown = true;
        return (
          <section
            key={band.layer ? band.layer.id : `default-${bandIdx}`}
            className={`page-band${contained ? ' page-band-contained' : ''}`}
            style={sectionStyle}
            data-edge={contained && band.layer.edge_align === 'content' ? 'content' : undefined}
            data-first-band={firstBand ? (band.layer ? 'layer' : 'plain') : undefined}
            data-band-stroke={band.layer && bandHasStroke(band.layer) ? '' : undefined}
            ref={openLastLayer && band.layer && bandIdx === bands.length - 1 ? openBandRef : undefined}
            data-open-band={openLastLayer && band.layer && bandIdx === bands.length - 1 ? '' : undefined}
          >
            {hasMedia && (
              <>
                {mediaType === 'video' ? (
                  /* 0.47.41 - Loop, once or N times (BandVideo). */
                  <BandVideo layer={band.layer} style={{
                      position: 'absolute', inset: 0,
                      width: '100%', height: '100%',
                      objectFit: mediaPosition === 'contain' ? 'contain' : 'cover',
                      // 0.47.43 - Where it is cut.
                      objectPosition: bandMediaFocus(band.layer),
                      zIndex: 0,
                    }} />
                ) : (
                  <div
                    aria-hidden="true"
                    style={{
                      position: 'absolute', inset: 0,
                      backgroundImage: `url(${withBase(mediaSrc)})`,
                      backgroundSize: mediaPosition === 'tile' ? 'auto' : mediaPosition,
                      backgroundRepeat: mediaPosition === 'tile' ? 'repeat' : 'no-repeat',
                      backgroundPosition: bandMediaFocus(band.layer),
                      zIndex: 0,
                    }}
                  />
                )}
                {/* Color tint overlay on top of the media. Uses the
                  * layer's background_color + opacity as the tint
                  * strength — opacity 0 hides the tint entirely
                  * (raw media shows), opacity 1 fully covers the
                  * media (looks like the color-only band did). */}
                <div
                  aria-hidden="true"
                  style={{
                    position: 'absolute', inset: 0,
                    background: bg,
                    zIndex: 1,
                  }}
                />
              </>
            )}
            {/* 0.47.43 - The media's shape, as the band's least height. */}
            {hasMedia && bandMediaSpacer(band.layer)}
            {/* Each band has its own container/row inside so columns
              * + grid math apply within the band, even though the
              * band itself is full-page-width. */}
            <div className="container" style={{ ...(hasMedia ? { position: 'relative', zIndex: 2 } : {}), ...layerSidePadding(band.layer) }}>
              {/* 0.39.95 - topSlot (the page's Return button) is drawn
                  inside the FIRST band when that band's layer asked to
                  start flush with the sub-header. It sits above the
                  band's own content row, on the colored area. */}
              {bandIdx === 0 && topSlot && (
                <div className="row">
                  <div className="col-12">
                    {topSlot}
                  </div>
                </div>
              )}
              <div className="row">
                {renderedElements}
              </div>
            </div>
            {hasMedia && bandMediaSpacer(band.layer) && <div aria-hidden="true" style={{ clear: 'both' }} />}
          </section>
        );
      })}
      {/* 0.46.89 - Last, not first: placed first, this hidden mark sat
          between a flow's step navigator and the step's first band, and
          the 24px rule after the navigator (global.css, 0.46.37) no
          longer matched, so the band's own padding and a title's top
          space came back (48px or more). */}
      <MainAlignAnchor />
    </>
  );
}

// 0.36.46 — Recursive element renderer used inside any `.row`. For
// column_group elements, emits the nested col + row + children
// pattern so children form their own sub-grid INSIDE the group. For
// every other element, dispatches through RENDERERS / INPUT_TYPES
// as before. Used by both the page band's outer .row and by every
// column_group's inner .row.
//
// 0.36.65 — Vertical spacing between rows of elements is set on the
// col wrapper here, not on each element's internal wrapper. Driven
// by the element's optional `margin_bottom` field (Henaket space-N
// step); falls back to 4 (16px) for elements that don't expose the
// field. This makes the col wrapper THE place where vertical
// rhythm is controlled, eliminating the previous bug where the col
// added 16px AND the inner markdown wrapper added another N px on
// top of it (stacked).
function colMarginBottomFor(el) {
  // 0.47.76 - A Scroll zone marker takes no space.
  if (el && el.type === 'scroll_marker') return '0';
  // 0.47.07 - A Spacer is exactly its height: no space of its own
  // below it (it had the usual 16px, so a 2px spacer added 18px).
  // 0.47.33 - unless it is given a Space below of its own.
  if (el && el.type === 'spacer') {
    return VALID_SPACE_STEPS.has(el.margin_bottom) && el.margin_bottom > 0 ? `var(--space-${el.margin_bottom})` : '0';
  }
  // 0.40.03 - Titles resolve through titleSpacingFor, which applies
  // the spacing MODE (henaket / symmetric / custom) and already
  // honors an explicit margin_bottom in the henaket and custom
  // modes. Checked before the generic margin_bottom branch below,
  // otherwise symmetric mode could never raise the bottom past a
  // stale margin_bottom left on the element.
  if (el && el.type === 'title') {
    const b = titleSpacingFor(el).bottom;
    return b === 0 ? '0' : `var(--space-${b})`;
  }
  const mb = el && el.margin_bottom;
  if (VALID_SPACE_STEPS.has(mb)) {
    return mb === 0 ? '0' : `var(--space-${mb})`;
  }
  // 0.46.37 - The step navigator is followed by 24px (Henaket).
  if (el && el.type === 'stepper') return 'var(--space-6)';
  // 0.46.63 - A guide card by 32px, the gutter between two cards side
  // by side, so rows of cards are evenly spaced.
  if (el && el.type === 'guide_card') return 'var(--space-8)';
  return 'var(--space-4)';  // default 16px, matches pre-0.36.65 behavior
}

/* 0.44.43 - Each element is drawn inside an ElementBoundary: an element
 * whose render crashes (bad data) is left out on its own instead of the
 * whole page failing to load. */
// 0.46.12 - `placed: false` leaves out the Position on the page: a
// column group places its children itself, after sizing them, and a
// card of a repeating display has no page row to sit in.
/* 0.46.64 - The element's Vertical position on the page, read by the
 * line alignment (lib/mainAlign.js) from its column. Top is the
 * default and needs no mark. */
function withVAlign(el, node) {
  const va = el && el.v_align;
  if ((va === 'center' || va === 'bottom') && isValidElement(node)) {
    return cloneElement(node, { 'data-valign': va });
  }
  return node;
}

function renderElementInRow(el, ctx, { placed = true } = {}) {
  const node = withVAlign(el, renderElementInRowCore(el, ctx));
  if (node == null || typeof node === 'boolean') return node;
  const bounded = <ElementBoundary key={(el && el.id) || undefined} el={el}>{node}</ElementBoundary>;
  return placed ? withElementPosition(el, bounded) : bounded;
}

function renderLineGroupChild(el, ctx) {
  const node = withVAlign(el, renderLineGroupChildCore(el, ctx));
  if (node == null || typeof node === 'boolean') return node;
  return <ElementBoundary key={(el && el.id) || undefined} el={el}>{node}</ElementBoundary>;
}

/* 0.47.76 - Every element's grid cell is marked (data-el-cell), so a
   floating element (Stay in view while scrolling) can see what is in
   its columns; a floating one carries its settings (lib/floating.js). */
function cellAttrs(el) {
  return { 'data-el-cell': el && el.id ? String(el.id) : '', ...floatAttrs(el) };
}
/* 0.49.26 - A floating cell's content sits in its own box, which
   lib/floating.js fixes to the screen while it follows it (smooth, the
   browser moves it) instead of shifting it after every scroll step. */
function floatBody(el, children) {
  return el && el.float ? <div data-float-body="">{children}</div> : children;
}

/* 0.48.63 - A Modal: a Column group in a modal window, opened and
   closed by rules (open modal / close modal). Its fields are drafts:
   OK checks the required ones first, then reports "<id>.ok" (and closes,
   unless the admin turned that off); Cancel, x and Escape report
   "<id>.cancel" and close. The content stays mounted while closed. */
function renderModalGroup(el, ctx) {
  const eid = getElementId(el) || String(el.id || '');
  const open = !!(ctx && ctx.openModals && ctx.openModals.has(eid));
  const childrenList = Array.isArray(el.children) ? el.children : [];
  const gspans = groupSpansFor({ ...el, col_span: 12, col_md_span: 12, col_lg_span: 12 });
  const rendered = childrenList.map((child) => {
    const node = renderElementInRow(child, ctx, { placed: false });
    if (!isValidElement(node)) return node;
    const props = colGroupChildProps(child, gspans);
    const mergedClassName = [(node.props.className || ''), props.className].filter(Boolean).join(' ');
    return withElementPosition(child,
      cloneElement(node, { className: mergedClassName, style: { ...(node.props.style || {}), ...props.style } }));
  });
  const txt = (raw, fallback) => {
    if (raw == null || raw === '') return fallback;
    const out = resolveAdminText(raw, ctx);
    return out == null ? '' : String(out);
  };
  const close = () => { if (ctx && ctx.setModalOpen) ctx.setModalOpen(eid, false); };
  const onOk = () => {
    // The modal's required fields, visible and empty, block OK.
    const values = (ctx && ctx.values) || {};
    const rs = (ctx && ctx.ruleState) || null;
    const missing = [];
    const walk = (list) => (list || []).forEach((c) => {
      if (!c) return;
      if (INPUT_TYPES.has(c.type)) {
        const id = getElementId(c);
        if (!id || c.hidden || (rs && rs.hidden && rs.hidden.has(id))) return;
        const req = rs && rs.required && rs.required.has(id) ? !!rs.required.get(id) : !!c.required;
        const v = values[id];
        if (req && (v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0))) {
          missing.push([id, (c.error_required && String(c.error_required)) || 'This field is required']);
        }
      }
      if (Array.isArray(c.children)) walk(c.children);
    });
    walk(childrenList);
    if (missing.length) { if (ctx && ctx.flagFieldErrors) ctx.flagFieldErrors(missing); return; }
    if (ctx && typeof ctx.signalClick === 'function') ctx.signalClick(`${eid}.ok`);
    if (el.modal_ok_closes !== false) close();
  };
  const onCancel = () => {
    if (ctx && typeof ctx.signalClick === 'function') ctx.signalClick(`${eid}.cancel`);
    close();
  };
  const width = Number(el.modal_width);
  return (
    <PageModal key={el.id} id={eid} open={open}
               title={txt(el.modal_title, '')}
               okLabel={txt(el.modal_ok_label, 'OK')}
               cancelLabel={txt(el.modal_cancel_label, 'Cancel')}
               closeLabel={txt(el.modal_close_label, 'Close')}
               hideCancel={!!el.modal_hide_cancel}
               width={[401, 610, 800, 1000].includes(width) ? width : 800}
               onOk={onOk} onCancel={onCancel}>
      <div className="row">{rendered}</div>
    </PageModal>
  );
}

function renderElementInRowCore(el, ctx) {
  /* 0.46.57 - The cards of a Repeating display's group, inside the
     Column group that holds them (RepeatingDisplay builds the slot). */
  if (el && el.type === 'rd_cards_slot') {
    return (
      <div className={colClassFor(el)} key={el.id} style={{ marginBottom: 0 }}>
        {el._node}
      </div>
    );
  }
  // 0.36.106 — Rule effect: hide. On the public side (citizen view)
  // a hidden field renders as NOTHING — no faded ghost like
  // AdminFormPreview shows. The col-X slot disappears entirely; the
  // row collapses gracefully via flex/grid wrap. Hidden has to be
  // checked first because if it fires we want to skip the
  // column_group / line_group / RENDERERS dispatch below entirely.
  if (ctx && el && getElementId(el) && ctx.ruleState && ctx.ruleState.hidden.has(getElementId(el))) {
    return null;
  }
  // 0.46.82 - A built-in element its system page leaves out just now
  // (the ID card button where ID cards are off, the message box with
  // no message).
  if (ctx && ctx.system && el && el.builtin && ctx.system.hidden && ctx.system.hidden.has(el.builtin)) {
    return null;
  }

  // 0.36.720 / 0.36.721 — Repeater. Ship B wires the runtime.
  // Delegates to <RepeaterRuntime> for the template + entries UI;
  // renderElementInRow is passed as the child renderer so template
  // inputs behave identically to how they'd render outside the
  // repeater (same rules integration, same value plumbing).
  //
  // Old __count / inline-instances state from the 0.36.647 model
  // is inert — the runtime reads `values[repName]` as an array,
  // and stray dotted keys like `family_members.0.name` from old
  // saves are simply ignored (they don't fit the new draft/entry
  // shape). Admin re-authoring the repeater under the new model
  // will start with a clean state.
  /* 0.41.84 - Repeating display.
   *
   * Rows are resolved HERE, not inside the component, so a grid of
   * cards and a table over the same data cannot disagree about what a
   * row is: both read the same path out of the same stored response.
   *
   * Only the API source is wired in this ship. Library and manual rows
   * are the same shape - resolve to an array, hand it over - and are
   * listed in the configurator as not yet available rather than
   * offered and silently empty.
   */
  if (el.type === 'repeating_display') {
    /* 0.42.24 - Three sources, then the same rule overlay a table uses.
     *
     *   api      a list in a stored API response
     *   library  a library's rows, loaded with the page
     *   manual   rows the admin typed, one value per column
     *
     * Every source ends as an array of OBJECTS keyed by column name,
     * because that is what `{column:name}` reads. Manual rows are
     * stored positionally, like a table's, and named here from the
     * element's own column list.
     */
    const conn = (el.api_connection || '').trim();
    const source = el.source || 'api';
    const manualCols = Array.isArray(el.manual_columns) ? el.manual_columns : [];
    const toObject = (arr, cols) => {
      const o = {};
      cols.forEach((c, i) => { if (c) o[c] = arr[i] === undefined ? '' : arr[i]; });
      return o;
    };

    let rows = [];
    if (source === 'api') {
      const entry = ctx && ctx.apiState ? ctx.apiState[conn] : null;
      if (entry && entry.response !== undefined) {
        rows = apiRows(entry.response, el.api_rows_path || '', { keepEmpty: !!el.api_rows_keep_empty });
      }
    } else if (source === 'library') {
      const lib = ctx && ctx.librariesData ? ctx.librariesData[el.library] : null;
      rows = lib && Array.isArray(lib.rows) ? lib.rows : [];
    } else if (source === 'manual') {
      rows = (Array.isArray(el.manual_rows) ? el.manual_rows : [])
        .map(r => (Array.isArray(r) ? toObject(r, manualCols) : r));
    }

    /* Rules: `add row` and `remove row`, exactly as they act on a
     * table, keyed by this element's id. Removal is 1-based and runs
     * FIRST, against the source's own rows, so "remove row 2" means
     * the second row the admin configured, not whatever is second
     * after something was added.
     *
     * An added row's values are positional. For a manual source they
     * follow its column list; for api and library, which declare no
     * columns here, they follow the keys of the first row - and with
     * no rows at all there is nothing to name them after, so they are
     * dropped rather than rendered as nameless cards. */
    const overlayKey = getElementId(el);
    const overlay = overlayKey && ctx && ctx.ruleState && ctx.ruleState.tableOverlays
      && ctx.ruleState.tableOverlays.get(overlayKey);
    if (overlay) {
      if (overlay.rowsRemoved && overlay.rowsRemoved.size > 0) {
        rows = rows.filter((_, i) => !overlay.rowsRemoved.has(i + 1));
      }
      if (Array.isArray(overlay.rowsAdded) && overlay.rowsAdded.length > 0) {
        const cols = manualCols.length > 0 ? manualCols
          : (rows[0] && typeof rows[0] === 'object' ? Object.keys(rows[0]) : []);
        if (cols.length > 0) {
          rows = rows.concat(overlay.rowsAdded.map(v => toObject(v, cols)));
        }
      }
    }
    /* 0.48.22 - Show only rows where: before Maximum cards and the
       pages, so "10 cards" means 10 matching rows. A value may read the
       page ({field}, a variable, a formula), so the cards follow it. */
    if (Array.isArray(el.row_filters) && el.row_filters.length) {
      rows = filterRows(rows, el.row_filters, el.row_filter_mode,
        (row, path) => readPath(row, path),
        (v) => resolveAdminText(typeof v === 'string' ? v : (v == null ? '' : String(v)), ctx));
    }
    /* 0.49.43 - Search and filters: the display's Search element and
       its filter lines, read from the page (lib/rdSearch.js). After Show
       only rows where, before Maximum cards and the pages. */
    const rdValueOf = (name) => {
      const v = ctx && ctx.values ? ctx.values[name] : undefined;
      return v === undefined || v === null ? '' : v;
    };
    const rdSearchOn = !!(el.search_enabled && el.search_source_element_id);
    const rdWords = rdSearchOn ? searchWords(rdValueOf(el.search_source_element_id)) : [];
    const rdFilters = Array.isArray(el.rd_filters) ? el.rd_filters : [];

    if (rdWords.length) rows = searchRows(rows, rdWords, el.search_columns, (row, path) => readPath(row, path));
    if (rdFilters.length) rows = filterByControls(rows, rdFilters, rdValueOf, (row, path) => readPath(row, path));
    const rdNarrowed = rdWords.length > 0 || rdFilters.some(f => f && f.control && (
      String(rdValueOf(f.control) || '').trim() !== '' || (f.control2 && String(rdValueOf(f.control2) || '').trim() !== '')));
    const rdControls = [...new Set([...(rdSearchOn ? [el.search_source_element_id] : []),
      ...rdFilters.flatMap(f => [f && f.control, f && f.control2]).filter(Boolean)])];
    // 0.49.49 - Nothing at all (no space) while neither part shows.
    const rdResults = ((el.rd_count_show && rdNarrowed && rows.length > 0) || (el.rd_clear_show && rdNarrowed)) ? (
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: '8px 16px',
                    margin: '0 0 var(--space-4, 16px)' }} aria-live="polite">
        {/* 0.49.49 - Only while something is typed or picked, and not
            for no result (the "nothing matches" text says it). */}
        {el.rd_count_show && rdNarrowed && rows.length > 0 && (
          <strong style={{ fontWeight: 'var(--font-weight-bold, 700)' }}>
            {/* {count} is put in first: resolveAdminText would read it as a field. */}
            {String(resolveAdminText(String(el.rd_count_text || '{count}').split('{count}').join(String(rows.length)), ctx) || rows.length)}
          </strong>
        )}
        {el.rd_clear_show && rdNarrowed && (
          <button type="button" className="hen-link-button"
                  style={{ background: 'none', border: 0, padding: 0, font: 'inherit', cursor: 'pointer',
                           color: 'var(--color-link, #004d99)', textDecoration: 'underline', textUnderlineOffset: 3 }}
                  onClick={() => { rdControls.forEach(n => ctx && ctx.updateValue && ctx.updateValue(n, '')); }}>
            {resolveAdminText(el.rd_clear_text || 'Clear filters', ctx) || 'Clear filters'}
          </button>
        )}
      </div>
    ) : null;
    // 0.45.61 - Pages from the API: the answer is one page already.
    const rdPaging = apiPagingOf(el);
    const rdApiEntry = source === 'api' && ctx && ctx.apiState ? ctx.apiState[conn] : null;
    const rdApiRows = rows.length;
    const pageSize = Number(el.page_size) || 0;
    if (pageSize > 0 && !rdPaging) rows = rows.slice(0, pageSize);

    return (
      <div className={colClassFor(el)} key={el.id}
           style={{ marginBottom: colMarginBottomFor(el) }} {...cellAttrs(el)}>
        {floatBody(el, <>
        {/* 0.45.85 - Where a page change scrolls back to. */}
        {el.pagination_enabled && getElementId(el) && (
          <div data-pagination-anchor={getElementId(el)} aria-hidden="true" />
        )}
        <RepeatingDisplay
          /* 0.49.49 - Nothing passes the search or the filters: the
             display's own "nothing matches" text, when it has one. */
          el={rdNarrowed && rows.length === 0 && String(el.rd_no_match_text || '').trim()
            ? { ...el, empty_text: resolveAdminText(el.rd_no_match_text, ctx) || el.rd_no_match_text }
            : el}
          rows={rows}
          paginationKey={getElementId(el) || ''}
          serverPaged={!!rdPaging}
          // 0.42.40 - Hides the empty-state wording while the call runs.
          // 0.45.61 - Not while another page loads: the cards stay.
          loading={source === 'api' && !!(rdApiEntry && rdApiEntry.status === 'pending'
            && !(rdApiEntry.paging && rdApiEntry.response !== undefined))}
          readValue={(row, path) => readPath(row, path)}
          /* 0.49.43 - The controls shown once above the cards, the
             results line and the search highlight. */
          renderOnce={(child, key) => <React.Fragment key={key}>{renderElementInRow(child, ctx)}</React.Fragment>}
          resultsLine={rdResults}
          narrowed={rdNarrowed}
          highlightWords={rdSearchOn && !el.search_highlight_off ? rdWords : null}
          highlightColor={(el.search_highlight_color && colorCss(el.search_highlight_color)) || ''}
          /* 0.41.93 - A click inside a card records WHICH row it was.
           *
           * Every card is a copy of one template, so they all share one
           * element id: a rule saying "when the button was clicked"
           * fires identically for all thirty with nothing to say which.
           * That is why a rule cannot be row-scoped, and why this is
           * solved with a VALUE instead.
           *
           * The card's own ctx wraps signalClick to first write the
           * row's key into a field the admin names. From that moment it
           * is an ordinary form value: every existing rule, condition
           * and expression works with it, and no new concept enters the
           * rules engine.
           */
          renderChild={(child, key, _i, row, inGroup) => {
            const selField = (el.selection_field || '').trim();
            /* 0.46.07 - One column, several, or the whole row. */
            const selColumns = (Array.isArray(el.selection_columns) && el.selection_columns.length
              ? el.selection_columns : [el.selection_column || ''])
              .map(c => String(c || '').trim()).filter(Boolean);
            const selColumn = selColumns[0] || '';
            /* 0.45.98 - The display itself counts as clicked too, so
               "cards was just clicked" fires for a click on any of its
               cards, whatever the template holds. Both ids travel in
               one signal, "card.action|cards", and the rules engine
               matches either. */
            const rdEid = getElementId(el) || '';
            const rowCtx = ((selField && selColumn) || rdEid) && ctx
              ? {
                ...ctx,
                signalClick: (clickedId) => {
                  /* 0.45.89 - "The whole row": the card's record is
                     kept as it is, read later by path
                     ({sign.numberAlias}). */
                  if (selField && selColumn) {
                    if (selColumns.includes('__row__')) {
                      ctx.updateValue(selField, row && typeof row === 'object' ? { ...row } : row);
                    } else if (selColumns.length > 1) {
                      /* 0.46.07 - Several columns: kept as an object in
                         the row's own shape, so each is read by the same
                         path as in the row ({sign.nature.code}). */
                      const picked = {};
                      for (const col of selColumns) {
                        const v = readPath(row, col);
                        const parts = col.replace(/\[\d*\]/g, '').split('.').filter(Boolean);
                        let node = picked;
                        parts.forEach((part, i) => {
                          if (i === parts.length - 1) node[part] = v == null ? '' : v;
                          else node = (node[part] && typeof node[part] === 'object') ? node[part] : (node[part] = {});
                        });
                      }
                      ctx.updateValue(selField, picked);
                    } else {
                      const v = readPath(row, selColumn);
                      ctx.updateValue(selField, v == null ? '' : String(v));
                    }
                  }
                  if (typeof ctx.signalClick === 'function') {
                    // The click still fires, so a rule can react to the
                    // button AND read the value it just set.
                    ctx.signalClick(rdEid ? `${clickedId}|${rdEid}` : clickedId);
                  }
                },
              }
              : ctx;
            /* 0.42.25 - A card's children fill the CARD.
             *
             * Every element added to a form gets default column spans -
             * 7 of 12 on desktop, 5 for an image - which make sense on a
             * page and none at all inside a card. An information card
             * inside a 368px card was being drawn at 7/12 of it, about
             * 208px, whatever width the admin had set.
             *
             * Forced here rather than only at creation, so forms built
             * before this fix render correctly without re-editing every
             * card. The width of a card is the repeating display's
             * decision, not its children's.
             *
             * 0.46.54 - No longer forced. New elements have 12 columns
             * since 0.46.41, and the elements already inside a display
             * were set to 12 by the 0.46.54 migration, so a size the
             * admin sets now is kept, measured inside the card (or
             * across the display for an element shown once per group),
             * as the editor shows it. */
            /* 0.46.60 - A card drawn inside a Column group (shown once
               per group, its cards set to For every row): sized in page
               columns against the group, as the group's other elements. */
            if (inGroup) {
              const node = renderElementInRow(child, rowCtx, { placed: false });
              if (!isValidElement(node)) return <React.Fragment key={key}>{node}</React.Fragment>;
              const props = colGroupChildProps(child, groupSpansFor(inGroup));
              return (
                <React.Fragment key={key}>
                  {cloneElement(node, {
                    className: [(node.props.className || ''), props.className].filter(Boolean).join(' '),
                    style: { ...(node.props.style || {}), ...props.style },
                  })}
                </React.Fragment>
              );
            }
            return (
              <React.Fragment key={key}>
                {renderElementInRow(child, rowCtx, { placed: false })}
              </React.Fragment>
            );
          }}
          /* An image path is relayed through the platform's proxy, not
             fetched from the far end by the browser: the citizen's
             browser never talks to the service, and the proxy is what
             validates the path. */
          imageUrlFor={(path) => (source === 'api' && conn && path
            ? `/api/api-connections/${encodeURIComponent(splitApiTarget(conn)[0])}/image`
              + `?path=${encodeURIComponent(path)}`
            /* 0.44.59 - Rows not from an API (a library, a collection or
               typed in; a connection left over from before the source
               was switched does not count): the value is already a
               usable address, such as a collection image's
               /api/media/..., and is kept. It was emptied, or sent to the
               connection's proxy, so those images never showed. */
            : (path || ''))}
        />
        {/* 0.42.27 - The paginator, built exactly as renderTable
            builds its own - same element shape, same component - so
            the two look and behave identically. */}
        {rdPaging && getElementId(el) && (
          <ApiPageDriver el={el} paginationKey={getElementId(el)} ctx={ctx} rowsOnPage={rdApiRows} />
        )}
        {el.pagination_enabled && getElementId(el) && (
          <div style={{ marginTop: colMarginBottomFor(el) }}>
            <PaginationElement el={{
              type: 'pagination',
              id: `${el.id}__pagination`,
              table_id: getElementId(el),
              per_page_default: Number(el.per_page_default) || 10,
              page_label: el.page_label ?? 'Page',
              of_label: el.of_label || 'of',
              show_per_page: !!el.show_per_page,
              per_page_label: el.per_page_label ?? 'Show per page',
              next_label: el.next_label || '',
              prev_label: el.prev_label || '',
              page_text: el.page_text || '',
              pagination_small: !!el.pagination_small,
              /* 0.45.86 - Paged by rows: the visitor picked rows too.
                 0.46.56 - Cards per page again: a display has no
                 Cards across any more (the page grid places them). */
              per_page_options: (() => {
                // The visitor's choices: 1 to 4 times the page size, so
                // full lines stay full (6, 12, 18, 24 with 3 a line).
                const d = Math.max(1, Number(el.per_page_default) || 10);
                return [1, 2, 3, 4].map(m => ({ value: String(d * m), label: String(d * m) }));
              })(),
            }} />
          </div>
        )}
        </>)}
      </div>
    );
  }

  if (el.type === 'repeater') {
    return (
      <div className={colClassFor(el)} key={el.id}
           style={{ marginBottom: colMarginBottomFor(el) }} {...cellAttrs(el)}>
        {floatBody(el, <>
        <RepeaterRuntime
          element={el}
          ctx={ctx}
          renderTemplateChild={(child) => renderElementInRow(child, ctx)}
        />
        </>)}
      </div>
    );
  }

  if (el.type === 'column_group' && el.modal) return renderModalGroup(el, ctx);
  if (el.type === 'column_group') {
    const childrenList = Array.isArray(el.children) ? el.children : [];
    // 0.36.107 — Render children first, then check if anything's
    // visible. If every child is null (all hidden by rules or no
    // children at all), the column_group itself collapses too —
    // without this, an empty group leaves an invisible col-X slot
    // with margin-bottom intact, creating the "blank space below
    // hidden field" the user reported.
    // 0.38.68 - Re-applied 0.38.62/0.38.63 page-relative child
    // sizing (reverted in 0.38.66 while chasing the React #31 crash,
    // which turned out NOT to be caused by this code). Child col-N
    // means N columns of the PAGE's 12-col grid, not N/12 of the
    // group's slot. Clamped at 100% of the group so a child wider
    // than the group fits inside rather than overflowing. Stored
    // col_span unchanged - widening the group later releases the
    // clamp. See colGroupChildProps + groupSpansFor.
    const gspans = groupSpansFor(el);
    const rendered = childrenList.map((child) => {
      const node = renderElementInRow(child, ctx, { placed: false });
      if (!isValidElement(node)) return node;
      const props = colGroupChildProps(child, gspans);
      const mergedClassName = [(node.props.className || ''), props.className]
        .filter(Boolean).join(' ');
      const mergedStyle = { ...(node.props.style || {}), ...props.style };
      return withElementPosition(child,
        cloneElement(node, { className: mergedClassName, style: mergedStyle }));
    });
    if (rendered.every(r => r === null)) return null;
    const bandStyle = groupBandStyle(el);
    return (
      <div className={colClassFor(el)} key={el.id}
           style={{ marginBottom: colMarginBottomFor(el) }} {...cellAttrs(el)}>
        {floatBody(el, <>
        {bandStyle ? (
          <div className="hen-group-band" style={bandStyle}
               data-band-stroke={bandHasStroke(el, 'band_') ? '' : undefined}>
            <div className="row">
              {withGroupSections(childrenList, rendered)}
            </div>
          </div>
        ) : (
          <div className="row">
            {withGroupSections(childrenList, rendered)}
          </div>
        )}
        </>)}
      </div>
    );
  }
  // 0.36.70 — line_group renders as a flex container with the
  // configured gap/justify/align/wrap. Children render WITHOUT col-X
  // wrappers — just their natural content side-by-side. The
  // line_group itself still respects the outer 12-col grid via
  // colClassFor (so it can sit at col-md-6 next to other content).
  if (el.type === 'line_group') {
    const childrenList = Array.isArray(el.children) ? el.children : [];
    // 0.36.107 — Same collapse logic as column_group above. An empty
    // line_group with margin-bottom still occupies vertical space.
    const rendered = childrenList.map((child) => renderLineGroupChild(child, ctx));
    if (rendered.every(r => r === null)) return null;
    // 0.36.189 — Gap stored as pixels in `gap_px`; legacy `gap` was
    // a Henaket spacing-scale step (x4 for pixels). Read with that
    // fallback so old saved line groups render identically.
    const gapPx = Number.isFinite(el.gap_px) ? el.gap_px
                : Number.isFinite(el.gap)    ? el.gap * 4
                : 12;
    const gapValue = gapPx === 0 ? '0' : `${gapPx}px`;
    const justifyMap = {
      start: 'flex-start', center: 'center', end: 'flex-end',
      'space-between': 'space-between', 'space-around': 'space-around',
      'space-evenly': 'space-evenly',
    };
    const alignMap = {
      start: 'flex-start', center: 'center', end: 'flex-end',
      stretch: 'stretch', baseline: 'baseline',
    };
    /* 0.46.64 - Top, center and bottom are done by the main-part
       alignment (lib/mainAlign.js): the children line up by their input
       boxes, whatever their labels and hints. The row itself then packs
       them at the top, and the fixed 28px label allowance of 0.36.x
       (--mixed) is not needed. Stretch and baseline stay as they were. */
    const vAuto = { start: 'top', center: 'center', end: 'bottom' }[el.align || 'start'];
    /* 0.48.61 - Kept at the bottom of the screen, over the page: a bar
       for Save / Discard or a message. Sticky, so it keeps its own
       place (and its columns) and rests there at the end of the page. */
    /* 0.48.65 - Or at the top of the screen. No shadow: a shadow, when
       wanted, is a color layer the admin adds. */
    const pinStyle = (el.pin_bottom || el.pin_top) ? {
      position: 'sticky', ...(el.pin_top ? { top: 0 } : { bottom: 0 }), zIndex: 30,
      background: (el.pin_color && colorCss(el.pin_color)) || 'var(--color-white, #fff)',
      paddingTop: 12, paddingBottom: 12,
    } : null;
    return (
      <div className={colClassFor(el)} key={el.id}
           style={{ marginBottom: colMarginBottomFor(el), ...(pinStyle || {}) }}
           {...(el.pin_top ? { 'data-pin-top': '' } : el.pin_bottom ? { 'data-pin-bottom': '' } : {})} {...cellAttrs(el)}>
        {floatBody(el, <>
        <div className={
          'hen-line-group-row' +
          (!vAuto && childrenList.some(lineGroupChildHasLabelArea)
            ? ' hen-line-group-row--mixed' : '')
        }
        {...(vAuto ? { 'data-align-row': '', 'data-align-default': vAuto } : {})}
        style={{
          display: 'flex',
          gap: gapValue,
          justifyContent: justifyMap[el.justify] || 'flex-start',
          // 0.36.198 — Default vertical align flipped from 'center'
          // to 'flex-start' (Top). Existing line_groups with align
          // explicitly stored as 'center' still render centered;
          // only the unset/undefined case changes.
          alignItems: vAuto ? 'flex-start' : (alignMap[el.align] || 'flex-start'),
          flexWrap: el.wrap !== false ? 'wrap' : 'nowrap',
        }}>
          {rendered}
        </div>
        </>)}
      </div>
    );
  }
  const renderer = RENDERERS[el.type];
  let content = null;
  // 0.36.679 — renderTable needs ctx to resolve filter values from
  // current form values. Other renderers accept only `el` — they
  // ignore any extra args, so forwarding ctx is harmless.
  if (renderer) content = renderer(el, ctx);
  else if (INPUT_TYPES.has(el.type)) content = renderRealInput(el, ctx);
  if (content === null) return null;
  // 0.38.65 - Attach data-element-id on the outer wrapper so
  // scrollToTarget('element', id) can find any element (input or
  // content type - titles, hints, buttons, etc.) via a single
  // consistent selector. Only elements that have an element_id
  // are addressable as scroll targets.
  const _scrollAttr = (() => {
    const eid = el && (el.element_id || el.name);
    const attrs = typeof eid === 'string' && eid ? { 'data-element-id': eid } : {};
    // 0.46.37 - Marks the step navigator, so the element after it
    // starts right at its 24px (a title's own top space is dropped).
    if (el && el.type === 'stepper') attrs['data-stepper-col'] = '';
    // 0.46.48 - An FAQ followed by another FAQ keeps the 8px between
    // questions (global.css), so the two read as one list. Not when
    // the admin set a Space below of their own.
    if (el && el.type === 'faq' && !VALID_SPACE_STEPS.has(el.margin_bottom)) attrs['data-faq-col'] = '';
    return attrs;
  })();
  return (
    <div className={colClassFor(el)} key={el.id}
         style={{ marginBottom: colMarginBottomFor(el) }}
         {..._scrollAttr} {...cellAttrs(el)}>
      {floatBody(el, <>
      {content}
      </>)}
    </div>
  );
}

// 0.36.70 - Renders a line_group child wrapped in a Henaket grid col
// class so the child gets its proper width inside the flex container.
// 0.36.182 - Class picked by lineGroupChildClass():
//   - 'col-N col-md-N col-lg-N' when the admin has explicitly cut the
//     child (any tier's span < 12). Hard fraction of the row.
//   - 'col'      when the child is a form-input type (text, dropdown,
//                etc.) and not cut. Flex-grows to fill the row, with
//                the Henaket max-width 610px on the input itself
//                capping it. Multiple inputs in one row share equally.
//   - 'col-auto' when the child is a content-natural type (button,
//                title, image, etc.) and not cut. Sits at content
//                width as the flex item.
// The line_group's outer flex container carries 'hen-line-group-row',
// which zeroes the 16px L/R padding that col-* classes normally
// inherit — gap-based spacing is the line_group's job instead.
// 0.36.183 - Plus 'hen-line-group-labelless' on children that have no
// label above their content (button, image, single-checkbox, etc.).
// When the parent flex row also carries 'hen-line-group-row--mixed'
// (i.e. at least one sibling DOES have a label area), the CSS adds
// padding-top equal to one Henaket label area (24px line-height + 6px
// gap = 28px, --field-label-gap) so label-less children line up with their
// labeled siblings' INPUTS instead of with the siblings' label text.

// Element types whose renderer puts a label above the main control.
// Only counts when the element actually has a non-empty label text -
// otherwise the renderer doesn't emit the <label> node and there's
// no label area to align to.
const LINE_GROUP_LABEL_ABOVE_TYPES = new Set([
  'text', 'password', 'textarea', 'number', 'email',
  'dropdown', 'multiselect', 'date', 'phone', 'cascade',
  'radio',
]);
function lineGroupChildHasLabelArea(el) {
  if (!el) return false;
  if (LINE_GROUP_LABEL_ABOVE_TYPES.has(el.type)) {
    return !!(el.label && String(el.label).trim());
  }
  if (el.type === 'checkbox') {
    // Group form (options present) renders a label above. Single
    // boolean checkbox is inline label-on-side - no label area above.
    const hasOptions = Array.isArray(el.options) && el.options.length > 0;
    return hasOptions && !!(el.label && String(el.label).trim());
  }
  return false;
}

function lineGroupChildClass(el) {
  // 0.36.185 - Each tier is "explicit cut" when the user actually set
  // a value (Number.isFinite of the field). Explicit at xs => col-N.
  // Explicit at md/lg => col-md-N / col-lg-N (overrides the xs class
  // at that breakpoint). Unset tiers fall back to the type-based
  // default for xs ('col' for inputs, 'col-auto' for content). This
  // makes col_span=12 mean "12/12 = 100% width" (forces a wrap when
  // siblings follow), consistent with col_span=1 meaning 1/12.
  //
  // 0.36.193 - The cut-tier emission is GATED to fill-types only
  // (input/dropdown/multiselect/date/phone). Content-type elements
  // - including radio/checkbox groups, button, image, icon, title,
  // markdown, separator, spacer, faq - have no hardcoded width and
  // their slot is always content-sized. col_span has no meaningful
  // cap interpretation for them inside a line_group, so we always
  // emit 'col-auto' regardless of whether any tier stored a finite
  // value. Stored values are preserved on the element (used outside
  // line_groups) but ignored here.
  const isFill = (INPUT_TYPES.has(el.type) && el.type !== 'radio' && el.type !== 'checkbox' && el.type !== 'toggle')
    // 0.47.35 - An image at the width of its column fills its slot too.
    || isFillImage(el);
  const xsSet = isFill && Number.isFinite(el.col_span);
  const mdSet = isFill && Number.isFinite(el.col_md_span);
  const lgSet = isFill && Number.isFinite(el.col_lg_span);
  const parts = [];
  if (xsSet) {
    parts.push(`col-${Math.max(0, Math.min(12, el.col_span))}`);
  } else {
    parts.push(isFill ? 'col' : 'col-auto');
  }
  if (mdSet) {
    parts.push(`col-md-${Math.max(0, Math.min(12, el.col_md_span))}`);
  }
  if (lgSet) {
    parts.push(`col-lg-${Math.max(0, Math.min(12, el.col_lg_span))}`);
  }
  let cls = parts.join(' ');
  if (!lineGroupChildHasLabelArea(el)) {
    cls += ' hen-line-group-labelless';
  }
  if (isFill) {
    // 0.36.188 - fill-type marker. Excludes radio/checkbox which
    // render as groups/inline labels without a 610px-capped input.
    cls += ' hen-line-group-fill';
  }
  return cls;
}

// 0.36.188 - Inline style for fill-type line group children. Sets
// flex-basis to the Henaket per-component max-width cap so the slot
// has the correct preferred size (610px for most inputs, 220px for
// multidropdown). The col-N max-width on the same flex item caps from
// above when the col-N percentage is smaller than the Henaket cap;
// otherwise the cap wins. Net effect: slot = min(col-N%, Henaket cap).
// 0.36.189 - flex-grow set to 0 so the slot never expands past the
// flex-basis. See AdminFormPreview for the full rationale.
/* 0.47.35 - An image sized to the width of its column: in a line group
 * it shares the line like a field (its widths cap it). */
function isFillImage(el) {
  return !!el && el.type === 'image' && imageSizeModeOf(el) === 'fill';
}

function lineGroupChildStyle(el) {
  if (isFillImage(el)) return { flexGrow: 1, flexShrink: 1, flexBasis: 0, minWidth: 0 };
  if (!INPUT_TYPES.has(el.type)) return undefined;
  if (el.type === 'radio' || el.type === 'checkbox' || el.type === 'toggle') return undefined;
  const cap = el.type === 'multiselect' ? '220px' : '610px';
  return { flexGrow: 0, flexShrink: 1, flexBasis: cap };
}

/* 0.47.34 - A column group inside a line group. Its widths (Mobile,
 * Tablet, PC) are SHARES of the line: two groups at 6 and 6 take half
 * each, 8 and 4 two thirds and one third, after the line's gap. 12
 * gives the group the whole line, so the next one wraps under it (on
 * phones, by default). Its children sit on the group's own 12-column
 * grid. */
export function lineGroupCgStyle(el) {
  const xs = clampInt(el.col_span, 0, 12, 12);
  const md = Number.isFinite(el.col_md_span) ? clampInt(el.col_md_span, 0, 12, xs) : xs;
  const lg = Number.isFinite(el.col_lg_span) ? clampInt(el.col_lg_span, 0, 12, md) : md;
  const style = {};
  for (const [tier, n] of [['xs', xs], ['md', md], ['lg', lg]]) {
    style[`--lgcg-grow-${tier}`] = n >= 12 ? 1 : n;
    style[`--lgcg-basis-${tier}`] = n >= 12 ? '100%' : '0%';
    style[`--lgcg-display-${tier}`] = n === 0 ? 'none' : 'block';
  }
  return style;
}
/* The same element, sized as a whole group of 12 so its children's
 * widths count against the group itself. */
export function lineGroupCgElement(el) {
  return { ...el, col_span: 12, col_md_span: undefined, col_lg_span: undefined,
           col_offset: 0, col_md_offset: undefined, col_lg_offset: undefined,
           margin_bottom: VALID_SPACE_STEPS.has(el.margin_bottom) ? el.margin_bottom : 0 };
}

function renderLineGroupChildCore(el, ctx) {
  // 0.46.82 - A built-in element its system page leaves out just now
  // (the ID card button where ID cards are off, the message box with
  // no message).
  if (ctx && ctx.system && el && el.builtin && ctx.system.hidden && ctx.system.hidden.has(el.builtin)) {
    return null;
  }
  // 0.36.106 — Hide rule applies inside line_group too.
  if (ctx && el && getElementId(el) && ctx.ruleState && ctx.ruleState.hidden.has(getElementId(el))) {
    return null;
  }
  // 0.47.34 - A column group in the line.
  if (el.type === 'column_group') {
    const node = renderElementInRowCore(lineGroupCgElement(el), ctx);
    if (!isValidElement(node)) return node;
    return cloneElement(node, {
      key: el.id,
      className: 'hen-line-group-cg',
      style: { ...(node.props.style || {}), ...lineGroupCgStyle(el) },
    });
  }
  const cls = lineGroupChildClass(el);
  const style = lineGroupChildStyle(el);
  const renderer = RENDERERS[el.type];
  if (renderer) return <div key={el.id} className={cls} style={style}>{renderer(el, ctx)}</div>;
  if (INPUT_TYPES.has(el.type)) {
    return <div key={el.id} className={cls} style={style}>{renderRealInput(el, ctx)}</div>;
  }
  return null;
}


// 0.36.173 - PaginationProvider wraps the existing impl so Pagination
// and Table elements can coordinate via shared context (current page +
// per-page values propagate from the Pagination component to the
// linked Table's row slicing).
export default function PageContentRenderer(props) {
  /* 0.47.76 - Floating elements (Stay in view while scrolling) follow
     the screen; placed again after every render, as content may move. */
  useEffect(() => watchFloating(), []);
  useEffect(() => { layoutFloating(); });
  return (
    <PaginationProvider structure={props.structure}
                        initialStates={props.paginationState}
                        onStatesChange={props.onPaginationStateChange}>
      <_PageContentRendererImpl {...props} />
    </PaginationProvider>
  );
}
